export const engineeringMessages = {
  en: {
    engineering: {
      appTitle: 'Technology & Engineering',
      appDescription: 'Product definitions, BOM versions, routing, technical passports, and production readiness.',
      nav: { products: 'Products', allProducts: 'All products', productGroups: 'Groups', rawMaterials: 'Raw materials', spareParts: 'Spare parts', operations: 'Operations', equipment: 'Machines & equipment', shopfloorMap: 'Shopfloor map', referenceData: 'Reference data', units: 'Units', colors: 'Colors', audiences: 'Audiences', seasons: 'Seasons', productionOrders: 'Production orders', dailyOutput: 'Daily output', costing: 'Cost simulation' },
      productTabs: { overview: 'Overview', uoms: 'Units', passports: 'Technical passports', bom: 'BOM and routing', costing: 'Costing', commercial: 'Tags and pricing' },
      action: { backProducts: 'Back to products', backProduct: 'Back to product', retry: 'Retry', cancel: 'Cancel', remove: 'Remove', createBom: 'Create BOM', revise: 'Revise v{version}', saveVersion: 'Save new version', savingVersion: 'Saving version...', addOperation: 'Add operation', standardRoute: 'Use standard socks route', addMaterial: 'Add raw material', dragHint: 'Drag to reorder' },
      picker: {
        repository: 'Product repository',
        search: 'Search products',
        searchPlaceholder: 'Name, SKU, or code',
        empty: 'No matching products found.',
        noSku: 'No SKU',
        itemsPage: '{count} items · Page {page} of {total}',
        close: 'Close product picker',
        retry: 'Retry',
        previous: 'Previous',
        next: 'Next',
        cancel: 'Cancel',
        loading: 'Loading products...',
        missingTitle: 'Missing repository record?',
        missingHelp: 'Create it here, then it will be selected automatically.',
        addNew: 'Add new',
        createTitle: 'Create {type}',
        repositoryTool: 'Repository tool',
        closeCreate: 'Close create form',
        name: 'Name',
        sku: 'SKU',
        unit: 'Unit',
        saving: 'Saving...',
        create: 'Create',
      },
      state: { loadingProduct: 'Loading product...', loadingBom: 'Loading BOM...', noBom: 'No BOM versions are configured for this product.', noMaterials: 'The selected BOM has no material bindings.', loadingWorkspace: 'Loading BOM workspace...', pending: 'Pending', planned: 'Planned', in_progress: 'In progress', completed: 'Completed', cancelled: 'Cancelled', active: 'Active' },
      product: { definition: 'Product definition', internalSku: 'Internal SKU', barcode: 'Barcode', type: 'Type', audience: 'Audience', season: 'Season', unit: 'Unit', minimumOrder: 'Minimum order', pack: 'Pack configuration', notSet: 'Not set', ready: 'Ready for production', notReady: 'Not ready', readiness: 'Production readiness', readinessHelp: 'Backend validation of the approved technical definition.', readyMessage: 'All required technical and material conditions are satisfied.', missing: 'Requirement missing', bomTitle: 'BOM versions, materials, and operations', bomHelp: 'Version history and the selected manufacturing definition.', materials: '{count} materials', operations: '{count} operations', active: 'Active', superseded: 'Superseded', version: 'Version {version}', departmentMissing: 'Department not set', material: 'Material', operation: 'Operation', department: 'Department', quantity: 'Quantity', uoms: 'Product UOMs', uomsHelp: 'Alternative entry and packaging units are converted to the product base unit.', uomsEmpty: 'No product UOMs configured.', uomCode: 'Code', uomLabel: 'Label', uomFactor: 'To base', uomEntry: 'Quantity entry', uomStatus: 'Status', fractionalAllowed: 'Fractional allowed', wholeOnly: 'Whole numbers only', baseUom: 'Base unit', inactive: 'Inactive', activeAlt: 'Active', costingTitle: 'Costing and cost formula', costingHelp: 'Cost simulation builds the pair cost from BOM components (materials, labour, overhead, waste, profit).', costingOpen: 'Open cost simulation', costingFormulaTitle: 'Cost formula version', costingFormulaInUse: 'The latest calculation used formula version #{version}.', costingFormulaMissing: 'No cost calculation has been saved for this product yet, so the active formula version is unknown.', costingFormulaWhere: 'The formula version is a global register in the backend (cost_formula_versions, is_active = 1, newest effective_from wins). It has no editing screen yet — an administrator must create or activate a version there, while the cost components themselves are entered in the cost simulation form.', costingRecent: 'Recent calculations', costingEmpty: 'No cost calculations saved for this product.', costingDate: 'Date', costingVersion: 'Formula version', costingType: 'Production type', costingFinal: 'Final cost', costingBy: 'Calculated by' },
      bom: { workspace: 'Product engineering · {sku}', create: 'Create BOM', revise: 'Revise BOM v{version}', immutable: '{product} · Changes are saved as a new immutable active version.', immutableNote: 'Changes are saved as a new immutable active version.', operations: 'Operations', bindings: 'Material bindings', standardTime: 'Standard time', costPair: 'Operation cost / pair', versionControl: 'Version control', versionHelp: 'The backend preserves the source revision and activates this version after save.', description: 'Change description', descriptionPlaceholder: 'Reason, scope, material substitution, or process improvement', route: 'Socks manufacturing route', routeTitle: 'Operations and consumption', materialConsumption: 'Material consumption', materialHelp: 'Net quantity plus process loss per finished pair.', noOperationMaterials: 'No material is consumed at this operation.', sequence: 'Sequence', operationReference: 'Operation reference', customOperation: 'Custom operation', operationName: 'Operation name', selectDepartment: 'Select department', equipment: 'Compatible equipment', noEquipment: 'No equipment reference', equipmentNote: 'Equipment note', duration: 'Standard time, min', operationCost: 'Operation cost / pair', operationCostDay: 'Day rate / pair', operationCostNight: 'Night rate / pair', netQuantity: 'Net quantity', loss: 'Loss %', effectiveQuantity: 'Effective quantity', selectMaterial: 'Select BOM raw material', replaceConfirm: 'Replace the current routing with the standard socks workflow?', removeOperationConfirm: 'Remove this operation and all of its material bindings?', tabOperation: 'Operation', tabMaterials: 'Raw materials', tabHasErrors: 'This tab has errors', machineType: 'Machine type', machineTypeUnset: 'Not set', machineTypeHelp: 'Machine types come from the equipment catalog.', moveUp: 'Move operation up', moveDown: 'Move operation down' },
      catalog: {
        rawEyebrow: 'Material master data', rawTitle: 'Raw materials', rawDescription: 'Yarns, elastic, packaging, and other production inputs.',
        spareEyebrow: 'Maintenance master data', spareTitle: 'Spare parts', spareDescription: 'Replacement parts and equipment consumables.',
        manufacturedEyebrow: 'Engineering master data', manufacturedTitle: 'Manufactured products', manufacturedDescription: 'Finished and semi-finished goods with production definitions.',
        readyOf: 'ready of {total}', search: 'Search', searchPlaceholder: 'Product name, SKU, barcode, or tag', type: 'Type', allManufacturedTypes: 'All manufactured types', manufacturedGood: 'Manufactured good', finishedGood: 'Finished good', semiFinishedGood: 'Semi-finished good', semiFinished: 'Semi-finished',
        season: 'Season', allSeasons: 'All seasons', audience: 'Audience', allAudiences: 'All audiences', winter: 'Winter', summer: 'Summer', allSeason: 'All season', age: 'Age', allAges: 'All ages', kids: 'Kids', teens: 'Teens', adults: 'Adults', gender: 'Gender', allGenders: 'All genders', men: 'Men', women: 'Women', readiness: 'Readiness', allStatuses: 'All statuses', ready: 'Ready', notReady: 'Not ready', apply: 'Apply',
        product: 'Product', classification: 'Classification', unit: 'Unit', actions: 'Actions', noSeason: 'No season', gaps: '{count} gaps', review: 'Review', loadingProducts: 'Loading products...', emptyProducts: 'No products match the selected filters.',
        rawSingular: 'raw material', spareSingular: 'spare part', typeRawMaterial: 'Raw material', typeSparePart: 'Spare part', invalidId: 'The item identifier is invalid.', wrongSection: 'This record does not belong to {section} management.', backTo: 'Back to {section}s', loadingItem: 'Loading item...', definition: 'Catalog definition', internalSku: 'Internal SKU', barcode: 'Barcode', minimumOrder: 'Minimum order', notSet: 'Not set', group: 'Group', notAssigned: 'Not assigned', created: 'Created', documents: 'Documents', hasBom: 'BOM exists', noBom: 'No BOM', hasPassport: 'Technical passport exists', noPassport: 'No technical passport', tagsAndPrices: 'Tags and prices', prices: '{count} prices', noTags: 'No tags assigned.',
      },
      passport: { title: 'Technical passports', help: 'Manufacturing definitions and their approval history.', create: 'Create passport', records: '{count} records', permission: 'Technical passports require the {permission} permission.', loading: 'Loading technical passports...', empty: 'No technical passports exist for this product.', label: 'Passport #{id}', dateUnavailable: 'Date not available', status: { draft: 'Draft', pending_approval: 'Pending approval', approved: 'Approved', rejected: 'Rejected', archived: 'Archived' } },
      passportDetail: { back: 'Back to product', edit: 'Edit draft', creating: 'Creating...', newRevision: 'New revision', prepareOrder: 'Prepare production order', print: 'Print passport', loading: 'Loading technical passport...', invalidId: 'The technical passport identifier is invalid.', eyebrow: 'Technical passport #{id}', product: 'Product #{id}', approvedDefinition: 'Approved manufacturing definition', printableAria: 'Printable technical passport', brand: 'Yuksalish ERP · Engineering', title: 'Technical Passport', passport: 'Passport', revision: 'Revision', status: 'Status', date: 'Date', productType: 'Product type', template: 'Template', sockType: 'Sock type', sizeRange: 'Size range', season: 'Season', pairWeight: 'Pair weight', machines: 'Machines', machine: 'Machine', needles: 'Needles', compatible: 'Compatible', equipment: 'Equipment #{id}', yes: 'Yes', no: 'No', noMachines: 'No machines configured.', packaging: 'Packaging', item: 'Item', quantity: 'Quantity', packagingItem: 'Packaging #{id}', noPackaging: 'No packaging configured.', labels: 'Labels', noLabels: 'No labels configured.', rawMaterials: 'Raw materials', material: 'Material', stage: 'Stage', perPair: 'Per pair', waste: 'Waste', materialItem: 'Material #{id}', noMaterials: 'No raw materials configured.', productionRoute: 'Production route', step: 'Step', operation: 'Operation', workstation: 'Workstation', time: 'Time', qc: 'QC', minutes: '{count} min', noRoute: 'No route configured.', quality: 'Quality checklist', check: 'Check', acceptable: 'Acceptable value', method: 'Method', noQuality: 'No quality checks configured.', preparedBy: 'Prepared by', checkedBy: 'Checked by', approved: 'Approved', pending: 'Pending', signature: 'Signature', dateSignature: 'Date / signature', definition: 'Technical definition', weight: 'Weight', machineType: 'Machine type', records: 'Passport records', recordsHelp: 'Open another manufacturing definition for this product.', recordCount: '{count} records', sockStructure: 'Sock structure', sockStructureHelp: 'Finished dimensions, construction zones, and approved yarn colors.', element: 'Element', noPosition: 'No position', noKnitType: 'No knit type', yarn: 'Yarn', unspecified: 'Unspecified', noComposition: 'No composition', steps: '{count} steps', standardTime: 'Standard time', unnamedOperation: 'Unnamed operation', required: 'Required', materialRecipe: 'Raw material recipe', lines: '{count} lines', calculator: 'Material requirement calculator', calculatorHelp: 'Calculate planned consumption, including configured waste.', pairs: 'Pairs', calculating: 'Calculating...', calculate: 'Calculate', calculatorEmpty: 'Enter a production quantity to calculate material requirements.', noRequirements: 'No material requirements were returned for this passport.', requiredFor: 'Required for {count} pairs', revisionHistory: 'Revision history', changes: '{count} changes', noRevisions: 'No revision history recorded.', changedBy: 'Changed by', description: 'Description' },
      passportForm: { general: 'General', sockStructure: 'Sock structure', sockElements: 'Sock elements', materials: 'Materials', routes: 'Routes', route: 'Route', measurements: 'Measurements', machines: 'Machines', quality: 'Quality', qcChecks: 'QC checks', packaging: 'Packaging', labels: 'Labels', threads: 'Threads', yarns: 'Yarns', costing: 'Costing', attachments: 'Attachments', revision: 'Revision', saving: 'Saving...', saveDraft: 'Save draft', submitting: 'Submitting...', submitApproval: 'Submit for approval', loading: 'Loading passport form...', returnProduct: 'Return to product', newPassport: 'New technical passport', passportNumber: 'Passport #{id}', intro: 'Create a draft technical definition and record its revision history.', sectionsAria: 'Technical passport sections', errorsAria: 'Contains validation errors', definition: 'Technical definition', template: 'Template', selectTemplate: 'Select template', sockType: 'Sock type', selectSockType: 'Select sock type', long: 'Long', middle: 'Mid-crew', short: 'Short', noShow: 'No-show', season: 'Season', selectSeason: 'Select season', seasonCatalog: 'Season catalog', sizeRange: 'Size range', sockWeight: 'Sock weight, g', sizes: 'Sizes', approvable: 'Ready to approve', notApprovable: 'Not ready to approve', missingSections: 'Missing sections', missingFields: 'Missing fields', description: 'Description', structureError: 'Complete the highlighted sock structure fields before saving.', rawMaterials: 'Raw materials', materialsHelp: 'Materials consumed per produced pair, including expected waste. They are shared with the product BOM: a row added here is added to the BOM stage you pick, and new BOM materials appear here.', bomSyncFailed: 'The raw materials could not be written to the BOM:', addMaterial: 'Add material', noMaterials: 'No materials added.', stage: 'Stage', selectRawMaterial: 'Select raw material', browseProducts: 'Browse products', materialName: 'Material name', consumptionPair: 'Consumption / pair', waste: 'Waste %', unit: 'Unit', remove: 'Remove', routeHelp: 'Ordered manufacturing operations, ownership, standard time, and QC requirement.', addOperation: 'Add operation', noRoutes: 'No route operations added.', operationName: 'Operation name', departmentId: 'Department ID', standardMinutes: 'Standard minutes', inputUnit: 'Input unit', outputUnit: 'Output unit', qcRequired: 'QC required', measurementsHelp: 'Finished-product dimensions and tolerances used during inspection.', addMeasurement: 'Add measurement', noMeasurements: 'No measurements added.', code: 'Code', value: 'Value', rows: 'Rows', compatibleMachines: 'Compatible machines', machinesHelp: 'Approved equipment references and needle configurations.', addMachine: 'Add machine', noMachines: 'No compatible machines added.', machine: 'Machine', selectMachine: 'Select a machine', machineName: 'Machine name', needleCount: 'Needle count', needlesValue: '{count} needles', searchMachines: 'Search machines', machinesSelected: '{selected} of {total} selected', noMachinesCatalog: 'No machines in the equipment catalog.', noMachineMatches: 'No machines match this search.', compatible: 'Compatible', qualityChecklist: 'Quality checklist', qualityHelp: 'Required checks, acceptance criteria, and inspection methods by stage.', addQc: 'Add QC item', noQuality: 'No quality checks added.', checkItem: 'Check item', acceptableValue: 'Acceptable value', inspectionMethod: 'Inspection method', notes: 'Notes', required: 'Required', packagingHelp: 'Packaging material references and required quantities per finished product.', addPackaging: 'Add packaging', noPackaging: 'No packaging added.', packagingProduct: 'Packaging product', selectPackaging: 'Select packaging material', referenceId: 'Reference ID', packagingName: 'Packaging name', quantity: 'Quantity', totalQuantity: 'Total quantity', labelsHelp: 'Product and compliance labels required for production release.', addLabel: 'Add label', noLabels: 'No labels added.', labelName: 'Label name', labelCode: 'Label code', threadsHelp: 'Approved knitting color and yarn-code combinations.', addThread: 'Add thread', noThreads: 'No threads added.', colorName: 'Color name', threadCode: 'Thread code', costingHelp: 'Material consumption and calculated cost per pair.', addCosting: 'Add costing row', noCosting: 'No costing rows added.', finalConsumption: 'Final consumption', unitCost: 'Unit cost', costPair: 'Cost / pair', attachmentsHelp: 'Server-accessible drawings, machine files, specifications, and references.', addAttachment: 'Add attachment', noAttachments: 'No attachments added.', type: 'Type', drawing: 'Drawing', machineFile: 'Machine file', specification: 'Specification', other: 'Other', fileName: 'File name', fileUrl: 'File URL', openFile: 'Open file', mimeType: 'MIME type', fileSize: 'File size (bytes)', revisionEntry: 'Revision entry', revisionNumber: 'Revision number', revisionDate: 'Revision date', changedBy: 'Changed by', preparedBy: 'Prepared by', changeDescription: 'Change description' },
      passportStructure: { finishedDimensions: 'Finished dimensions', dimension: 'Dimension name', value: 'Value', unit: 'Unit', measurementHow: 'How measured', tolerance: 'Tolerance', measurementValue: 'Measurement', yarnsColors: 'Yarns and colors', yarnsHelp: 'Functional yarn roles and approved color definitions.', addYarn: 'Add yarn', role: 'Role', type: 'Type', composition: 'Composition', color: 'Color', code: 'Code', comment: 'Comment', remove: 'Remove', drawing: 'Sock drawing', profile: '{profile} profile', drawingAria: 'Technical passport sock drawing', highlighted: 'Highlighted zones are defined in this passport.', workspace: 'Sock construction workspace', elements: 'Sock elements', elementsHelp: 'Define each construction feature and its exact position on the sock.', coverageAria: 'Construction coverage', elementCount: '{count} elements', zonesUsed: '{used}/{total} zones used', workflowAria: 'Editor workflow', addElement: 'Add element', choosePosition: 'Choose position', setSpecifications: 'Set specifications', library: 'Element library', constructionLibrary: 'Construction elements', decorationLibrary: 'Decorative details', decorationParametersHelp: 'Decoration dimensions control decorated span, repeat pitch, and projection from the cuff edge.', decorationSpan: 'Decorated span, mm', repeatPitch: 'Repeat pitch, mm', projection: 'Projection, mm', addAria: 'Add sock element', positionMap: 'Position map', sideView: 'Side view · millimeter profile', assignedZones: 'Assigned zones', selectedPosition: 'Selected position', duplicate: 'Duplicate', duplicateTitle: 'Duplicate element', removeTitle: 'Remove element', inspectorHelp: 'Edit the selected element. Changes appear on the position map immediately.', placement: 'Placement', elementType: 'Element type', position: 'Position', dimensions: 'Dimensions', length: 'Length, mm', width: 'Width, mm', height: 'Height, mm', construction: 'Construction', knitType: 'Knit type', pattern: 'Pattern', yarnRole: 'Yarn role', engineeringNote: 'Engineering note', optionalInstruction: 'Optional production instruction', requiredProduction: 'Required for production', noSelection: 'No element selected', noSelectionHelp: 'Choose a tool above to add the first construction element.', constructionElements: 'Construction elements', total: '{count} total', standardTemplate: 'Standard template', assignElement: 'Assign: {element}', selectElement: 'Select an element', interactiveDiagramAria: '{profile}, {template}, interactive construction position diagram', elementNumber: 'Element {number} · {zone}', profileType: { invisible: 'Invisible sock', 'low-cut': 'Low-cut sock', ankle: 'Ankle sock', crew: 'Crew sock', 'knee-high': 'Knee-high sock' }, tool: { cuff: 'Cuff', heel: 'Heel', toe: 'Toe', mesh: 'Mesh panel', compression_band: 'Compression band', elastic_band: 'Elastic band', stripe: 'Stripe', logo: 'Logo', jacquard: 'Jacquard', lace_trim: 'Lace trim', picot_edge: 'Picot edge', scalloped_edge: 'Scalloped edge', double_layered_ruffle: 'Double-layered ruffle', fabric_loop_trim: 'Small fabric loop', folded_over_cuff: 'Folded-over cuff', custom: 'Custom element' }, zone: { top_cuff: 'Cuff', leg_front: 'Front leg', leg_back: 'Back leg', ankle_area: 'Ankle transition', heel_area: 'Heel', instep_area: 'Instep', sole_area: 'Sole', toe_area: 'Toe', custom: 'Custom position' }, knit: { plain: 'Plain knit', rib_1x1: '1×1 rib', rib_2x2: '2×2 rib', reinforced: 'Reinforced knit', terry: 'Terry knit', mesh: 'Mesh knit', elastic: 'Elastic knit', jacquard: 'Jacquard knit' }, patternOption: { hatch: 'Hatch', rib: 'Rib', mesh: 'Mesh', dots: 'Dots', waves: 'Waves' } },
      yarnCatalog: { eyebrow: 'Material and color master data', help: 'Build the approved knitting palette from registered raw materials and color references.', materialsAvailable: 'materials', colorsAvailable: 'colors', findMaterial: 'Filter yarn materials', materialSearch: 'Name, SKU, or composition', findColor: 'Filter colors', colorSearch: 'Color name or code', snapshotNote: 'Catalog selections are saved as passport snapshots so released revisions remain traceable.', empty: 'No yarn specification lines', emptyHelp: 'Add the primary yarn, reinforcement, elastomer, and decorative feeds required by the construction.', unassignedMaterial: 'Material not assigned', catalogMaterial: 'Raw-material catalog', rawMaterial: 'Yarn raw material', selectMaterial: 'Select registered raw material', compositionSpecification: 'Fiber composition / specification', compositionPlaceholder: 'Example: 80% cotton, 17% polyamide, 3% elastane', catalogColor: 'Approved color', selectColor: 'Select registered color', colorCode: 'Color / dye-lot code', colorCodePlaceholder: 'Catalog code or #HEX', instructionPlaceholder: 'Feed, plating, supplier lot, or substitution instruction', removeAria: 'Remove yarn line {number}' },
      manufacturingStage: { knitting: 'Knitting', rosso: 'Rosso', boarding: 'Boarding', labeling: 'Labeling', packaging: 'Packaging', finishing: 'Finishing' },
      consistency: { title: 'BOM consistency', help: 'Comparison with the active BOM version for this product.', consistent: 'Consistent', issues: '{count} issues', permission: 'BOM diagnostics require the {permission} permission.', matched: 'Active BOM operations and materials match this passport.', mismatch: 'Mismatch', review: 'Review' },
      passportValidation: { materialId: 'Enter a valid material product ID.', consumption: 'Consumption must be greater than zero.', dimensions: 'Dimensions cannot be negative.', element: 'Element type and position are required.', yarn: 'Yarn role and type are required.', operation: 'Enter an operation name.', standardTime: 'Standard time cannot be negative.', measurement: 'Code, description, and value are required.', equipment: 'Enter a valid equipment reference ID.', needles: 'Needle count must be greater than zero.', qc: 'Stage and check item are required.', packaging: 'Enter a product ID, reference ID, or packaging name.', quantity: 'Quantity must be greater than zero.', label: 'Label name is required.', thread: 'Thread color and code are required.', materialName: 'Material name is required.', costs: 'Cost values cannot be negative.', attachment: 'File name and URL are required.', approvedRevision: 'Approved passports must be duplicated into a new revision.', productId: 'A valid product identifier is required.' },
      yarnRole: { main: 'Main', secondary: 'Secondary', reinforcement: 'Reinforcement', elastic: 'Elastic', decorative: 'Decorative' },
      bomLink: { notice: 'Routes and materials are linked to active BOM v{version}. Edit the BOM to change this manufacturing definition.', edit: 'Edit active BOM', snapshot: 'The passport stores a synchronized BOM snapshot when saved.' },
      structurePreview: { specification: 'Construction specification', dimensions: 'Critical dimensions', elements: 'Construction features', yarnPalette: 'Yarn palette', featureCount: '{count} features', yarnCount: '{count} yarns', requiredCount: '{count} required', zonesDefined: '{count} zones defined', assigned: 'Defined zone', reference: 'Reference zone', cuff: 'Cuff', ankle: 'Ankle', heel: 'Heel', instep: 'Instep', sole: 'Sole', toe: 'Toe', required: 'Required', optional: 'Optional', noComment: 'No engineering note', dimension: { foot_length_mm: 'A. Foot length', leg_height_mm: 'B. Leg height', foot_width_mm: 'C. Foot width', cuff_height_mm: 'D. Cuff height', cuff_width_mm: 'E. Cuff width', toe_length_mm: 'F. Toe length', heel_length_mm: 'G. Heel length', instep_length_mm: 'H. Instep length' } },
      pagination: { aria: 'Table pagination', count: '{first}-{last} of {total}', rows: 'Rows', previousAria: 'Previous page', previous: 'Previous', nextAria: 'Next page', next: 'Next' },
      costing: {
        eyebrow: 'Costing',
        title: 'Cost simulation',
        description: 'Build a component-based cost model for a product and calculate expected unit cost before profit margin.',
        product: 'Product',
        productHelp: 'The cost model applies to the selected manufactured product.',
        changeProduct: 'Change product',
        noProduct: 'No product selected.',
        selectProduct: 'Select product',
        invalidProduct: 'This record is not a manufactured product. Select a manufactured product to simulate costs.',
        components: 'Cost components',
        componentsHelp: 'Materials, labor, overhead, waste, and profit lines. Waste and profit are computed from group or total bases.',
        componentsToolbar: 'Add cost components',
        addMaterial: 'Add material',
        addLabor: 'Add labor',
        addOverhead: 'Add overhead',
        addWaste: 'Add waste',
        addProfit: 'Add profit',
        noComponents: 'No cost components configured.',
        name: 'Name',
        namePlaceholder: 'Component name',
        typeLabel: 'Type',
        group: 'Group',
        mode: 'Mode',
        modeFixed: 'Fixed amount',
        modePercentTotal: '% of total',
        modePercentGroup: '% of group',
        modeLabel: { fixed: 'Fixed', percent_of_total: '% of total', percent_of_group: '% of group' },
        amount: 'Amount',
        percent: 'Percent',
        uomPlaceholder: 'unit',
        uom: 'UOM',
        targetGroup: 'Target group',
        targetGroupPlaceholder: 'e.g. knitting',
        remove: 'Remove component',
        materialTotal: 'Materials',
        laborTotal: 'Labor',
        overheadTotal: 'Overhead',
        productionType: 'Production type',
        productionTypePlaceholder: 'socks',
        currency: 'Currency',
        currencyPlaceholder: 'UZS',
        wasteDefault: 'Production waste',
        profitDefault: 'Profit',
        type: { material: 'Material', labor: 'Labor', overhead: 'Overhead', waste: 'Waste', profit: 'Profit' },
        calculate: 'Calculate cost',
        calculating: 'Calculating...',
        permissionHint: 'Cost calculations require the production_orders.manage permission.',
        productRequired: 'Select a product before calculating.',
        componentsRequired: 'Add at least one cost component.',
        componentInvalid: 'Component {index} is incomplete. Provide a name and a valid amount or percent.',
        selectMaterial: 'Select raw material',
        selectOperation: 'Select operation',
        selectExpense: 'Select expense',
        createOperation: 'Create operation',
        createExpense: 'Create expense type',
        result: 'Cost breakdown',
        resultHelp: 'Computed by the costing engine and saved to the calculation history.',
        calculation: 'Calculation #{id}',
        version: 'Formula version',
        versionAuto: 'Automatic (active version)',
        versionHint: 'Versions come from the backend cost_formula_versions register; automatic uses the active one.',
        versionUsed: 'formula v{version}',
        baseCost: 'Base cost',
        wasteCost: 'Waste',
        costBeforeProfit: 'Cost before profit',
        profitCost: 'Profit',
        finalCost: 'Final cost / pair',
        history: 'Calculation history',
        historyHelp: 'Previously saved cost calculations for this product.',
        refresh: 'Refresh',
        historyNoProduct: 'Select a product to view its calculation history.',
        historyLoading: 'Loading calculation history...',
        historyEmpty: 'No cost calculations saved for this product yet.',
        createdBy: 'Created by',
        createdAt: 'Created at',
        actions: 'Actions',
        loadIntoEditor: 'Load into editor',
        snapshotEmpty: 'This calculation snapshot has no editable components.',
        formulaMissingTitle: 'Cost formula version is not configured',
        formulaMissingBody: 'Costing needs an active row in the backend cost formula register (cost_formula_versions, is_active = 1, newest effective_from wins). The screen cannot create it yet — ask an administrator to add or activate a version, then run the simulation again.',
        picker: {
          noCode: 'No code',
          itemsPage: '{count} items · Page {page} of {total}',
          close: 'Close picker',
          retry: 'Retry',
          previous: 'Previous',
          next: 'Next',
          cancel: 'Cancel',
          loading: 'Loading repository...',
          missingTitle: 'Missing repository record?',
          missingHelp: 'Create it here, then it will be selected automatically.',
          addNew: 'Add new',
          operationsEyebrow: 'Operations repository',
          expensesEyebrow: 'Expenses repository',
          searchOperations: 'Search operations',
          searchExpenses: 'Search expenses',
          searchNameOrCode: 'Name or code',
          noOperations: 'No matching operations found.',
          noExpenses: 'No matching expenses found.',
          costPerUnit: 'Cost per unit',
          repositoryTool: 'Repository tool',
          code: 'Code',
          name: 'Name',
          baseAmount: 'Base amount',
          saving: 'Saving...',
          create: 'Create',
          closeCreate: 'Close create form',
        },
      },
    },
  },
  uz: {
    engineering: {
      appTitle: 'Texnologiya va muhandislik',
      appDescription: 'Mahsulot ta’riflari, BOM versiyalari, texnologik yo‘nalishlar, texnik pasportlar va ishlab chiqarishga tayyorlik.',
      nav: { products: 'Mahsulotlar', allProducts: 'Barcha mahsulotlar', productGroups: 'Guruhlar', rawMaterials: 'Xomashyolar', spareParts: 'Ehtiyot qismlar', operations: 'Operatsiyalar', equipment: 'Mashina va uskunalar', shopfloorMap: 'Sex xaritasi', referenceData: 'Ma’lumotnoma', units: 'O‘lchov birliklari', colors: 'Ranglar', audiences: 'Auditoriyalar', seasons: 'Mavsumlar', productionOrders: 'Ishlab chiqarish buyurtmalari', dailyOutput: 'Kunlik ishlab chiqarish', costing: 'Tannarx simulyatsiyasi' },
      productTabs: { overview: 'Umumiy ma’lumot', uoms: 'O‘lchov birliklari', passports: 'Texnik pasportlar', bom: 'BOM va yo‘nalish', costing: 'Tannarx', commercial: 'Teglar va narxlar' },
      action: { backProducts: 'Mahsulotlarga qaytish', backProduct: 'Mahsulotga qaytish', retry: 'Qayta urinish', cancel: 'Bekor qilish', remove: 'Olib tashlash', createBom: 'BOM yaratish', revise: 'v{version} ni tahrirlash', saveVersion: 'Yangi versiyani saqlash', savingVersion: 'Versiya saqlanmoqda...', addOperation: 'Operatsiya qo‘shish', standardRoute: 'Standart paypoq yo‘nalishi', addMaterial: 'Xomashyo qo‘shish', dragHint: 'Saralash uchun sudrab o‘tkazing' },
      picker: {
        repository: 'Mahsulotlar ma’lumotnomasi',
        search: 'Mahsulotlarni qidirish',
        searchPlaceholder: 'Nomi, SKU yoki kodi',
        empty: 'Mos mahsulotlar topilmadi.',
        noSku: 'SKU yo‘q',
        itemsPage: '{count} ta element · {page}/{total} sahifa',
        close: 'Mahsulot tanlovchisini yopish',
        retry: 'Qayta urinish',
        previous: 'Oldingi',
        next: 'Keyingi',
        cancel: 'Bekor qilish',
        loading: 'Mahsulotlar yuklanmoqda...',
        missingTitle: 'Ma’lumotnomada yozuv yo‘qmi?',
        missingHelp: 'Uni shu yerda yarating, avtomatik tanlanadi.',
        addNew: 'Yangi qo‘shish',
        createTitle: '{type} yaratish',
        repositoryTool: 'Ma’lumotnoma vositasi',
        closeCreate: 'Yaratish shaklini yopish',
        name: 'Nomi',
        sku: 'SKU',
        unit: 'O‘lchov birligi',
        saving: 'Saqlanmoqda...',
        create: 'Yaratish',
      },
      state: { loadingProduct: 'Mahsulot yuklanmoqda...', loadingBom: 'BOM yuklanmoqda...', noBom: 'Ushbu mahsulot uchun BOM versiyalari mavjud emas.', noMaterials: 'Tanlangan BOMda xomashyo bog‘lanishlari mavjud emas.', loadingWorkspace: 'BOM ish maydoni yuklanmoqda...', pending: 'Kutilmoqda', planned: 'Rejalashtirilgan', in_progress: 'Jarayonda', completed: 'Yakunlangan', cancelled: 'Bekor qilingan', active: 'Faol' },
      product: { definition: 'Mahsulot ta’rifi', internalSku: 'Ichki SKU', barcode: 'Shtrix-kod', type: 'Turi', audience: 'Maqsadli guruh', season: 'Mavsum', unit: 'O‘lchov birligi', minimumOrder: 'Minimal buyurtma', pack: 'Qadoqlash konfiguratsiyasi', notSet: 'Belgilanmagan', ready: 'Ishlab chiqarishga tayyor', notReady: 'Tayyor emas', readiness: 'Ishlab chiqarishga tayyorlik', readinessHelp: 'Tasdiqlangan texnik ta’rifning server tekshiruvi.', readyMessage: 'Barcha zarur texnik va xomashyo shartlari bajarilgan.', missing: 'Talab bajarilmagan', bomTitle: 'BOM versiyalari, xomashyolar va operatsiyalar', bomHelp: 'Versiyalar tarixi va tanlangan ishlab chiqarish ta’rifi.', materials: '{count} ta xomashyo', operations: '{count} ta operatsiya', active: 'Faol', superseded: 'Almashtirilgan', version: 'Versiya {version}', departmentMissing: 'Bo‘lim belgilanmagan', material: 'Xomashyo', operation: 'Operatsiya', department: 'Bo‘lim', quantity: 'Miqdor', uoms: 'Mahsulot O‘B lari', uomsHelp: 'Muqobil kiritish va qadoqlash birliklari mahsulotning bazaviy birligiga aylantiriladi.', uomsEmpty: 'Mahsulot O‘B sozlanmagan.', uomCode: 'Kod', uomLabel: 'Nomi', uomFactor: 'Bazaga', uomEntry: 'Miqdor kiritish', uomStatus: 'Holat', fractionalAllowed: 'Kasr ruxsat etiladi', wholeOnly: 'Faqat butun son', baseUom: 'Bazaviy birlik', inactive: 'Nofaol', activeAlt: 'Faol', costingTitle: 'Tannarx va tannarx formulasi', costingHelp: 'Tannarx simulyatsiyasi bir juft tannarxini BOM tarkibiy qismlaridan (xomashyo, mehnat, qo‘shimcha xarajat, yo‘qotish, foyda) hisoblaydi.', costingOpen: 'Tannarx simulyatsiyasini ochish', costingFormulaTitle: 'Tannarx formulasi versiyasi', costingFormulaInUse: 'Oxirgi hisob-kitob #{version}-formula versiyasida bajarilgan.', costingFormulaMissing: 'Bu mahsulot uchun hali hisob-kitob saqlanmagan, shuning uchun faol formula versiyasi noma’lum.', costingFormulaWhere: 'Formula versiyasi — serverdagi global reyestr (cost_formula_versions, is_active = 1, eng yangi effective_from tanlanadi). U hali tahrirlash ekraniga ega emas — versiyani administrator yaratishi yoki faollashtirishi kerak; tannarx tarkibiy qismlari esa tannarx simulyatsiyasi formasida kiritiladi.', costingRecent: 'So‘nggi hisob-kitoblar', costingEmpty: 'Bu mahsulot uchun hisob-kitob saqlanmagan.', costingDate: 'Sana', costingVersion: 'Formula versiyasi', costingType: 'Ishlab chiqarish turi', costingFinal: 'Yakuniy tannarx', costingBy: 'Hisoblagan' },
      bom: { workspace: 'Mahsulot muhandisligi · {sku}', create: 'BOM yaratish', revise: 'BOM v{version} ni tahrirlash', immutable: '{product} · O‘zgarishlar yangi, o‘zgarmas faol versiya sifatida saqlanadi.', immutableNote: 'O‘zgarishlar yangi, o‘zgarmas faol versiya sifatida saqlanadi.', operations: 'Operatsiyalar', bindings: 'Xomashyo bog‘lanishlari', standardTime: 'Me’yoriy vaqt', costPair: 'Bir juft uchun operatsiya narxi', versionControl: 'Versiyalar boshqaruvi', versionHelp: 'Server manba versiyasini saqlaydi va yangi versiyani saqlangandan so‘ng faollashtiradi.', description: 'O‘zgarish tavsifi', descriptionPlaceholder: 'Sabab, qamrov, xomashyo almashtirish yoki jarayon takomillashuvi', route: 'Paypoq ishlab chiqarish yo‘nalishi', routeTitle: 'Operatsiyalar va sarf', materialConsumption: 'Xomashyo sarfi', materialHelp: 'Tayyor bir juft uchun sof miqdor va texnologik yo‘qotish.', noOperationMaterials: 'Ushbu operatsiyada xomashyo sarflanmaydi.', sequence: 'Tartib', operationReference: 'Operatsiya ma’lumotnomasi', customOperation: 'Maxsus operatsiya', operationName: 'Operatsiya nomi', selectDepartment: 'Bo‘limni tanlang', equipment: 'Mos uskuna', noEquipment: 'Uskuna ma’lumotnomasi yo‘q', equipmentNote: 'Uskuna izohi', duration: 'Me’yoriy daqiqa', operationCost: 'Bir juft uchun operatsiya narxi', operationCostDay: 'Kunduzgi smena narxi / juft', operationCostNight: 'Tungi smena narxi / juft', netQuantity: 'Sof miqdor', loss: 'Yo‘qotish %', effectiveQuantity: 'Haqiqiy sarf', selectMaterial: 'BOM xomashyosini tanlang', replaceConfirm: 'Joriy yo‘nalish standart paypoq jarayoni bilan almashtirilsinmi?', removeOperationConfirm: 'Ushbu operatsiya va uning barcha xomashyo bog‘lanishlari olib tashlansinmi?', tabOperation: 'Operatsiya', tabMaterials: 'Xomashyo', tabHasErrors: 'Bu bo‘limda xatolar bor', machineType: 'Mashina turi', machineTypeUnset: 'Belgilanmagan', machineTypeHelp: 'Mashina turlari uskunalar katalogidan olinadi.', moveUp: 'Operatsiyani yuqoriga surish', moveDown: 'Operatsiyani pastga surish' },
      catalog: {
        rawEyebrow: 'Materiallar ma\u2019lumotnomasi', rawTitle: 'Xomashyolar', rawDescription: 'Ip-kalava, elastik, qadoqlash va boshqa ishlab chiqarish materiallari.',
        spareEyebrow: 'Texnik xizmat ma\u2019lumotnomasi', spareTitle: 'Ehtiyot qismlar', spareDescription: 'Uskunalar uchun almashtiriladigan qismlar va sarf materiallari.',
        manufacturedEyebrow: 'Muhandislik ma\u2019lumotnomasi', manufacturedTitle: 'Ishlab chiqariladigan mahsulotlar', manufacturedDescription: 'Ishlab chiqarish ta\u2019rifiga ega tayyor va yarim tayyor mahsulotlar.',
        readyOf: 'jami {total} tadan tayyor', search: 'Qidirish', searchPlaceholder: 'Mahsulot nomi, SKU, shtrix-kod yoki teg', type: 'Turi', allManufacturedTypes: 'Barcha ishlab chiqarish turlari', manufacturedGood: 'Ishlab chiqariladigan mahsulot', finishedGood: 'Tayyor mahsulot', semiFinishedGood: 'Yarim tayyor mahsulot', semiFinished: 'Yarim tayyor',
        season: 'Mavsum', allSeasons: 'Barcha mavsumlar', audience: 'Auditoriya', allAudiences: 'Barcha auditoriyalar', winter: 'Qish', summer: 'Yoz', allSeason: 'Barcha mavsum', age: 'Yosh', allAges: 'Barcha yoshlar', kids: 'Bolalar', teens: 'O\u2018smirlar', adults: 'Kattalar', gender: 'Jins', allGenders: 'Barcha jinslar', men: 'Erkaklar', women: 'Ayollar', readiness: 'Tayyorlik', allStatuses: 'Barcha holatlar', ready: 'Tayyor', notReady: 'Tayyor emas', apply: 'Qo\u2018llash',
        product: 'Mahsulot', classification: 'Tasnif', unit: 'O\u2018lchov birligi', actions: 'Amallar', noSeason: 'Mavsum belgilanmagan', gaps: '{count} ta kamchilik', review: 'Ko\u2018rib chiqish', loadingProducts: 'Mahsulotlar yuklanmoqda...', emptyProducts: 'Tanlangan filtrlarga mos mahsulot topilmadi.',
        rawSingular: 'xomashyo', spareSingular: 'ehtiyot qism', typeRawMaterial: 'Xomashyo', typeSparePart: 'Ehtiyot qism', invalidId: 'Element identifikatori noto\u2018g\u2018ri.', wrongSection: 'Bu yozuv {section} boshqaruviga tegishli emas.', backTo: '{section} ro\u2018yxatiga qaytish', loadingItem: 'Element yuklanmoqda...', definition: 'Katalog ta\u2019rifi', internalSku: 'Ichki SKU', barcode: 'Shtrix-kod', minimumOrder: 'Minimal buyurtma', notSet: 'Belgilanmagan', group: 'Guruh', notAssigned: 'Biriktirilmagan', created: 'Yaratilgan', documents: 'Hujjatlar', hasBom: 'BOM mavjud', noBom: 'BOM yo‘q', hasPassport: 'Texnik pasport mavjud', noPassport: 'Texnik pasport yo‘q', tagsAndPrices: 'Belgilar va narxlar', prices: '{count} ta narx', noTags: 'Belgilar biriktirilmagan.',
      },
      passport: { title: 'Texnik pasportlar', help: 'Ishlab chiqarish ta\u2019riflari va ularning tasdiqlash tarixi.', create: 'Pasport yaratish', records: '{count} ta yozuv', permission: 'Texnik pasportlarni ko\u2018rish uchun {permission} ruxsati talab qilinadi.', loading: 'Texnik pasportlar yuklanmoqda...', empty: 'Bu mahsulot uchun texnik pasportlar mavjud emas.', label: 'Pasport #{id}', dateUnavailable: 'Sana mavjud emas', status: { draft: 'Qoralama', pending_approval: 'Tasdiqlash kutilmoqda', approved: 'Tasdiqlangan', rejected: 'Rad etilgan', archived: 'Arxivlangan' } },
      passportDetail: { back: 'Mahsulotga qaytish', edit: 'Qoralamani tahrirlash', creating: 'Yaratilmoqda...', newRevision: 'Yangi tahrir', prepareOrder: 'Ishlab chiqarish buyurtmasini tayyorlash', print: 'Pasportni chop etish', loading: 'Texnik pasport yuklanmoqda...', invalidId: 'Texnik pasport identifikatori noto‘g‘ri.', eyebrow: 'Texnik pasport #{id}', product: 'Mahsulot #{id}', approvedDefinition: 'Tasdiqlangan ishlab chiqarish ta’rifi', printableAria: 'Chop etiladigan texnik pasport', brand: 'Yuksalish ERP · Muhandislik', title: 'Texnik pasport', passport: 'Pasport', revision: 'Tahrir', status: 'Holat', date: 'Sana', productType: 'Mahsulot turi', template: 'Shablon', sockType: 'Paypoq turi', sizeRange: 'O‘lcham oralig‘i', season: 'Mavsum', pairWeight: 'Juft og‘irligi', machines: 'Uskunalar', machine: 'Uskuna', needles: 'Ignalar', compatible: 'Mos', equipment: 'Uskuna #{id}', yes: 'Ha', no: 'Yo‘q', noMachines: 'Uskunalar sozlanmagan.', packaging: 'Qadoqlash', item: 'Element', quantity: 'Miqdor', packagingItem: 'Qadoqlash #{id}', noPackaging: 'Qadoqlash sozlanmagan.', labels: 'Yorliqlar', noLabels: 'Yorliqlar sozlanmagan.', rawMaterials: 'Xomashyolar', material: 'Xomashyo', stage: 'Bosqich', perPair: 'Bir juftga', waste: 'Yo‘qotish', materialItem: 'Xomashyo #{id}', noMaterials: 'Xomashyolar sozlanmagan.', productionRoute: 'Ishlab chiqarish yo‘nalishi', step: 'Bosqich', operation: 'Operatsiya', workstation: 'Ish joyi', time: 'Vaqt', qc: 'Sifat nazorati', minutes: '{count} daq.', noRoute: 'Yo‘nalish sozlanmagan.', quality: 'Sifat nazorati ro‘yxati', check: 'Tekshiruv', acceptable: 'Qabul qilinadigan qiymat', method: 'Usul', noQuality: 'Sifat tekshiruvlari sozlanmagan.', preparedBy: 'Tayyorlagan', checkedBy: 'Tekshirgan', approved: 'Tasdiqlangan', pending: 'Kutilmoqda', signature: 'Imzo', dateSignature: 'Sana / imzo', definition: 'Texnik ta’rif', weight: 'Og‘irlik', machineType: 'Uskuna turi', records: 'Pasport yozuvlari', recordsHelp: 'Bu mahsulotning boshqa ishlab chiqarish ta’rifini ochish.', recordCount: '{count} ta yozuv', sockStructure: 'Paypoq tuzilishi', sockStructureHelp: 'Tayyor o‘lchamlar, konstruksiya zonalari va tasdiqlangan ip ranglari.', element: 'Element', noPosition: 'Joylashuv yo‘q', noKnitType: 'To‘qish turi yo‘q', yarn: 'Ip', unspecified: 'Belgilanmagan', noComposition: 'Tarkib yo‘q', steps: '{count} ta bosqich', standardTime: 'Me’yoriy vaqt', unnamedOperation: 'Nomsiz operatsiya', required: 'Majburiy', materialRecipe: 'Xomashyo retsepti', lines: '{count} ta qator', calculator: 'Xomashyo talabini hisoblash', calculatorHelp: 'Rejalashtirilgan sarfni yo‘qotish bilan birga hisoblash.', pairs: 'Juftlar', calculating: 'Hisoblanmoqda...', calculate: 'Hisoblash', calculatorEmpty: 'Xomashyo talabini hisoblash uchun ishlab chiqarish miqdorini kiriting.', noRequirements: 'Bu pasport uchun xomashyo talablari qaytarilmadi.', requiredFor: '{count} juft uchun talab', revisionHistory: 'Tahrirlar tarixi', changes: '{count} ta o‘zgarish', noRevisions: 'Tahrirlar tarixi mavjud emas.', changedBy: 'O‘zgartirgan', description: 'Tavsif' },
      passportForm: { general: 'Umumiy', sockStructure: 'Paypoq tuzilishi', sockElements: 'Paypoq elementlari', materials: 'Xomashyolar', routes: 'Yo‘nalishlar', route: 'Yo‘nalish', measurements: 'O‘lchovlar', machines: 'Uskunalar', quality: 'Sifat', qcChecks: 'Sifat tekshiruvlari', packaging: 'Qadoqlash', labels: 'Yorliqlar', threads: 'Iplar', yarns: 'Ip-kalavalar', costing: 'Tannarx', attachments: 'Ilovalar', revision: 'Tahrir', saving: 'Saqlanmoqda...', saveDraft: 'Qoralamani saqlash', submitting: 'Yuborilmoqda...', submitApproval: 'Tasdiqlashga yuborish', loading: 'Pasport shakli yuklanmoqda...', returnProduct: 'Mahsulotga qaytish', newPassport: 'Yangi texnik pasport', passportNumber: 'Pasport #{id}', intro: 'Texnik ta’rif qoralamasini yarating va tahrirlar tarixini yozib boring.', sectionsAria: 'Texnik pasport bo‘limlari', errorsAria: 'Tekshiruv xatolari mavjud', definition: 'Texnik ta’rif', template: 'Shablon', selectTemplate: 'Shablonni tanlang', sockType: 'Paypoq turi', selectSockType: 'Paypoq turini tanlang', long: 'Uzun', middle: 'O‘rta', short: 'Kalta', noShow: 'Sledi', season: 'Mavsum', selectSeason: 'Mavsumni tanlang', seasonCatalog: 'Mavsumlar katalogi', sizeRange: 'O‘lcham oralig‘i', sockWeight: 'Paypoq og‘irligi, g', sizes: 'O‘lchamlar', approvable: 'Tasdiqlashga tayyor', notApprovable: 'Tasdiqlashga tayyor emas', missingSections: 'Yetishmayotgan bo‘limlar', missingFields: 'Yetishmayotgan maydonlar', description: 'Tavsif', structureError: 'Saqlashdan oldin ajratilgan paypoq tuzilishi maydonlarini to‘ldiring.', rawMaterials: 'Xomashyolar', materialsHelp: 'Bir juft mahsulotga sarflanadigan xomashyo va kutiladigan yo‘qotish. Ular mahsulot BOMi bilan umumiy: bu yerda qo‘shilgan qator tanlangan bosqichga BOMda ham qo‘shiladi, BOMdagi yangi xomashyolar esa bu yerda paydo bo‘ladi.', bomSyncFailed: 'Xomashyolarni BOMga yozib bo‘lmadi:', addMaterial: 'Xomashyo qo‘shish', noMaterials: 'Xomashyo qo‘shilmagan.', stage: 'Bosqich', selectRawMaterial: 'Xomashyoni tanlang', browseProducts: 'Mahsulotlarni ko‘rish', materialName: 'Xomashyo nomi', consumptionPair: 'Bir juftga sarf', waste: 'Yo‘qotish %', unit: 'O‘lchov birligi', remove: 'Olib tashlash', routeHelp: 'Tartiblangan operatsiyalar, mas’ul bo‘lim, me’yoriy vaqt va sifat nazorati talabi.', addOperation: 'Operatsiya qo‘shish', noRoutes: 'Yo‘nalish operatsiyalari qo‘shilmagan.', operationName: 'Operatsiya nomi', departmentId: 'Bo‘lim ID', standardMinutes: 'Me’yoriy daqiqa', inputUnit: 'Kirish birligi', outputUnit: 'Chiqish birligi', qcRequired: 'Sifat nazorati talab qilinadi', measurementsHelp: 'Tekshiruvda ishlatiladigan tayyor mahsulot o‘lchamlari va toleranslari.', addMeasurement: 'O‘lchov qo‘shish', noMeasurements: 'O‘lchovlar qo‘shilmagan.', code: 'Kod', value: 'Qiymat', rows: 'Qatorlar', compatibleMachines: 'Mos uskunalar', machinesHelp: 'Tasdiqlangan uskunalar va igna konfiguratsiyalari.', addMachine: 'Uskuna qo‘shish', noMachines: 'Mos uskuna qo‘shilmagan.', machine: 'Uskuna', selectMachine: 'Uskunani tanlang', machineName: 'Uskuna nomi', needleCount: 'Ignalar soni', needlesValue: '{count} igna', searchMachines: 'Uskunalarni qidirish', machinesSelected: '{total} tadan {selected} tasi tanlandi', noMachinesCatalog: 'Uskunalar katalogida uskuna yo‘q.', noMachineMatches: 'Bu qidiruvga mos uskuna topilmadi.', compatible: 'Mos', qualityChecklist: 'Sifat nazorati ro‘yxati', qualityHelp: 'Bosqichlar bo‘yicha majburiy tekshiruvlar, qabul mezonlari va usullari.', addQc: 'Sifat tekshiruvi qo‘shish', noQuality: 'Sifat tekshiruvlari qo‘shilmagan.', checkItem: 'Tekshiruv bandi', acceptableValue: 'Qabul qilinadigan qiymat', inspectionMethod: 'Tekshiruv usuli', notes: 'Izohlar', required: 'Majburiy', packagingHelp: 'Tayyor mahsulot uchun qadoqlash materiallari va zarur miqdorlar.', addPackaging: 'Qadoqlash qo‘shish', noPackaging: 'Qadoqlash qo‘shilmagan.', packagingProduct: 'Qadoqlash mahsuloti', selectPackaging: 'Qadoqlash materialini tanlang', referenceId: 'Ma’lumotnoma ID', packagingName: 'Qadoqlash nomi', quantity: 'Miqdor', totalQuantity: 'Jami miqdor', labelsHelp: 'Ishlab chiqarishga chiqarish uchun zarur mahsulot va muvofiqlik yorliqlari.', addLabel: 'Yorliq qo‘shish', noLabels: 'Yorliqlar qo‘shilmagan.', labelName: 'Yorliq nomi', labelCode: 'Yorliq kodi', threadsHelp: 'Tasdiqlangan to‘qish rangi va ip kodi birikmalari.', addThread: 'Ip qo‘shish', noThreads: 'Iplar qo‘shilmagan.', colorName: 'Rang nomi', threadCode: 'Ip kodi', costingHelp: 'Xomashyo sarfi va bir juft uchun hisoblangan tannarx.', addCosting: 'Tannarx qatori qo‘shish', noCosting: 'Tannarx qatorlari qo‘shilmagan.', finalConsumption: 'Yakuniy sarf', unitCost: 'Birlik narxi', costPair: 'Bir juft tannarxi', attachmentsHelp: 'Serverdagi chizmalar, uskuna fayllari, spetsifikatsiyalar va ma’lumotnomalar.', addAttachment: 'Ilova qo‘shish', noAttachments: 'Ilovalar qo‘shilmagan.', type: 'Turi', drawing: 'Chizma', machineFile: 'Uskuna fayli', specification: 'Spetsifikatsiya', other: 'Boshqa', fileName: 'Fayl nomi', fileUrl: 'Fayl URL', openFile: 'Faylni ochish', mimeType: 'MIME turi', fileSize: 'Fayl hajmi (bayt)', revisionEntry: 'Tahrir yozuvi', revisionNumber: 'Tahrir raqami', revisionDate: 'Tahrir sanasi', changedBy: 'O‘zgartirgan', preparedBy: 'Tayyorlagan', changeDescription: 'O‘zgarish tavsifi' },
      passportStructure: { finishedDimensions: 'Tayyor o‘lchamlar', dimension: 'O‘lcham nomi', value: 'Qiymat', unit: 'Birlik', measurementHow: 'Qanday o‘lchanadi', tolerance: 'Chegara', measurementValue: 'O‘lcham', yarnsColors: 'Ip-kalava va ranglar', yarnsHelp: 'Ip-kalavaning funksional vazifalari va tasdiqlangan rang ta’riflari.', addYarn: 'Ip-kalava qo‘shish', role: 'Vazifasi', type: 'Turi', composition: 'Tarkibi', color: 'Rang', code: 'Kod', comment: 'Izoh', remove: 'Olib tashlash', drawing: 'Paypoq chizmasi', profile: '{profile} profili', drawingAria: 'Texnik pasport paypoq chizmasi', highlighted: 'Ajratilgan zonalar ushbu pasportda belgilangan.', workspace: 'Paypoq konstruksiyasi ish maydoni', elements: 'Paypoq elementlari', elementsHelp: 'Har bir konstruktiv xususiyat va uning paypoqdagi aniq joylashuvini belgilang.', coverageAria: 'Konstruksiya qamrovi', elementCount: '{count} ta element', zonesUsed: '{used}/{total} ta zona ishlatilgan', workflowAria: 'Tahrirlash jarayoni', addElement: 'Element qo‘shish', choosePosition: 'Joylashuvni tanlash', setSpecifications: 'Xususiyatlarni belgilash', library: 'Elementlar kutubxonasi', addAria: 'Paypoq elementi qo‘shish', positionMap: 'Joylashuv xaritasi', sideView: 'Yon ko‘rinish · millimetrli profil', assignedZones: 'Biriktirilgan zonalar', selectedPosition: 'Tanlangan joylashuv', duplicate: 'Nusxalash', duplicateTitle: 'Elementni nusxalash', removeTitle: 'Elementni olib tashlash', inspectorHelp: 'Tanlangan elementni tahrirlang. O‘zgarishlar xaritada darhol ko‘rinadi.', placement: 'Joylashuv', elementType: 'Element turi', position: 'Joylashuv', dimensions: 'O‘lchamlar', length: 'Uzunlik, mm', width: 'Kenglik, mm', height: 'Balandlik, mm', construction: 'Konstruksiya', knitType: 'To‘qish turi', pattern: 'Naqsh', yarnRole: 'Ip-kalava vazifasi', engineeringNote: 'Muhandislik izohi', optionalInstruction: 'Ixtiyoriy ishlab chiqarish ko‘rsatmasi', requiredProduction: 'Ishlab chiqarish uchun majburiy', noSelection: 'Element tanlanmagan', noSelectionHelp: 'Birinchi konstruksiya elementini qo‘shish uchun yuqoridagi vositani tanlang.', constructionElements: 'Konstruksiya elementlari', total: 'Jami {count}', standardTemplate: 'Standart shablon', assignElement: 'Biriktirish: {element}', selectElement: 'Elementni tanlang', interactiveDiagramAria: '{profile}, {template}, interaktiv konstruksiya xaritasi', elementNumber: '{number}-element · {zone}', profileType: { invisible: 'Ko‘rinmas paypoq', 'low-cut': 'Past paypoq', ankle: 'To‘piqqacha paypoq', crew: 'Standart paypoq', 'knee-high': 'Tizzagacha paypoq' }, tool: { cuff: 'Manjet', heel: 'Tovon', toe: 'Uch qismi', mesh: 'To‘rli zona', compression_band: 'Kompression tasma', elastic_band: 'Elastik tasma', stripe: 'Yo‘l', logo: 'Logotip', jacquard: 'Jakkard', custom: 'Erkin element' }, zone: { top_cuff: 'Manjet', leg_front: 'Qo‘nj oldi', leg_back: 'Qo‘nj orqasi', ankle_area: 'To‘piq o‘tish zonasi', heel_area: 'Tovon', instep_area: 'Oyoq usti', sole_area: 'Tag qismi', toe_area: 'Uch qismi', custom: 'Erkin joylashuv' }, knit: { plain: 'Silliq to‘qima', rib_1x1: '1×1 rezinka', rib_2x2: '2×2 rezinka', reinforced: 'Kuchaytirilgan to‘qima', terry: 'Mahra to‘qima', mesh: 'To‘rli to‘qima', elastic: 'Elastik to‘qima', jacquard: 'Jakkard to‘qima' }, patternOption: { hatch: 'Shtrix', rib: 'Rezinka', mesh: 'To‘r', dots: 'Nuqtalar', waves: 'To‘lqinlar' } },
      yarnCatalog: { eyebrow: 'Xomashyo va rang ma’lumotnomasi', help: 'Tasdiqlangan to‘qish palitrasini ro‘yxatdagi xomashyolar va ranglardan tuzing.', materialsAvailable: 'xomashyo', colorsAvailable: 'rang', findMaterial: 'Ip-kalavani filtrlash', materialSearch: 'Nomi, SKU yoki tarkibi', findColor: 'Ranglarni filtrlash', colorSearch: 'Rang nomi yoki kodi', snapshotNote: 'Katalog tanlovlari pasport nusxasi sifatida saqlanadi va tasdiqlangan tahrir kuzatuvchan bo‘lib qoladi.', empty: 'Ip-kalava spetsifikatsiyasi yo‘q', emptyHelp: 'Konstruksiya uchun asosiy, mustahkamlovchi, elastomer va bezak iplarini qo‘shing.', unassignedMaterial: 'Xomashyo biriktirilmagan', catalogMaterial: 'Xomashyo katalogi', rawMaterial: 'Ip-kalava xomashyosi', selectMaterial: 'Ro‘yxatdagi xomashyoni tanlang', compositionSpecification: 'Tola tarkibi / spetsifikatsiya', compositionPlaceholder: 'Masalan: 80% paxta, 17% poliamid, 3% elastan', catalogColor: 'Tasdiqlangan rang', selectColor: 'Ro‘yxatdagi rangni tanlang', colorCode: 'Rang / bo‘yoq partiyasi kodi', colorCodePlaceholder: 'Katalog kodi yoki #HEX', instructionPlaceholder: 'Ip uzatish, qoplama, yetkazib beruvchi partiyasi yoki almashtirish ko‘rsatmasi', removeAria: '{number}-ip qatorini olib tashlash' },
      manufacturingStage: { knitting: 'To‘qish', rosso: 'Rosso', boarding: 'Formaga keltirish', labeling: 'Yorliqlash', packaging: 'Qadoqlash', finishing: 'Yakunlash' },
      consistency: { title: 'BOM muvofiqligi', help: 'Ushbu mahsulotning faol BOM versiyasi bilan taqqoslash.', consistent: 'Muvofiq', issues: '{count} ta muammo', permission: 'BOM diagnostikasi uchun {permission} ruxsati talab qilinadi.', matched: 'Faol BOM operatsiyalari va xomashyolari ushbu pasportga mos.', mismatch: 'Nomuvofiqlik', review: 'Ko‘rib chiqish' },
      passportValidation: { materialId: 'Xomashyo mahsulotining to‘g‘ri ID raqamini kiriting.', consumption: 'Sarf noldan katta bo‘lishi kerak.', dimensions: 'O‘lchamlar manfiy bo‘lishi mumkin emas.', element: 'Element turi va joylashuvi majburiy.', yarn: 'Ip-kalava vazifasi va turi majburiy.', operation: 'Operatsiya nomini kiriting.', standardTime: 'Me’yoriy vaqt manfiy bo‘lishi mumkin emas.', measurement: 'Kod, tavsif va qiymat majburiy.', equipment: 'Uskuna ma’lumotnomasining to‘g‘ri ID raqamini kiriting.', needles: 'Ignalar soni noldan katta bo‘lishi kerak.', qc: 'Bosqich va tekshiruv bandi majburiy.', packaging: 'Mahsulot ID, ma’lumotnoma ID yoki qadoqlash nomini kiriting.', quantity: 'Miqdor noldan katta bo‘lishi kerak.', label: 'Yorliq nomi majburiy.', thread: 'Ip rangi va kodi majburiy.', materialName: 'Xomashyo nomi majburiy.', costs: 'Narx qiymatlari manfiy bo‘lishi mumkin emas.', attachment: 'Fayl nomi va URL majburiy.', approvedRevision: 'Tasdiqlangan pasportdan yangi tahrir nusxasi yaratilishi kerak.', productId: 'To‘g‘ri mahsulot identifikatori talab qilinadi.' },
      yarnRole: { main: 'Asosiy', secondary: 'Ikkilamchi', reinforcement: 'Mustahkamlovchi', elastic: 'Elastik', decorative: 'Bezak' },
      bomLink: { notice: 'Yo‘nalishlar va xomashyolar faol BOM v{version} bilan bog‘langan. Ishlab chiqarish ta’rifini o‘zgartirish uchun BOMni tahrirlang.', edit: 'Faol BOMni tahrirlash', snapshot: 'Pasport saqlanganda BOMning sinxron nusxasi saqlanadi.' },
      structurePreview: { specification: 'Konstruksiya spetsifikatsiyasi', dimensions: 'Muhim o‘lchamlar', elements: 'Konstruksiya xususiyatlari', yarnPalette: 'Ip-kalava palitrasi', featureCount: '{count} ta xususiyat', yarnCount: '{count} ta ip-kalava', requiredCount: '{count} ta majburiy', zonesDefined: '{count} ta zona belgilangan', assigned: 'Belgilangan zona', reference: 'Ma’lumot zonasi', cuff: 'Manjet', ankle: 'To‘piq usti', heel: 'Tovon', instep: 'Oyoq usti', sole: 'Tag qismi', toe: 'Uch qismi', required: 'Majburiy', optional: 'Ixtiyoriy', noComment: 'Muhandislik izohi yo‘q', dimension: { foot_length_mm: 'A. Oyoq uzunligi', leg_height_mm: 'B. Qo‘nj balandligi', foot_width_mm: 'C. Oyoq kengligi', cuff_height_mm: 'D. Manjet balandligi', cuff_width_mm: 'E. Manjet kengligi', toe_length_mm: 'F. Uch qismi uzunligi', heel_length_mm: 'G. Tovon uzunligi', instep_length_mm: 'H. Oyoq usti uzunligi' } },
      pagination: { aria: 'Jadval sahifalari', count: '{first}-{last}, jami {total}', rows: 'Qatorlar', previousAria: 'Oldingi sahifa', previous: 'Oldingi', nextAria: 'Keyingi sahifa', next: 'Keyingi' },
      costing: {
        eyebrow: 'Tannarx',
        title: 'Tannarx simulyatsiyasi',
        description: 'Mahsulot uchun komponentlarga asoslangan tannarx modelini tuzing va foyda chegarasigacha bo‘lgan kutilgan birlik tannarxini hisoblang.',
        product: 'Mahsulot',
        productHelp: 'Tannarx modeli tanlangan ishlab chiqariladigan mahsulotga qo‘llaniladi.',
        changeProduct: 'Mahsulotni o‘zgartirish',
        noProduct: 'Mahsulot tanlanmagan.',
        selectProduct: 'Mahsulotni tanlash',
        invalidProduct: 'Bu yozuv ishlab chiqariladigan mahsulot emas. Tannarxni hisoblash uchun ishlab chiqariladigan mahsulotni tanlang.',
        components: 'Tannarx komponentlari',
        componentsHelp: 'Materiallar, mehnat, qo‘shimcha xarajatlar, yo‘qotish va foyda qatorlari. Yo‘qotish va foyda guruh yoki umumiy bazadan hisoblanadi.',
        componentsToolbar: 'Tannarx komponentlarini qo‘shish',
        addMaterial: 'Material qo‘shish',
        addLabor: 'Mehnat qo‘shish',
        addOverhead: 'Qo‘shimcha xarajat qo‘shish',
        addWaste: 'Yo‘qotish qo‘shish',
        addProfit: 'Foyda qo‘shish',
        noComponents: 'Tannarx komponentlari sozlanmagan.',
        name: 'Nomi',
        namePlaceholder: 'Komponent nomi',
        typeLabel: 'Turi',
        group: 'Guruh',
        mode: 'Usul',
        modeFixed: 'Ruxsat etilgan miqdor',
        modePercentTotal: 'Umumiy miqdordan %',
        modePercentGroup: 'Guruhdan %',
        modeLabel: { fixed: 'Ruxsat etilgan', percent_of_total: 'Umumiy %', percent_of_group: 'Guruh %' },
        amount: 'Miqdor',
        percent: 'Foiz',
        uomPlaceholder: 'birlik',
        uom: 'O‘B',
        targetGroup: 'Maqsadli guruh',
        targetGroupPlaceholder: 'masalan, knitting',
        remove: 'Komponentni olib tashlash',
        materialTotal: 'Materiallar',
        laborTotal: 'Mehnat',
        overheadTotal: 'Qo‘shimcha xarajatlar',
        productionType: 'Ishlab chiqarish turi',
        productionTypePlaceholder: 'socks',
        currency: 'Valyuta',
        currencyPlaceholder: 'UZS',
        wasteDefault: 'Ishlab chiqarish yo‘qotishi',
        profitDefault: 'Foyda',
        type: { material: 'Material', labor: 'Mehnat', overhead: 'Qo‘shimcha xarajat', waste: 'Yo‘qotish', profit: 'Foyda' },
        calculate: 'Tannarxni hisoblash',
        calculating: 'Hisoblanmoqda...',
        permissionHint: 'Tannarx hisoblash uchun production_orders.manage ruxsati talab qilinadi.',
        productRequired: 'Hisoblashdan oldin mahsulotni tanlang.',
        componentsRequired: 'Kamida bitta tannarx komponenti qo‘shing.',
        componentInvalid: '{index}-komponent to‘liq emas. Nom va to‘g‘ri miqdor yoki foizni kiriting.',
        selectMaterial: 'Xomashyoni tanlang',
        selectOperation: 'Operatsiyani tanlang',
        selectExpense: 'Xarajatni tanlang',
        createOperation: 'Operatsiya yaratish',
        createExpense: 'Xarajat turini yaratish',
        result: 'Tannarx tarkibi',
        resultHelp: 'Tannarx dvigateli tomonidan hisoblanadi va hisoblash tarixiga saqlanadi.',
        calculation: 'Hisoblash #{id}',
        baseCost: 'Asosiy tannarx',
        wasteCost: 'Yo‘qotish',
        costBeforeProfit: 'Foydagacha tannarx',
        profitCost: 'Foyda',
        finalCost: 'Yakuniy tannarx / juft',
        history: 'Hisoblash tarixi',
        historyHelp: 'Ushbu mahsulot uchun avval saqlangan tannarx hisob-kitoblari.',
        refresh: 'Yangilash',
        historyNoProduct: 'Hisoblash tarixini ko‘rish uchun mahsulotni tanlang.',
        historyLoading: 'Hisoblash tarixi yuklanmoqda...',
        historyEmpty: 'Bu mahsulot uchun tannarx hisob-kitoblari saqlanmagan.',
        createdBy: 'Kim tomonidan',
        version: 'Formula versiyasi',
        versionAuto: 'Avtomatik (faol versiya)',
        versionHint: 'Versiyalar serverdagi cost_formula_versions reyestridan olinadi; avtomatik rejim faol versiyani ishlatadi.',
        versionUsed: 'formula v{version}',
        createdAt: 'Yaratilgan vaqti',
        actions: 'Amallar',
        loadIntoEditor: 'Tahrirlovchiga yuklash',
        snapshotEmpty: 'Bu hisoblash nusxasida tahrirlanadigan komponentlar yo‘q.',
        formulaMissingTitle: 'Tannarx formulasi versiyasi sozlanmagan',
        formulaMissingBody: 'Tannarx hisoblash uchun serverdagi formula reyestrida faol yozuv kerak (cost_formula_versions, is_active = 1, eng yangi effective_from tanlanadi). Bu ekran uni yarata olmaydi — administrator versiyani qo‘shishi yoki faollashtirishi kerak, so‘ngra simulyatsiyani qayta ishga tushiring.',
        picker: {
          noCode: 'Kod yo‘q',
          itemsPage: '{count} ta element · {page}/{total} sahifa',
          close: 'Tanlovchini yopish',
          retry: 'Qayta urinish',
          previous: 'Oldingi',
          next: 'Keyingi',
          cancel: 'Bekor qilish',
          loading: 'Ma’lumotnoma yuklanmoqda...',
          missingTitle: 'Ma’lumotnomada yozuv yo‘qmi?',
          missingHelp: 'Uni shu yerda yarating, avtomatik tanlanadi.',
          addNew: 'Yangi qo‘shish',
          operationsEyebrow: 'Operatsiyalar ma’lumotnomasi',
          expensesEyebrow: 'Xarajatlar ma’lumotnomasi',
          searchOperations: 'Operatsiyalarni qidirish',
          searchExpenses: 'Xarajatlarni qidirish',
          searchNameOrCode: 'Nomi yoki kodi',
          noOperations: 'Mos operatsiyalar topilmadi.',
          noExpenses: 'Mos xarajatlar topilmadi.',
          costPerUnit: 'Bir birlik narxi',
          repositoryTool: 'Ma’lumotnoma vositasi',
          code: 'Kod',
          name: 'Nomi',
          baseAmount: 'Bazaviy miqdor',
          saving: 'Saqlanmoqda...',
          create: 'Yaratish',
          closeCreate: 'Yaratish shaklini yopish',
        },
      },
    },
  },
  ru: {
    engineering: {
      appTitle: 'Технология и инженерия',
      appDescription: 'Определения продукции, версии BOM, маршруты, технические паспорта и готовность к производству.',
      nav: { products: 'Продукция', allProducts: 'Все продукты', productGroups: 'Группы', rawMaterials: 'Сырьё', spareParts: 'Запчасти', operations: 'Операции', equipment: 'Машины и оборудование', shopfloorMap: 'Карта цеха', referenceData: 'Справочные данные', units: 'Единицы измерения', colors: 'Цвета', audiences: 'Аудитории', seasons: 'Сезоны', productionOrders: 'Производственные заказы', dailyOutput: 'Дневная выработка', costing: 'Расчёт себестоимости' },
      productTabs: { overview: 'Обзор', uoms: 'Единицы измерения', passports: 'Технические паспорта', bom: 'BOM и маршрут', costing: 'Себестоимость', commercial: 'Теги и цены' },
      action: { backProducts: 'Назад к продукции', backProduct: 'Назад к продукту', retry: 'Повторить', cancel: 'Отмена', remove: 'Удалить', createBom: 'Создать BOM', revise: 'Редактировать v{version}', saveVersion: 'Сохранить новую версию', savingVersion: 'Сохранение версии...', addOperation: 'Добавить операцию', standardRoute: 'Использовать стандартный маршрут носка', addMaterial: 'Добавить сырьё', dragHint: 'Перетащите, чтобы изменить порядок' },
      picker: {
        repository: 'Справочник продукции',
        search: 'Поиск продукции',
        searchPlaceholder: 'Название, SKU или код',
        empty: 'Подходящая продукция не найдена.',
        noSku: 'Нет SKU',
        itemsPage: '{count} элементов · стр. {page} из {total}',
        close: 'Закрыть выбор продукции',
        retry: 'Повторить',
        previous: 'Назад',
        next: 'Вперёд',
        cancel: 'Отмена',
        loading: 'Загрузка продукции...',
        missingTitle: 'Нет записи в справочнике?',
        missingHelp: 'Создайте её здесь — она выберется автоматически.',
        addNew: 'Добавить',
        createTitle: 'Создать {type}',
        repositoryTool: 'Инструмент справочника',
        closeCreate: 'Закрыть форму создания',
        name: 'Название',
        sku: 'SKU',
        unit: 'Единица',
        saving: 'Сохранение...',
        create: 'Создать',
      },
      state: { loadingProduct: 'Загрузка продукции...', loadingBom: 'Загрузка BOM...', noBom: 'Для этой продукции не настроены версии BOM.', noMaterials: 'В выбранном BOM нет привязок материалов.', loadingWorkspace: 'Загрузка рабочего пространства BOM...', pending: 'В ожидании', planned: 'Запланирован', in_progress: 'В процессе', completed: 'Завершён', cancelled: 'Отменён', active: 'Активный' },
      yarnRole: { main: 'Основная пряжа', secondary: 'Дополнительная пряжа', reinforcement: 'Усилительная нить', elastic: 'Эластомерная нить', decorative: 'Декоративная пряжа' },
      passportStructure: { yarnsColors: 'Заправка пряжи и цветовая карта', addYarn: 'Добавить пряжу', role: 'Назначение', comment: 'Технологическое указание', workspace: 'Рабочая область конструкции носка', elements: 'Конструктивные элементы носка', elementsHelp: 'Задайте технологические зоны, переплетения и точное расположение элементов изделия.', coverageAria: 'Заполнение конструкции', elementCount: '{count} элементов', zonesUsed: 'Задействовано зон: {used} из {total}', workflowAria: 'Порядок работы конструктора', addElement: 'Добавьте элемент', choosePosition: 'Укажите зону', setSpecifications: 'Задайте параметры', library: 'Библиотека элементов', addAria: 'Добавить конструктивный элемент носка', positionMap: 'Карта зон изделия', sideView: 'Вид сбоку · размеры в миллиметрах', assignedZones: 'Зоны с элементами', selectedPosition: 'Выбранная зона', duplicate: 'Дублировать', duplicateTitle: 'Дублировать элемент', remove: 'Удалить', removeTitle: 'Удалить элемент', inspectorHelp: 'Настройте выбранный элемент. Изменения сразу отображаются на карте зон.', placement: 'Расположение', elementType: 'Конструктивный элемент', position: 'Технологическая зона', dimensions: 'Размеры', length: 'Длина, мм', width: 'Ширина, мм', height: 'Высота, мм', construction: 'Параметры вязания', knitType: 'Вид переплетения', pattern: 'Структура рисунка', yarnRole: 'Назначение пряжи', engineeringNote: 'Указание технолога', optionalInstruction: 'Дополнительное технологическое указание', requiredProduction: 'Обязательный элемент изделия', noSelection: 'Элемент не выбран', noSelectionHelp: 'Выберите инструмент выше, чтобы добавить первый элемент конструкции.', constructionElements: 'Состав конструкции', total: 'Всего: {count}', standardTemplate: 'Базовая конструкция', assignElement: 'Размещение: {element}', selectElement: 'Выберите элемент', interactiveDiagramAria: '{profile}, {template}, интерактивная карта конструкции носка', elementNumber: 'Элемент {number} · {zone}', profileType: { invisible: 'Невидимый носок', 'low-cut': 'Низкий носок', ankle: 'Носок до щиколотки', crew: 'Носок со стандартным паголенком', 'knee-high': 'Гольф' }, tool: { cuff: 'Борт', heel: 'Пятка', toe: 'Мысок', mesh: 'Сетчатая зона', compression_band: 'Компрессионная лента', elastic_band: 'Эластичная лента', stripe: 'Полоса', logo: 'Логотип', jacquard: 'Жаккард', custom: 'Произвольный элемент' }, zone: { top_cuff: 'Борт', leg_front: 'Перед паголенка', leg_back: 'Задняя часть паголенка', ankle_area: 'Переходная зона', heel_area: 'Пятка', instep_area: 'Верх стопы', sole_area: 'След', toe_area: 'Мысок', custom: 'Произвольная зона' }, knit: { plain: 'Гладь', rib_1x1: 'Ластик 1×1', rib_2x2: 'Ластик 2×2', reinforced: 'Усиленное переплетение', terry: 'Плюшевое переплетение', mesh: 'Сетчатое переплетение', elastic: 'Эластичное переплетение', jacquard: 'Жаккардовое переплетение' }, patternOption: { hatch: 'Штриховка', rib: 'Ластик', mesh: 'Сетка', dots: 'Точки', waves: 'Волны' } },
      yarnCatalog: { eyebrow: 'Справочники сырья и цветов', help: 'Сформируйте утверждённую заправку изделия из зарегистрированной пряжи и эталонов цвета.', materialsAvailable: 'видов сырья', colorsAvailable: 'цветов', findMaterial: 'Фильтр пряжи', materialSearch: 'Наименование, артикул или состав', findColor: 'Фильтр цветов', colorSearch: 'Наименование или код цвета', snapshotNote: 'Выбранные справочные данные сохраняются в ревизии паспорта как неизменяемый технологический снимок.', empty: 'Заправка пряжи не задана', emptyHelp: 'Добавьте грунтовую, усилительную, эластомерную и отделочную нити, предусмотренные конструкцией.', unassignedMaterial: 'Пряжа не назначена', catalogMaterial: 'Справочник сырья', rawMaterial: 'Пряжа из справочника сырья', selectMaterial: 'Выберите зарегистрированную пряжу', compositionSpecification: 'Волокнистый состав / спецификация', compositionPlaceholder: 'Например: хлопок 80%, ПА 17%, эластан 3%', catalogColor: 'Утверждённый цвет', selectColor: 'Выберите цвет из справочника', colorCode: 'Код цвета / красильной партии', colorCodePlaceholder: 'Код справочника или #HEX', instructionPlaceholder: 'Укажите нитевод, платировку, партию поставщика или допустимую замену', removeAria: 'Удалить позицию пряжи {number}' },
      costing: {
        eyebrow: 'Калькуляция',
        title: 'Расчёт себестоимости',
        description: 'Сформируйте модель себестоимости по компонентам для продукции и рассчитайте ожидаемую себестоимость единицы до наценки.',
        product: 'Продукция',
        productHelp: 'Модель себестоимости применяется к выбранной производимой продукции.',
        changeProduct: 'Сменить продукцию',
        noProduct: 'Продукция не выбрана.',
        selectProduct: 'Выбрать продукцию',
        invalidProduct: 'Это не производимая продукция. Выберите производимую продукцию для расчёта себестоимости.',
        components: 'Компоненты себестоимости',
        componentsHelp: 'Строки материалов, труда, накладных расходов, отходов и прибыли. Отходы и прибыль рассчитываются от групп или общей базы.',
        componentsToolbar: 'Добавить компоненты себестоимости',
        addMaterial: 'Добавить материал',
        addLabor: 'Добавить труд',
        addOverhead: 'Добавить накладные',
        addWaste: 'Добавить отходы',
        addProfit: 'Добавить прибыль',
        noComponents: 'Компоненты себестоимости не заданы.',
        name: 'Название',
        namePlaceholder: 'Название компонента',
        typeLabel: 'Тип',
        group: 'Группа',
        mode: 'Способ',
        modeFixed: 'Фиксированная сумма',
        modePercentTotal: '% от итога',
        modePercentGroup: '% от группы',
        modeLabel: { fixed: 'Фиксированная', percent_of_total: '% от итога', percent_of_group: '% от группы' },
        amount: 'Сумма',
        percent: 'Процент',
        uomPlaceholder: 'ед.',
        uom: 'Ед.',
        targetGroup: 'Целевая группа',
        targetGroupPlaceholder: 'например, knitting',
        remove: 'Удалить компонент',
        materialTotal: 'Материалы',
        laborTotal: 'Труд',
        overheadTotal: 'Накладные',
        productionType: 'Тип производства',
        productionTypePlaceholder: 'socks',
        currency: 'Валюта',
        currencyPlaceholder: 'UZS',
        wasteDefault: 'Производственные отходы',
        profitDefault: 'Прибыль',
        type: { material: 'Материал', labor: 'Труд', overhead: 'Накладные', waste: 'Отходы', profit: 'Прибыль' },
        calculate: 'Рассчитать себестоимость',
        calculating: 'Расчёт...',
        permissionHint: 'Для расчёта себестоимости требуется право production_orders.manage.',
        productRequired: 'Выберите продукцию перед расчётом.',
        componentsRequired: 'Добавьте хотя бы один компонент себестоимости.',
        componentInvalid: 'Компонент {index} заполнен не полностью. Укажите название и корректную сумму или процент.',
        selectMaterial: 'Выберите сырьё',
        selectOperation: 'Выберите операцию',
        selectExpense: 'Выберите расход',
        createOperation: 'Создать операцию',
        createExpense: 'Создать тип расхода',
        result: 'Разбивка себестоимости',
        resultHelp: 'Рассчитано движком калькуляции и сохранено в истории расчётов.',
        calculation: 'Расчёт #{id}',
        baseCost: 'Базовая себестоимость',
        wasteCost: 'Отходы',
        costBeforeProfit: 'Себестоимость без прибыли',
        profitCost: 'Прибыль',
        finalCost: 'Итоговая себестоимость / пара',
        history: 'История расчётов',
        historyHelp: 'Ранее сохранённые расчёты себестоимости для этой продукции.',
        refresh: 'Обновить',
        historyNoProduct: 'Выберите продукцию, чтобы просмотреть историю расчётов.',
        historyLoading: 'Загрузка истории расчётов...',
        historyEmpty: 'Для этой продукции расчёты себестоимости ещё не сохранены.',
        createdBy: 'Создано',
        version: 'Версия формулы',
        versionAuto: 'Автоматически (активная версия)',
        versionHint: 'Версии берутся из серверного реестра cost_formula_versions; автоматический режим использует активную.',
        versionUsed: 'формула v{version}',
        createdAt: 'Дата',
        actions: 'Действия',
        loadIntoEditor: 'Загрузить в редактор',
        snapshotEmpty: 'В этом снимке расчёта нет редактируемых компонентов.',
        formulaMissingTitle: 'Версия формулы себестоимости не настроена',
        formulaMissingBody: 'Для расчёта нужна активная запись в реестре формул на сервере (cost_formula_versions, is_active = 1, выбирается самый новый effective_from). Создать её на этом экране нельзя — администратор должен добавить или активировать версию, после чего повторите расчёт.',
        picker: {
          noCode: 'Нет кода',
          itemsPage: '{count} элементов · стр. {page} из {total}',
          close: 'Закрыть выбор',
          retry: 'Повторить',
          previous: 'Назад',
          next: 'Вперёд',
          cancel: 'Отмена',
          loading: 'Загрузка справочника...',
          missingTitle: 'Нет записи в справочнике?',
          missingHelp: 'Создайте её здесь — она выберется автоматически.',
          addNew: 'Добавить',
          operationsEyebrow: 'Справочник операций',
          expensesEyebrow: 'Справочник расходов',
          searchOperations: 'Поиск операций',
          searchExpenses: 'Поиск расходов',
          searchNameOrCode: 'Название или код',
          noOperations: 'Подходящие операции не найдены.',
          noExpenses: 'Подходящие расходы не найдены.',
          costPerUnit: 'Стоимость за единицу',
          repositoryTool: 'Инструмент справочника',
          code: 'Код',
          name: 'Название',
          baseAmount: 'Базовая сумма',
          saving: 'Сохранение...',
          create: 'Создать',
          closeCreate: 'Закрыть форму создания',
        },
      },
    },
  },
}

Object.assign(engineeringMessages.uz.engineering.passportStructure, {
  constructionLibrary: 'Konstruksiya elementlari',
  decorationLibrary: 'Dekorativ detallar',
  decorationParametersHelp: 'Dekor o‘lchamlari tanlangan zonadagi bezak uzunligi, takror qadami va chiqish balandligini boshqaradi.',
  decorationSpan: 'Bezak uzunligi, mm',
  repeatPitch: 'Takror qadami, mm',
  projection: 'Chiqish balandligi, mm',
})
Object.assign(engineeringMessages.uz.engineering.passportStructure.tool, {
  lace_trim: 'To‘rli bezak',
  picot_edge: 'Piko cheti',
  scalloped_edge: 'Festonli chet',
  double_layered_ruffle: 'Ikki qatlamli burma',
  fabric_loop_trim: 'Kichik mato ilgagi',
  folded_over_cuff: 'Qayrilma manjet',
  bow_detail: 'Bantik detal',
  embroidery_motif: 'Kashta motivi',
  contrast_piping: 'Kontrast kant',
  applique_patch: 'Applikatsiya',
  bead_trim: 'Munchoqli bezak',
  silicone_grip_dots: 'Sirpanmas nuqtalar',
})

Object.assign(engineeringMessages.ru.engineering.passportStructure, {
  constructionLibrary: 'Конструктивные элементы',
  decorationLibrary: 'Декоративные детали',
  decorationParametersHelp: 'Размеры декора задают длину отделки, шаг раппорта и вылет в выбранной зоне изделия.',
  decorationSpan: 'Длина отделки, мм',
  repeatPitch: 'Шаг раппорта, мм',
  projection: 'Вылет, мм',
})
Object.assign(engineeringMessages.ru.engineering.passportStructure.tool, {
  lace_trim: 'Кружевная тесьма',
  picot_edge: 'Край «пико»',
  scalloped_edge: 'Фестончатый край',
  double_layered_ruffle: 'Двухслойная оборка',
  fabric_loop_trim: 'Декоративная тканевая петля',
  folded_over_cuff: 'Борт с отворотом',
  bow_detail: 'Декоративный бантик',
  embroidery_motif: 'Вышивной мотив',
  contrast_piping: 'Контрастный кант',
  applique_patch: 'Аппликация',
  bead_trim: 'Отделка бусинами',
  silicone_grip_dots: 'Противоскользящие точки',
})

Object.assign(engineeringMessages.en.engineering.passportStructure, {
  measurementTemplate: 'Measurement template',
  measurementUnit: 'Dimensions in cm',
  derivedValues: 'Derived values',
  dimensionField: {
    leg_height_mm: { label: 'Overall height / from top to heel bottom', measurement: 'From the upper cuff edge down to the bottom heel point', tolerance: '+/- 0.5', unit: 'cm' },
    instep_length_mm: { label: 'Foot-section height / from top to instep start', measurement: 'From the upper edge down to the instep / heel-start line', tolerance: '+/- 0.5', unit: 'cm' },
    foot_length_mm: { label: 'Foot length / heel to toe', measurement: 'From the back of the heel to the tip of the toe', tolerance: '+/- 0.5', unit: 'cm' },
    cuff_width_mm: { label: 'Cuff width (flat)', measurement: 'Width of the opened cuff in flat position', tolerance: '+/- 0.3', unit: 'cm' },
    foot_width_mm: { label: 'Foot width (widest point)', measurement: 'Flat width at the widest part of the foot', tolerance: '+/- 0.3', unit: 'cm' },
    heel_length_mm: { label: 'Ankle / heel zone width (flat)', measurement: 'Width around the ankle-upper section in flat position', tolerance: '+/- 0.3', unit: 'cm' },
    cuff_height_mm: { label: 'Cuff height', measurement: 'Height of the cuff section', tolerance: '+/- 0.3', unit: 'cm' },
    toe_length_mm: { label: 'Toe section length', measurement: 'Length of the toe section from toe-start seam to the tip', tolerance: '+/- 0.3', unit: 'cm' },
    eni_plotnost_sm: { label: 'Density width', measurement: 'Flat width of the density sample', tolerance: '+/- 0.3', unit: 'cm' },
    uzunligi_plotnost_sm: { label: 'Density length', measurement: 'Flat length of the density sample', tolerance: '+/- 0.3', unit: 'cm' },
  },
})

Object.assign(engineeringMessages.uz.engineering.passportStructure, {
  measurementTemplate: 'O‘lcham shabloni',
  measurementUnit: 'O‘lchamlar sm da',
  derivedValues: 'Hisoblangan qiymatlar',
  dimensionField: {
    leg_height_mm: { label: 'Umumiy balandlik / yuqori chetdan tovon tagigacha', measurement: 'Yuqori manjet chetidan tovonning pastki nuqtasigacha o‘lchanadi', tolerance: '+/- 0.5', unit: 'sm' },
    instep_length_mm: { label: 'Oyoq qismi balandligi / yuqoridan oyoq usti boshlanishigacha', measurement: 'Yuqori chetdan oyoq usti yoki tovon boshlanish chizig‘igacha o‘lchanadi', tolerance: '+/- 0.5', unit: 'sm' },
    foot_length_mm: { label: 'Oyoq uzunligi / tovondan uchigacha', measurement: 'Tovonning orqa qismidan barmoq uchigacha o‘lchanadi', tolerance: '+/- 0.5', unit: 'sm' },
    cuff_width_mm: { label: 'Manjet kengligi (tekis holatda)', measurement: 'Yoyilgan manjetning tekis holatdagi kengligi', tolerance: '+/- 0.3', unit: 'sm' },
    foot_width_mm: { label: 'Oyoq kengligi (eng keng nuqta)', measurement: 'Oyoqning eng keng qismidagi tekis kenglik', tolerance: '+/- 0.3', unit: 'sm' },
    heel_length_mm: { label: 'To‘piq / tovon zonasi kengligi (tekis holatda)', measurement: 'To‘piqning yuqori qismi atrofidagi tekis kenglik', tolerance: '+/- 0.3', unit: 'sm' },
    cuff_height_mm: { label: 'Manjet balandligi', measurement: 'Manjet qismining balandligi', tolerance: '+/- 0.3', unit: 'sm' },
    toe_length_mm: { label: 'Uch qismi uzunligi', measurement: 'Uch qismi boshlanish chokidan barmoq uchigacha bo‘lgan uzunlik', tolerance: '+/- 0.3', unit: 'sm' },
    eni_plotnost_sm: { label: 'Zichlik eni', measurement: 'Zichlik namunasining tekis holatdagi eni', tolerance: '+/- 0.3', unit: 'sm' },
    uzunligi_plotnost_sm: { label: 'Zichlik uzunligi', measurement: 'Zichlik namunasining tekis holatdagi uzunligi', tolerance: '+/- 0.3', unit: 'sm' },
  },
})

Object.assign(engineeringMessages.ru.engineering.passportStructure, {
  measurementTemplate: 'Шаблон измерений',
  measurementUnit: 'Размеры в см',
  derivedValues: 'Расчётные значения',
  finishedDimensions: 'Готовые размеры',
  dimension: 'Наименование размера',
  unit: 'Единица',
  measurementHow: 'Как измеряется',
  tolerance: 'Допуск',
  measurementValue: 'Размер',
  dimensionField: {
    leg_height_mm: { label: 'Общая высота / от верхнего края до низа пятки', measurement: 'Измеряется от верхнего края борта до нижней точки пятки', tolerance: '+/- 0.5', unit: 'см' },
    instep_length_mm: { label: 'Высота следа / от верха до начала подъёма', measurement: 'Измеряется от верхнего края до линии начала подъёма или пятки', tolerance: '+/- 0.5', unit: 'см' },
    foot_length_mm: { label: 'Длина стопы / от пятки до мыска', measurement: 'Измеряется от задней точки пятки до кончика мыска', tolerance: '+/- 0.5', unit: 'см' },
    cuff_width_mm: { label: 'Ширина борта (в разложенном виде)', measurement: 'Ширина раскрытого борта в плоском положении', tolerance: '+/- 0.3', unit: 'см' },
    foot_width_mm: { label: 'Ширина стопы (в самой широкой части)', measurement: 'Плоская ширина в самой широкой части стопы', tolerance: '+/- 0.3', unit: 'см' },
    heel_length_mm: { label: 'Ширина зоны щиколотки / пятки (в разложенном виде)', measurement: 'Ширина по верхней части щиколотки в плоском положении', tolerance: '+/- 0.3', unit: 'см' },
    cuff_height_mm: { label: 'Высота борта', measurement: 'Высота участка борта', tolerance: '+/- 0.3', unit: 'см' },
    toe_length_mm: { label: 'Длина мысочной части', measurement: 'Длина мысочной части от линии начала до кончика', tolerance: '+/- 0.3', unit: 'см' },
    eni_plotnost_sm: { label: 'Ширина плотности', measurement: 'Плоская ширина образца плотности', tolerance: '+/- 0.3', unit: 'см' },
    uzunligi_plotnost_sm: { label: 'Длина плотности', measurement: 'Плоская длина образца плотности', tolerance: '+/- 0.3', unit: 'см' },
  },
})

Object.assign(engineeringMessages.en.engineering.passportStructure.tool, {
  bow_detail: 'Decorative bow',
  embroidery_motif: 'Embroidery motif',
  contrast_piping: 'Contrast piping',
  applique_patch: 'Applique patch',
  bead_trim: 'Bead trim',
  silicone_grip_dots: 'Silicone grip dots',
})

engineeringMessages.en.engineering.passportStructure.decorationParametersHelp = 'Decoration dimensions control decorated span, repeat pitch, and projection in the selected sock zone.'

Object.assign(engineeringMessages.en.engineering, {
  passportDetail: {
    ...engineeringMessages.en.engineering.passportDetail,
    approval: 'Approval workflow',
    approvalHelp: 'Track production approval across the required review roles.',
    approver: 'Approver',
    approvedAt: 'Approved at',
    notAssigned: 'Not assigned',
    notApproved: 'Not approved yet',
    rejectedTitle: 'Rejected',
    rejectedNoReason: 'This technical passport was rejected without a recorded reason.',
    technologist: 'Technologist',
    shiftManager: 'Shift manager',
    manager: 'Manager',
  },
})

Object.assign(engineeringMessages.en.engineering.passportForm, {
  approval: 'Approval',
  approvalHelp: 'Assign the people who approve this passport for production release.',
  selectApprover: 'Select approver',
  browsePersonnel: 'Browse personnel',
  approve: 'Approve passport',
  approving: 'Approving...',
  reject: 'Reject passport',
  rejecting: 'Rejecting...',
  rejectReasonLabel: 'Rejection reason',
  rejectReasonPlaceholder: 'Why is this passport being rejected?',
  rejectReasonRequired: 'Enter a reason before rejecting the passport.',
  rejectConfirm: 'Reject this technical passport? The passport returns to draft and the approvers are cleared.',
  rubberReturnPlace: 'Rubber return place',
  cottonYarn: 'Cotton yarn',
  spandexYarn: 'Spandex yarn',
  rubberYarn: 'Rubber yarn',
  nylonYarn: 'Nylon yarn',
  mainProductImage: 'Main product image URL',
  technicalDrawingImage: 'Technical drawing image URL',
  knittingMachineDesignFiles: 'Knitting machine design files',
  knittingMachineDesignFilesHelp: 'Add at least one machine program or pattern file URL required for approval.',
  addMachineDesignFile: 'Add design file',
  machineFileUrl: 'Machine file URL',
  knittingProgramFile: 'Knitting program file',
  chooseFile: 'Choose file',
  labelProduct: 'Label product',
  selectLabel: 'Select label product',
  addNewMachine: 'Add new machine',
  requiredSpecifications: 'Required specifications',
  requiredSpecificationsHelp: 'Legacy approval fields required by the backend before a passport can be released.',
  legacySpecifications: 'Legacy specifications',
  legacySpecificationsHelp: 'Optional specifications inherited from the previous passport format.',
  summaryTitle: 'Passport summary',
  summaryErrors: '{count} fields need attention',
  summaryReady: 'No validation issues found.',
  summaryChanges: '{count} sections modified',
  imageUploadHelp: 'Selecting an image uploads it through the passport save request and stores the returned server file path.',
  imageQueued: 'Preparing image for the next save request...',
  imageQueuedSuccess: 'Image queued. Save or approve the passport to upload it to the server.',
  imageUploadTypeError: 'Only JPG, PNG, WEBP, and GIF files can be uploaded directly here. Use a server URL for other file types.',
  attachmentUploadHelp: 'Selecting a file fills its metadata. Paste the server file URL after the file is uploaded or published.',
  attachmentSelectedStatus: 'File selected locally ({size} bytes).',
  attachmentUrlRequiredHint: 'Add the final server URL for this file before approval.',
  fileReadError: 'The selected file could not be read.',
  approvalTab: 'Approval',
  approvalChecklist: 'Approval readiness',
  approvalRecommendations: 'Recommendations',
  attachmentsChecklistTitle: 'Attachments required for approval',
  attachmentsFixMainImage: 'Upload or provide the main product image.',
  attachmentsFixTechnicalDrawing: 'Upload or provide the technical drawing image.',
  attachmentsFixMachineFile: 'Add at least one machine-file attachment with a saved file URL.',
  attachmentsFixAttachmentRows: 'Complete file name and file URL in {count} attachment row(s).',
  approvalApiError: 'Approval request failed',
  approvalValidationError: 'Approval requirements are incomplete',
  approvalFixTechnologist: 'Assign a technologist approver linked to a user account.',
  approvalFixShiftManager: 'Assign a shift manager approver linked to a user account.',
  approvalFixManager: 'Assign a manager approver linked to a user account.',
  approvalFixMachine: 'Add at least one compatible knitting machine before approving.',
  approvalFixMachineFiles: 'Add at least one machine-file attachment in the Attachments tab.',
  approvalFixRevision: 'Enter a revision change description before approving.',
  approvalRetry: 'Review the backend validation message and retry approval after correcting the passport.',
  backendHelpTab: 'Backend help',
  backendHelpTitle: 'Backend validation guide',
  backendHelpIntro: 'Use this tab to understand backend approval errors and fix the required passport fields before retrying.',
  backendDetectedBadge: 'Detected now',
  backendCurrentIssue: 'Current backend issue',
  backendMissingFieldTitle: 'Missing required field',
  backendMissingFieldDefinition: 'The backend blocks approval until every required legacy field and required attachment field is filled.',
  backendMissingFieldInstructionGeneral: 'Check the General tab for required legacy specification fields such as yarn and rubber settings.',
  backendMissingFieldInstructionAttachments: 'Check the Attachments tab for the main product image, technical drawing image, and machine design files.',
  backendMissingFieldInstructionApproval: 'Retry approval after the missing field is saved in the passport draft.',
  backendUnknownApproverTitle: 'Approver does not exist',
  backendUnknownApproverDefinition: 'The selected approver identifier is not accepted by the backend, usually because the person has no linked user account or an outdated value was saved.',
  backendUnknownApproverInstructionPersonnel: 'Open the Approval tab and select the approver again from Personnel.',
  backendUnknownApproverInstructionReselect: 'Choose a person who is active and linked to a real backend user account.',
  backendUnknownApproverInstructionUsers: 'If the correct person is missing or still fails, their personnel record must be linked to a valid user in the backend.',
  backendMissingFieldCurrent: 'The backend reports that {field} is missing. Complete it in the {tab} tab.',
  backendUnknownApproverCurrent: 'The backend reports that the selected value for {field} does not exist.',
  backendOpenTabInstruction: 'Open the {tab} tab.',
  backendCompleteFieldInstruction: 'Fill or correct the {field} field and save the draft.',
  backendSelectPersonnelInstruction: 'Re-select {field} from the personnel picker so the saved value comes from personnel data.',
  backendRetryApprovalInstruction: 'Submit approval again after saving the corrected draft.',
  backendGenericInstruction: 'Review the backend error text, correct the related form data, save the draft, and retry approval.',
})

Object.assign(engineeringMessages.uz.engineering, {
  passportDetail: {
    ...((engineeringMessages.uz.engineering as Record<string, any>).passportDetail ?? {}),
    approval: 'Tasdiqlash jarayoni',
    approvalHelp: 'Ishlab chiqarishga ruxsat berish uchun kerakli tasdiqlovchi rollarni kuzating.',
    approver: 'Tasdiqlovchi',
    approvedAt: 'Tasdiqlangan sana',
    notAssigned: 'Biriktirilmagan',
    notApproved: 'Hali tasdiqlanmagan',
    rejectedTitle: 'Rad etilgan',
    rejectedNoReason: 'Ushbu texnik pasport sabab yozilmasdan rad etilgan.',
    technologist: 'Texnolog',
    shiftManager: 'Smena boshlig‘i',
    manager: 'Menejer',
  },
})

Object.assign((engineeringMessages.uz.engineering as Record<string, any>).passportForm ?? {}, {
  approval: 'Tasdiqlash',
  approvalHelp: 'Ishlab chiqarishga chiqarish uchun ushbu pasportni tasdiqlovchilarni belgilang.',
  selectApprover: 'Tasdiqlovchini tanlang',
  browsePersonnel: 'Xodimlarni ko‘rish',
  approve: 'Pasportni tasdiqlash',
  approving: 'Tasdiqlanmoqda...',
  reject: 'Pasportni rad etish',
  rejecting: 'Rad etilmoqda...',
  rejectReasonLabel: 'Rad etish sababi',
  rejectReasonPlaceholder: 'Nima uchun bu pasport rad etilmoqda?',
  rejectReasonRequired: 'Rad etishdan oldin sababni kiriting.',
  rejectConfirm: 'Ushbu texnik pasport rad etilsinmi? Pasport qoralamaga qaytadi va tasdiqlovchilar tozalanadi.',
  rubberReturnPlace: 'Rezina qaytish joyi',
  cottonYarn: 'Paxta ipi',
  spandexYarn: 'Spandeks ipi',
  rubberYarn: 'Rezina ip',
  nylonYarn: 'Neylon ip',
  mainProductImage: 'Asosiy mahsulot rasmi URL',
  technicalDrawingImage: 'Texnik chizma rasmi URL',
  knittingMachineDesignFiles: 'To‘qish uskunasi dizayn fayllari',
  knittingMachineDesignFilesHelp: 'Tasdiqlash uchun kamida bitta uskuna dasturi yoki naqsh fayli URL manzilini qo‘shing.',
  addMachineDesignFile: 'Dizayn fayli qo‘shish',
  machineFileUrl: 'Uskuna fayli URL',
  knittingProgramFile: 'To‘qish dasturi fayli',
  chooseFile: 'Fayl tanlash',
  labelProduct: 'Yorliq mahsuloti',
  selectLabel: 'Yorliq mahsulotini tanlang',
  addNewMachine: 'Yangi uskuna qo‘shish',
  requiredSpecifications: 'Majburiy spetsifikatsiyalar',
  requiredSpecificationsHelp: 'Pasportni tasdiqlashdan oldin backend talab qiladigan eski maydonlar.',
  legacySpecifications: 'Eski spetsifikatsiyalar',
  legacySpecificationsHelp: 'Avvalgi pasport shaklidan o‘tgan ixtiyoriy spetsifikatsiyalar.',
  summaryTitle: 'Pasport xulosasi',
  summaryErrors: '{count} maydonni to‘g‘rilash kerak',
  summaryReady: 'Tekshiruv xatolari yo‘q.',
  summaryChanges: '{count} bo‘lim o‘zgartirildi',
  imageUploadHelp: 'Rasm tanlanganda u pasportni saqlash so‘rovi orqali yuboriladi va server qaytargan fayl yo‘li saqlanadi.',
  imageQueued: 'Rasm keyingi saqlash so‘rovi uchun tayyorlanmoqda...',
  imageQueuedSuccess: 'Rasm navbatga qo‘yildi. Uni serverga yuklash uchun pasportni saqlang yoki tasdiqlang.',
  imageUploadTypeError: 'Bu yerga faqat JPG, PNG, WEBP va GIF fayllarini to‘g‘ridan-to‘g‘ri yuklash mumkin. Boshqa tur uchun server URL manzilidan foydalaning.',
  attachmentUploadHelp: 'Fayl tanlanganda uning metadata ma’lumotlari to‘ldiriladi. Fayl yuklangach yoki e’lon qilingach server URL manzilini kiriting.',
  attachmentSelectedStatus: 'Fayl lokal tanlandi ({size} bayt).',
  attachmentUrlRequiredHint: 'Tasdiqlashdan oldin bu faylning yakuniy server URL manzilini kiriting.',
  fileReadError: 'Tanlangan faylni o‘qib bo‘lmadi.',
  approvalTab: 'Tasdiqlash',
  approvalChecklist: 'Tasdiqlashga tayyorlik',
  approvalRecommendations: 'Tavsiyalar',
  approvalApiError: 'Tasdiqlash so‘rovi bajarilmadi',
  approvalValidationError: 'Tasdiqlash talablari to‘liq bajarilmagan',
  approvalFixTechnologist: 'Foydalanuvchi hisobiga bog‘langan texnologni tasdiqlovchi sifatida belgilang.',
  approvalFixShiftManager: 'Foydalanuvchi hisobiga bog‘langan smena boshlig‘ini tasdiqlovchi sifatida belgilang.',
  approvalFixManager: 'Foydalanuvchi hisobiga bog‘langan menejerni tasdiqlovchi sifatida belgilang.',
  approvalFixMachine: 'Tasdiqlashdan oldin kamida bitta mos to‘qish uskunasini qo‘shing.',
  approvalFixMachineFiles: 'Ilovalar bo‘limiga kamida bitta uskuna faylini qo‘shing.',
  approvalFixRevision: 'Tasdiqlashdan oldin tahrirdagi o‘zgarish tavsifini kiriting.',
  approvalRetry: 'Backend tekshiruv xabarini ko‘rib chiqing, pasportni to‘g‘rilang va tasdiqlashni qayta yuboring.',
  attachmentsChecklistTitle: 'Tasdiqlash uchun zarur ilovalar',
  attachmentsFixMainImage: 'Asosiy mahsulot rasmini yuklang yoki URL manzilini kiriting.',
  attachmentsFixTechnicalDrawing: 'Texnik chizma rasmini yuklang yoki URL manzilini kiriting.',
  attachmentsFixMachineFile: 'Saqlanadigan fayl URL manziliga ega kamida bitta uskuna faylini qo‘shing.',
  attachmentsFixAttachmentRows: '{count} ta ilova qatorida fayl nomi va fayl URL maydonlarini to‘ldiring.',
  backendHelpTab: 'Backend yordami',
  backendHelpTitle: 'Backend tekshiruv qo‘llanmasi',
  backendHelpIntro: 'Ushbu bo‘lim backend tasdiqlash xatolarini tushuntiradi va qayta yuborishdan oldin qaysi maydonlarni to‘g‘rilash kerakligini ko‘rsatadi.',
  backendDetectedBadge: 'Hozir aniqlangan',
  backendCurrentIssue: 'Joriy backend muammosi',
  backendMissingFieldTitle: 'Majburiy maydon yetishmayapti',
  backendMissingFieldDefinition: 'Backend barcha eski majburiy maydonlar va kerakli ilovalar to‘ldirilmaguncha tasdiqlashni bloklaydi.',
  backendMissingFieldInstructionGeneral: 'Paxta ipi, rezina va boshqa eski spetsifikatsiya maydonlarini Umumiy bo‘limida tekshiring.',
  backendMissingFieldInstructionAttachments: 'Asosiy mahsulot rasmi, texnik chizma va uskuna dizayn fayllarini Ilovalar bo‘limida tekshiring.',
  backendMissingFieldInstructionApproval: 'Yetishmayotgan maydon saqlangandan keyin tasdiqlashni qayta yuboring.',
  backendUnknownApproverTitle: 'Tasdiqlovchi mavjud emas',
  backendUnknownApproverDefinition: 'Tanlangan tasdiqlovchi identifikatori backend tomonidan qabul qilinmadi. Odatda xodimga foydalanuvchi biriktirilmagan yoki eski qiymat saqlangan bo‘ladi.',
  backendUnknownApproverInstructionPersonnel: 'Tasdiqlash bo‘limiga o‘ting va xodimni qayta tanlang.',
  backendUnknownApproverInstructionReselect: 'Faol va backend foydalanuvchi akkauntiga ulangan xodimni tanlang.',
  backendUnknownApproverInstructionUsers: 'Agar to‘g‘ri xodim ko‘rinmasa yoki xato davom etsa, backendda xodim yozuvi amaldagi foydalanuvchiga bog‘lanishi kerak.',
  backendMissingFieldCurrent: 'Backend {field} maydoni yetishmayotganini bildirmoqda. Uni {tab} bo‘limida to‘ldiring.',
  backendUnknownApproverCurrent: 'Backend {field} uchun tanlangan qiymat mavjud emasligini bildirmoqda.',
  backendOpenTabInstruction: '{tab} bo‘limini oching.',
  backendCompleteFieldInstruction: '{field} maydonini to‘ldiring yoki to‘g‘rilang va qoralamani saqlang.',
  backendSelectPersonnelInstruction: '{field} ni xodimlar tanlovidan qayta tanlang, shunda saqlanadigan qiymat personnel ma’lumotidan olinadi.',
  backendRetryApprovalInstruction: 'To‘g‘rilangan qoralama saqlangandan keyin tasdiqlashni yana yuboring.',
  backendGenericInstruction: 'Backend xatosi matnini ko‘rib chiqing, tegishli forma ma’lumotlarini to‘g‘rilang, qoralamani saqlang va tasdiqlashni qayta urinib ko‘ring.',
})

Object.assign(engineeringMessages.ru.engineering, {
  passportDetail: {
    ...((engineeringMessages.ru.engineering as Record<string, any>).passportDetail ?? {}),
    approval: 'Маршрут утверждения',
    approvalHelp: 'Отслеживайте обязательные роли согласования перед выпуском в производство.',
    approver: 'Согласующий',
    approvedAt: 'Дата утверждения',
    notAssigned: 'Не назначен',
    notApproved: 'Ещё не утверждено',
    rejectedTitle: 'Отклонено',
    rejectedNoReason: 'Этот технический паспорт отклонён без указания причины.',
    technologist: 'Технолог',
    shiftManager: 'Начальник смены',
    manager: 'Менеджер',
    labels: 'Ярлыки',
    noLabels: 'Ярлыки не настроены.',
  },
})

Object.assign((engineeringMessages.ru.engineering as Record<string, any>).passportStructure ?? {}, {
  finishedDimensions: 'Размеры готового изделия',
  dimension: 'Наименование размера',
  value: 'Значение',
  unit: 'Единица',
  measurementHow: 'Способ измерения',
  tolerance: 'Допуск',
  measurementValue: 'Размер',
  type: 'Тип',
  composition: 'Состав',
  color: 'Цвет',
  code: 'Код',
  drawing: 'Чертёж носка',
  profile: 'Профиль: {profile}',
  drawingAria: 'Чертёж носка из технического паспорта',
  highlighted: 'Выделенные зоны определены в этом паспорте.',
  constructionLibrary: 'Конструктивные элементы',
  decorationLibrary: 'Декоративные элементы',
  decorationParametersHelp: 'Размеры декора задают длину декорированной зоны, шаг повтора и выступ относительно края борта.',
  decorationSpan: 'Длина декора, мм',
  repeatPitch: 'Шаг повтора, мм',
  projection: 'Выступ, мм',
})

Object.assign((engineeringMessages.ru.engineering as Record<string, any>).passportStructure.tool, {
  lace_trim: 'Кружевная отделка',
  picot_edge: 'Край пико',
  scalloped_edge: 'Фестончатый край',
  double_layered_ruffle: 'Двухслойная оборка',
  fabric_loop_trim: 'Маленькая тканевая петля',
  folded_over_cuff: 'Отложной борт',
  bow_detail: 'Декоративный бант',
  embroidery_motif: 'Вышитый мотив',
  contrast_piping: 'Контрастный кант',
  applique_patch: 'Аппликация',
  bead_trim: 'Отделка бусинами',
  silicone_grip_dots: 'Силиконовые противоскользящие точки',
})

Object.assign((engineeringMessages.en.engineering as Record<string, any>).catalog ??= {}, {
  selectedProducts: '{count} selected',
  selectProduct: 'Select {name}',
  selectPageProducts: 'Select products on this page',
  deleteSelected: 'Delete selected',
  deletingSelected: 'Deleting...',
  deletedSelected: '{count} products deleted.',
  confirmDeleteSelected: 'Delete {count} selected products?',
  deleteAction: 'Delete',
  deletedSingle: '{name} deleted.',
  confirmDeleteSingle: 'Delete {name}?',
})

Object.assign((engineeringMessages.uz.engineering as Record<string, any>).catalog ??= {}, {
  selectedProducts: '{count} ta tanlangan',
  selectProduct: '{name} ni tanlash',
  selectPageProducts: 'Bu sahifadagi mahsulotlarni tanlash',
  deleteSelected: 'Tanlanganlarni o‘chirish',
  deletingSelected: 'O‘chirilmoqda...',
  deletedSelected: '{count} ta mahsulot o‘chirildi.',
  confirmDeleteSelected: '{count} ta tanlangan mahsulot o‘chirilsinmi?',
  deleteAction: 'O‘chirish',
  deletedSingle: '{name} o‘chirildi.',
  confirmDeleteSingle: '{name} o‘chirilsinmi?',
})

Object.assign((engineeringMessages.ru.engineering as Record<string, any>).catalog ??= {}, {
  manufacturedGood: 'Готовое изделие',
  finishedGood: 'Готовый продукт',
  semiFinishedGood: 'Полуфабрикат',
  semiFinished: 'Полуготовый продукт',
  typeRawMaterial: 'Сырьё',
  typeSparePart: 'Запчасть',
  selectedProducts: 'Выбрано: {count}',
  selectProduct: 'Выбрать {name}',
  selectPageProducts: 'Выбрать продукты на этой странице',
  deleteSelected: 'Удалить выбранные',
  deletingSelected: 'Удаление...',
  deletedSelected: 'Удалено продуктов: {count}.',
  confirmDeleteSelected: 'Удалить {count} выбранных продуктов?',
  deleteAction: 'Удалить',
  deletedSingle: '{name} удалён.',
  confirmDeleteSingle: 'Удалить {name}?',
})

Object.assign((engineeringMessages.ru.engineering as Record<string, any>).passportForm ?? {}, {
  general: 'Общие данные',
  sockStructure: 'Конструкция носка',
  sockElements: 'Элементы носка',
  materials: 'Материалы',
  routes: 'Маршруты',
  route: 'Маршрут',
  measurements: 'Измерения',
  machines: 'Машины',
  quality: 'Качество',
  qcChecks: 'Проверки качества',
  packaging: 'Упаковка',
  labels: 'Ярлыки',
  threads: 'Нити',
  yarns: 'Пряжа',
  costing: 'Себестоимость',
  attachments: 'Вложения',
  revision: 'Редакция',
  saving: 'Сохранение...',
  saveDraft: 'Сохранить черновик',
  submitting: 'Отправка...',
  submitApproval: 'Отправить на утверждение',
  loading: 'Загрузка формы паспорта...',
  returnProduct: 'Вернуться к продукту',
  newPassport: 'Новый технический паспорт',
  passportNumber: 'Паспорт №{id}',
  intro: 'Создайте черновик технического определения и зафиксируйте историю редакций.',
  sectionsAria: 'Разделы технического паспорта',
  errorsAria: 'Содержит ошибки проверки',
  definition: 'Техническое определение',
  template: 'Шаблон',
  selectTemplate: 'Выберите шаблон',
  sockType: 'Тип носка',
  selectSockType: 'Выберите тип носка',
  long: 'Длинный',
  middle: 'Средний',
  short: 'Короткий',
  noShow: 'Следки',
  season: 'Сезон',
  selectSeason: 'Выберите сезон',
  seasonCatalog: 'Справочник сезонов',
  sizeRange: 'Размерный ряд',
  sockWeight: 'Вес носка, г',
  sizes: 'Размеры',
  approvable: 'Готов к утверждению',
  notApprovable: 'Не готов к утверждению',
  missingSections: 'Недостающие разделы',
  missingFields: 'Недостающие поля',
  description: 'Описание',
  structureError: 'Перед сохранением заполните выделенные поля конструкции носка.',
  rawMaterials: 'Сырьё',
  materialsHelp: 'Материалы на одну произведённую пару с учётом ожидаемых отходов. Они общие с BOM продукта: добавленная здесь строка попадёт в выбранный этап BOM, а новые материалы BOM появятся здесь.',
  bomSyncFailed: 'Не удалось записать сырьё в BOM:',
  addMaterial: 'Добавить материал',
  noMaterials: 'Материалы не добавлены.',
  stage: 'Этап',
  selectRawMaterial: 'Выберите сырьё',
  browseProducts: 'Открыть справочник',
  materialName: 'Наименование материала',
  consumptionPair: 'Расход на пару',
  waste: 'Отходы, %',
  unit: 'Единица',
  remove: 'Удалить',
  routeHelp: 'Упорядоченные производственные операции, ответственные подразделения, нормативное время и требования контроля качества.',
  addOperation: 'Добавить операцию',
  noRoutes: 'Операции маршрута не добавлены.',
  operationName: 'Наименование операции',
  departmentId: 'ID подразделения',
  standardMinutes: 'Норматив, мин',
  inputUnit: 'Входная единица',
  outputUnit: 'Выходная единица',
  qcRequired: 'Требуется контроль качества',
  measurementsHelp: 'Размеры готового изделия и допуски, используемые при проверке.',
  addMeasurement: 'Добавить измерение',
  noMeasurements: 'Измерения не добавлены.',
  code: 'Код',
  value: 'Значение',
  rows: 'Ряды',
  compatibleMachines: 'Совместимые машины',
  machinesHelp: 'Утверждённые машины и конфигурации игл.',
  addMachine: 'Добавить машину',
  noMachines: 'Совместимые машины не добавлены.',
  machine: 'Машина',
  selectMachine: 'Выберите машину',
  machineName: 'Наименование машины',
  needleCount: 'Количество игл',
  needlesValue: '{count} игл',
  searchMachines: 'Поиск машин',
  machinesSelected: 'Выбрано {selected} из {total}',
  noMachinesCatalog: 'В справочнике нет машин.',
  noMachineMatches: 'По запросу машины не найдены.',
  compatible: 'Совместима',
  qualityChecklist: 'Контроль качества',
  qualityHelp: 'Обязательные проверки, критерии приемки и методы контроля по этапам.',
  addQc: 'Добавить проверку',
  noQuality: 'Проверки качества не добавлены.',
  checkItem: 'Проверяемый параметр',
  acceptableValue: 'Допустимое значение',
  inspectionMethod: 'Метод проверки',
  notes: 'Примечания',
  required: 'Обязательно',
  packagingHelp: 'Упаковочные материалы и их количество на готовое изделие.',
  legacySpecifications: 'Устаревшие характеристики',
  legacySpecificationsHelp: 'Необязательные характеристики из прежнего формата паспорта.',
  summaryTitle: 'Сводка паспорта',
  summaryErrors: 'Полей требуют внимания: {count}',
  summaryReady: 'Ошибок проверки нет.',
  summaryChanges: 'Изменено разделов: {count}',
  totalQuantity: 'Общее количество',
  addPackaging: 'Добавить упаковку',
  noPackaging: 'Упаковка не добавлена.',
  packagingProduct: 'Упаковочный материал',
  selectPackaging: 'Выберите упаковочный материал',
  referenceId: 'ID справочника',
  packagingName: 'Наименование упаковки',
  quantity: 'Количество',
  labelsHelp: 'Продуктовые и нормативные ярлыки, необходимые для выпуска в производство.',
  addLabel: 'Добавить ярлык',
  noLabels: 'Ярлыки не добавлены.',
  labelName: 'Наименование ярлыка',
  labelCode: 'Код ярлыка',
  threadsHelp: 'Утверждённые сочетания цвета вязания и кода нити.',
  addThread: 'Добавить нить',
  noThreads: 'Нити не добавлены.',
  colorName: 'Наименование цвета',
  threadCode: 'Код нити',
  costingHelp: 'Расход материала и расчётная себестоимость одной пары.',
  addCosting: 'Добавить строку себестоимости',
  noCosting: 'Строки себестоимости не добавлены.',
  finalConsumption: 'Итоговый расход',
  unitCost: 'Цена единицы',
  costPair: 'Себестоимость пары',
  attachmentsHelp: 'Доступные серверу чертежи, файлы машин, спецификации и справочные материалы.',
  addAttachment: 'Добавить вложение',
  noAttachments: 'Вложения не добавлены.',
  type: 'Тип',
  drawing: 'Чертёж',
  machineFile: 'Файл машины',
  specification: 'Спецификация',
  other: 'Другое',
  fileName: 'Имя файла',
  fileUrl: 'URL файла',
  mimeType: 'MIME-тип',
  fileSize: 'Размер файла (байт)',
  revisionEntry: 'Запись редакции',
  revisionNumber: 'Номер редакции',
  revisionDate: 'Дата редакции',
  changedBy: 'Автор изменений',
  preparedBy: 'Подготовил',
  changeDescription: 'Описание изменений',
  approvalTab: 'Утверждение',
  approvalChecklist: 'Готовность к утверждению',
  approvalRecommendations: 'Рекомендации',
  approvalApiError: 'Не удалось выполнить утверждение',
  approvalValidationError: 'Не выполнены требования для утверждения',
  approvalFixTechnologist: 'Назначьте технолога, связанного с учётной записью пользователя.',
  approvalFixShiftManager: 'Назначьте начальника смены, связанного с учётной записью пользователя.',
  approvalFixManager: 'Назначьте менеджера, связанного с учётной записью пользователя.',
  approvalFixMachine: 'Перед утверждением добавьте хотя бы одну совместимую вязальную машину.',
  approvalFixMachineFiles: 'Добавьте хотя бы один файл машины на вкладке «Вложения».',
  approvalFixRevision: 'Перед утверждением опишите изменения редакции.',
  approvalRetry: 'Проверьте сообщение backend, исправьте паспорт и повторите утверждение.',
  approval: 'Утверждение',
  approvalHelp: 'Назначьте сотрудников, которые утверждают этот паспорт для выпуска в производство.',
  selectApprover: 'Выберите согласующего',
  browsePersonnel: 'Открыть список персонала',
  approve: 'Утвердить паспорт',
  approving: 'Утверждение...',
  reject: 'Отклонить паспорт',
  rejecting: 'Отклонение...',
  rejectReasonLabel: 'Причина отклонения',
  rejectReasonPlaceholder: 'Почему отклоняется этот паспорт?',
  rejectReasonRequired: 'Укажите причину перед отклонением паспорта.',
  rejectConfirm: 'Отклонить этот технический паспорт? Паспорт вернётся в черновик, утверждающие будут сброшены.',
  rubberReturnPlace: 'Место возврата резины',
  cottonYarn: 'Хлопковая пряжа',
  spandexYarn: 'Спандекс',
  rubberYarn: 'Резиновая нить',
  nylonYarn: 'Нейлоновая нить',
  mainProductImage: 'URL основного изображения изделия',
  technicalDrawingImage: 'URL технического рисунка',
  knittingMachineDesignFiles: 'Файлы программы вязальной машины',
  knittingMachineDesignFilesHelp: 'Добавьте как минимум один URL программы или файла рисунка машины, требуемый для утверждения.',
  addMachineDesignFile: 'Добавить файл',
  machineFileUrl: 'URL файла машины',
  knittingProgramFile: 'Файл программы вязания',
  chooseFile: 'Выбрать файл',
  labelProduct: 'Продукт ярлыка',
  selectLabel: 'Выберите продукт ярлыка',
  addNewMachine: 'Добавить новую машину',
  requiredSpecifications: 'Обязательные спецификации',
  requiredSpecificationsHelp: 'Устаревшие поля, которые backend все еще требует перед утверждением паспорта.',
  openFile: 'Открыть файл',
  imageUploadHelp: 'При выборе изображения оно передается в запросе сохранения паспорта, а затем сохраняется путь к файлу, возвращенный сервером.',
  imageQueued: 'Изображение подготавливается для следующего запроса сохранения...',
  imageQueuedSuccess: 'Изображение поставлено в очередь. Сохраните или утвердите паспорт, чтобы загрузить его на сервер.',
  imageUploadTypeError: 'Здесь можно напрямую загрузить только JPG, PNG, WEBP и GIF. Для других типов используйте серверный URL.',
  attachmentUploadHelp: 'Выбор файла заполняет его метаданные. Вставьте серверный URL после загрузки или публикации файла.',
  attachmentSelectedStatus: 'Файл выбран локально ({size} байт).',
  attachmentUrlRequiredHint: 'Перед утверждением добавьте итоговый серверный URL для этого файла.',
  fileReadError: 'Не удалось прочитать выбранный файл.',
  attachmentsChecklistTitle: 'Вложения, обязательные для утверждения',
  attachmentsFixMainImage: 'Загрузите или укажите основное изображение изделия.',
  attachmentsFixTechnicalDrawing: 'Загрузите или укажите изображение технического рисунка.',
  attachmentsFixMachineFile: 'Добавьте хотя бы один файл машины с сохраненным URL файла.',
  attachmentsFixAttachmentRows: 'Заполните имя файла и URL файла в {count} строк(е/ах) вложений.',
  backendHelpTab: 'Подсказки backend',
  backendHelpTitle: 'Справка по проверкам backend',
  backendHelpIntro: 'На этой вкладке показано, как расшифровать ошибки backend при утверждении и что исправить в паспорте перед повторной отправкой.',
  backendDetectedBadge: 'Обнаружено сейчас',
  backendCurrentIssue: 'Текущая проблема backend',
  backendMissingFieldTitle: 'Не заполнено обязательное поле',
  backendMissingFieldDefinition: 'Backend блокирует утверждение, пока не заполнены все обязательные устаревшие поля и обязательные вложения.',
  backendMissingFieldInstructionGeneral: 'Проверьте на вкладке «Общие» устаревшие обязательные поля спецификации, включая пряжу и параметры резины.',
  backendMissingFieldInstructionAttachments: 'Проверьте на вкладке «Вложения» основное изображение изделия, технический рисунок и файлы программы вязальной машины.',
  backendMissingFieldInstructionApproval: 'После сохранения недостающего поля повторно отправьте паспорт на утверждение.',
  backendUnknownApproverTitle: 'Согласующий не существует',
  backendUnknownApproverDefinition: 'Backend не принимает выбранный идентификатор согласующего. Обычно у сотрудника нет связанного пользователя или в паспорте сохранено устаревшее значение.',
  backendUnknownApproverInstructionPersonnel: 'Откройте вкладку «Утверждение» и выберите сотрудника заново.',
  backendUnknownApproverInstructionReselect: 'Выберите активного сотрудника, связанного с действующей учетной записью пользователя backend.',
  backendUnknownApproverInstructionUsers: 'Если нужный сотрудник отсутствует или ошибка остается, запись персонала должна быть связана с корректным пользователем в backend.',
  backendMissingFieldCurrent: 'Backend сообщает, что отсутствует поле {field}. Заполните его на вкладке {tab}.',
  backendUnknownApproverCurrent: 'Backend сообщает, что выбранное значение для поля {field} не существует.',
  backendOpenTabInstruction: 'Откройте вкладку {tab}.',
  backendCompleteFieldInstruction: 'Заполните или исправьте поле {field} и сохраните черновик.',
  backendSelectPersonnelInstruction: 'Повторно выберите {field} через выбор сотрудника, чтобы сохранить значение из данных personnel.',
  backendRetryApprovalInstruction: 'После сохранения исправленного черновика снова отправьте паспорт на утверждение.',
  backendGenericInstruction: 'Проверьте текст ошибки backend, исправьте связанные данные формы, сохраните черновик и повторите утверждение.',
})

Object.assign(engineeringMessages.en.engineering.passportValidation, {
  approverRequired: 'Select an approver before approving the passport.',
  requiredField: '{field} is required.',
  productRequired: 'A valid product is required.',
  templateRequired: 'Select a template.',
  sockTypeRequired: 'Select a sock type.',
  sockWeight: 'Sock weight must be greater than zero.',
  revisionDescription: 'Describe this revision.',
  authorRequired: 'Enter the author.',
  machineRequired: 'Add at least one compatible machine.',
})

Object.assign((engineeringMessages.uz.engineering as Record<string, any>).passportValidation ?? {}, {
  approverRequired: 'Pasportni tasdiqlashdan oldin tasdiqlovchini tanlang.',
  requiredField: '{field} majburiy.',
  productRequired: 'To‘g‘ri mahsulotni tanlash talab qilinadi.',
  templateRequired: 'Shablonni tanlang.',
  sockTypeRequired: 'Paypoq turini tanlang.',
  sockWeight: 'Paypoq og‘irligi noldan katta bo‘lishi kerak.',
  revisionDescription: 'Ushbu tahrirni tavsiflang.',
  authorRequired: 'Muallifni kiriting.',
  machineRequired: 'Kamida bitta mos uskunani qo‘shing.',
})

Object.assign((engineeringMessages.ru.engineering as Record<string, any>).passportValidation ?? {}, {
  materialId: 'Укажите корректный ID материала.',
  consumption: 'Расход должен быть больше нуля.',
  dimensions: 'Размеры не могут быть отрицательными.',
  element: 'Необходимо указать тип и расположение элемента.',
  yarn: 'Необходимо указать назначение и тип пряжи.',
  operation: 'Введите наименование операции.',
  standardTime: 'Нормативное время не может быть отрицательным.',
  measurement: 'Необходимо указать код, описание и значение.',
  equipment: 'Укажите корректную машину из справочника.',
  needles: 'Количество игл должно быть больше нуля.',
  qc: 'Необходимо указать этап и проверяемый параметр.',
  packaging: 'Укажите ID продукта, ID справочника или наименование упаковки.',
  quantity: 'Количество должно быть больше нуля.',
  label: 'Необходимо указать наименование ярлыка.',
  thread: 'Необходимо указать цвет и код нити.',
  materialName: 'Необходимо указать наименование материала.',
  costs: 'Значения стоимости не могут быть отрицательными.',
  attachment: 'Необходимо указать имя файла и URL.',
  approvedRevision: 'Для утверждённого паспорта необходимо создать новую редакцию.',
  productId: 'Необходим корректный идентификатор продукта.',
  approverRequired: 'Перед утверждением паспорта выберите согласующего.',
  requiredField: 'Поле «{field}» обязательно.',
  productRequired: 'Необходимо выбрать корректный продукт.',
  templateRequired: 'Выберите шаблон.',
  sockTypeRequired: 'Выберите тип носка.',
  sockWeight: 'Вес носка должен быть больше нуля.',
  revisionDescription: 'Опишите изменения в этой редакции.',
  authorRequired: 'Укажите автора.',
  machineRequired: 'Добавьте хотя бы одну совместимую машину.',
})

Object.assign(engineeringMessages.en.engineering, {
  passportPicker: {
    noCode: 'No code', noName: 'No name', page: '{count} items · Page {page} of {total}', retry: 'Retry', previous: 'Previous', next: 'Next', cancel: 'Cancel', addNew: 'Add new', createAutoSelect: 'Create it here, then it will be selected automatically.',
    colorRepository: 'Color repository', searchColors: 'Search colors', nameOrCode: 'Name or code', noColors: 'No matching colors found.', closeColor: 'Close color picker', needColor: 'Need a new color?', createColor: 'Create color',
    seasonRepository: 'Season catalog', searchSeasons: 'Search seasons', noSeasons: 'No matching seasons found.', closeSeason: 'Close season picker',
    equipmentRepository: 'Equipment repository', searchMachines: 'Search machines', noMachines: 'No matching machines found.', closeMachine: 'Close machine picker', needMachine: 'Need a new machine?', createMachine: 'Create machine',
    personnelRepository: 'Personnel repository', searchPersonnel: 'Search personnel', personnelSearchPlaceholder: 'Name, code, title, or department', noPersonnel: 'No matching personnel found.', closePersonnel: 'Close personnel picker', loadingPersonnel: 'Loading personnel...',
    threadRepository: 'Thread code repository', searchThreads: 'Search thread codes', threadSearchPlaceholder: 'Code, name, yarn type', noThreads: 'No matching thread codes found.', closeThread: 'Close thread code picker', needThread: 'Need a new thread code?', createThread: 'Create thread code', repositoryTool: 'Repository tool', closeCreate: 'Close create form', code: 'Code', name: 'Name', yarnType: 'Yarn type', yarnComposition: 'Yarn composition', threadNamePlaceholder: '30-001 Black Main Thread', yarnTypePlaceholder: 'cotton', saving: 'Saving...', create: 'Create',
    dropUpload: 'Drag and drop or click to upload', fileUrlEditable: 'Image or file URL can be edited above.',
  },
})

Object.assign(engineeringMessages.uz.engineering, {
  passportPicker: {
    noCode: 'Kod yo‘q', noName: 'Nom yo‘q', page: '{count} ta element · {page}/{total}-sahifa', retry: 'Qayta urinish', previous: 'Oldingi', next: 'Keyingi', cancel: 'Bekor qilish', addNew: 'Yangi qo‘shish', createAutoSelect: 'Uni shu yerda yarating, so‘ng avtomatik tanlanadi.',
    colorRepository: 'Ranglar ma’lumotnomasi', searchColors: 'Ranglarni qidirish', nameOrCode: 'Nomi yoki kodi', noColors: 'Mos rang topilmadi.', closeColor: 'Rang tanlovini yopish', needColor: 'Yangi rang kerakmi?', createColor: 'Rang yaratish',
    seasonRepository: 'Mavsumlar katalogi', searchSeasons: 'Mavsumlarni qidirish', noSeasons: 'Mos mavsum topilmadi.', closeSeason: 'Mavsum tanlovini yopish',
    equipmentRepository: 'Uskunalar ma’lumotnomasi', searchMachines: 'Uskunalarni qidirish', noMachines: 'Mos uskuna topilmadi.', closeMachine: 'Uskuna tanlovini yopish', needMachine: 'Yangi uskuna kerakmi?', createMachine: 'Uskuna yaratish',
    personnelRepository: 'Xodimlar ma’lumotnomasi', searchPersonnel: 'Xodimlarni qidirish', personnelSearchPlaceholder: 'Ism, kod, lavozim yoki bo‘lim', noPersonnel: 'Mos xodim topilmadi.', closePersonnel: 'Xodim tanlovini yopish', loadingPersonnel: 'Xodimlar yuklanmoqda...',
    threadRepository: 'Ip kodlari ma’lumotnomasi', searchThreads: 'Ip kodlarini qidirish', threadSearchPlaceholder: 'Kod, nom yoki ip turi', noThreads: 'Mos ip kodi topilmadi.', closeThread: 'Ip kodi tanlovini yopish', needThread: 'Yangi ip kodi kerakmi?', createThread: 'Ip kodi yaratish', repositoryTool: 'Ma’lumotnoma vositasi', closeCreate: 'Yaratish shaklini yopish', code: 'Kod', name: 'Nomi', yarnType: 'Ip turi', yarnComposition: 'Ip tarkibi', threadNamePlaceholder: '30-001 Qora asosiy ip', yarnTypePlaceholder: 'paxta', saving: 'Saqlanmoqda...', create: 'Yaratish',
    dropUpload: 'Faylni tashlang yoki yuklash uchun bosing', fileUrlEditable: 'Rasm yoki fayl URL manzilini yuqorida tahrirlash mumkin.',
  },
})

Object.assign(engineeringMessages.ru.engineering, {
  passportPicker: {
    noCode: 'Без кода', noName: 'Без названия', page: '{count} элементов · Страница {page} из {total}', retry: 'Повторить', previous: 'Назад', next: 'Вперёд', cancel: 'Отмена', addNew: 'Добавить', createAutoSelect: 'Создайте запись здесь — она выберется автоматически.',
    colorRepository: 'Справочник цветов', searchColors: 'Поиск цветов', nameOrCode: 'Название или код', noColors: 'Подходящие цвета не найдены.', closeColor: 'Закрыть выбор цвета', needColor: 'Нужен новый цвет?', createColor: 'Создать цвет',
    seasonRepository: 'Справочник сезонов', searchSeasons: 'Поиск сезонов', noSeasons: 'Подходящие сезоны не найдены.', closeSeason: 'Закрыть выбор сезона',
    equipmentRepository: 'Справочник оборудования', searchMachines: 'Поиск машин', noMachines: 'Подходящие машины не найдены.', closeMachine: 'Закрыть выбор машины', needMachine: 'Нужна новая машина?', createMachine: 'Создать машину',
    personnelRepository: 'Справочник персонала', searchPersonnel: 'Поиск сотрудников', personnelSearchPlaceholder: 'Имя, код, должность или подразделение', noPersonnel: 'Подходящие сотрудники не найдены.', closePersonnel: 'Закрыть выбор сотрудника', loadingPersonnel: 'Загрузка сотрудников...',
    threadRepository: 'Справочник кодов нитей', searchThreads: 'Поиск кодов нитей', threadSearchPlaceholder: 'Код, название или тип пряжи', noThreads: 'Подходящие коды нитей не найдены.', closeThread: 'Закрыть выбор кода нити', needThread: 'Нужен новый код нити?', createThread: 'Создать код нити', repositoryTool: 'Инструмент справочника', closeCreate: 'Закрыть форму создания', code: 'Код', name: 'Название', yarnType: 'Тип пряжи', yarnComposition: 'Состав пряжи', threadNamePlaceholder: '30-001 Чёрная основная нить', yarnTypePlaceholder: 'хлопок', saving: 'Сохранение...', create: 'Создать',
    dropUpload: 'Перетащите файл или нажмите для загрузки', fileUrlEditable: 'URL изображения или файла можно изменить выше.',
  },
})

Object.assign(engineeringMessages.en.engineering, {
  simpleOrder: {
    newOrder: 'New order', back: 'Back to production orders', eyebrow: 'Quick production launch', title: 'New production order', help: 'Choose the product and warehouses, confirm the BOM route, then distribute the quantity by color and size.', setup: 'Order setup', product: 'Product', selectProduct: 'Select product', selectColor: 'Select catalog color', productRequired: 'Select a product.', operations: 'BOM production operations', loadingBom: 'Loading active BOM...', chooseProductForBom: 'Select a product to review its production operations.', bomRequired: 'This product needs an active BOM with valid production operations.', matrix: 'Color and size matrix', matrixHelp: 'Enter every color and size combination included in this order.', addCombination: 'Add combination', color: 'Color', size: 'Size', sizePlaceholder: 'Select or enter a size', sizeHint: 'Choose a size used before, or type a new one.', quantity: 'Quantity', remove: 'Remove', total: 'Total order quantity', sourceRequired: 'Select a source warehouse.', destinationRequired: 'Select a destination warehouse.', variantRequired: 'Color, size, and a positive quantity are required.', duplicateVariant: 'This color and size combination is duplicated.', totalRequired: 'Total order quantity must be greater than zero.', activate: 'Activate manufacturing order', activating: 'Activating...',
  },
})

Object.assign(engineeringMessages.uz.engineering, {
  simpleOrder: {
    newOrder: 'Yangi buyurtma', back: 'Ishlab chiqarish buyurtmalariga qaytish', eyebrow: 'Ishlab chiqarishni tez boshlash', title: 'Yangi ishlab chiqarish buyurtmasi', help: 'Mahsulot va omborlarni tanlang, BOM yo‘nalishini tekshiring, so‘ng miqdorni rang va o‘lchamlar bo‘yicha taqsimlang.', setup: 'Buyurtma sozlamalari', product: 'Mahsulot', selectProduct: 'Mahsulotni tanlang', selectColor: 'Katalog rangini tanlang', productRequired: 'Mahsulotni tanlang.', operations: 'BOM ishlab chiqarish operatsiyalari', loadingBom: 'Faol BOM yuklanmoqda...', chooseProductForBom: 'Ishlab chiqarish operatsiyalarini ko‘rish uchun mahsulotni tanlang.', bomRequired: 'Bu mahsulot uchun to‘g‘ri ishlab chiqarish operatsiyalariga ega faol BOM talab qilinadi.', matrix: 'Rang va o‘lcham jadvali', matrixHelp: 'Buyurtmadagi har bir rang va o‘lcham birikmasini kiriting.', addCombination: 'Birikma qo‘shish', color: 'Rang', size: 'O‘lcham', sizePlaceholder: 'O‘lchamni tanlang yoki kiriting', sizeHint: 'Avval ishlatilgan o‘lchamni tanlang yoki yangisini kiriting.', quantity: 'Miqdor', remove: 'Olib tashlash', total: 'Buyurtmaning jami miqdori', sourceRequired: 'Manba omborini tanlang.', destinationRequired: 'Qabul qiluvchi omborni tanlang.', variantRequired: 'Rang, o‘lcham va musbat miqdor majburiy.', duplicateVariant: 'Bu rang va o‘lcham birikmasi takrorlangan.', totalRequired: 'Buyurtmaning jami miqdori noldan katta bo‘lishi kerak.', activate: 'Ishlab chiqarish buyurtmasini faollashtirish', activating: 'Faollashtirilmoqda...',
  },
})

Object.assign(engineeringMessages.ru.engineering, {
  simpleOrder: {
    newOrder: 'Новый заказ', back: 'Назад к производственным заказам', eyebrow: 'Быстрый запуск производства', title: 'Новый производственный заказ', help: 'Выберите продукт и склады, проверьте маршрут BOM, затем распределите количество по цветам и размерам.', setup: 'Параметры заказа', product: 'Продукт', selectProduct: 'Выберите продукт', selectColor: 'Выберите цвет из справочника', productRequired: 'Выберите продукт.', operations: 'Производственные операции BOM', loadingBom: 'Загрузка активного BOM...', chooseProductForBom: 'Выберите продукт, чтобы просмотреть производственные операции.', bomRequired: 'Для продукта требуется активный BOM с корректными производственными операциями.', matrix: 'Матрица цветов и размеров', matrixHelp: 'Введите каждое сочетание цвета и размера в этом заказе.', addCombination: 'Добавить сочетание', color: 'Цвет', size: 'Размер', sizePlaceholder: 'Выберите или введите размер', sizeHint: 'Выберите ранее использованный размер или введите новый.', quantity: 'Количество', remove: 'Удалить', total: 'Общее количество заказа', sourceRequired: 'Выберите склад-источник.', destinationRequired: 'Выберите склад-получатель.', variantRequired: 'Необходимо указать цвет, размер и положительное количество.', duplicateVariant: 'Такое сочетание цвета и размера уже добавлено.', totalRequired: 'Общее количество заказа должно быть больше нуля.', activate: 'Активировать производственный заказ', activating: 'Активация...',
  },
})

Object.assign(engineeringMessages.en.engineering, {
  productForm: {
    backList: 'Back to list',
    backItem: 'Back to item',
    saving: 'Saving...',
    save: 'Save changes',
    editAction: { manufactured: 'Edit product', 'raw-materials': 'Edit raw material', 'spare-parts': 'Edit spare part' },
    createAction: { manufactured: 'Create product', 'raw-materials': 'Create raw material', 'spare-parts': 'Create spare part' },
    create: 'Create product',
    eyebrow: 'Engineering master data',
    createTitle: { manufactured: 'Create manufactured product', raw: 'Create raw material', spare: 'Create spare part' },
    editTitle: { manufactured: 'Edit manufactured product', raw: 'Edit raw material', spare: 'Edit spare part' },
    createDescription: {
      manufactured: 'Register a new manufactured product before preparing BOM, technical passport, and production order data.',
      raw: 'Register a new raw material for engineering, BOM consumption, and production planning.',
      spare: 'Register a new spare part for equipment support and maintenance usage.',
    },
    editDescription: {
      manufactured: 'Update the manufactured product identity, segmentation, pack configuration, and tags.',
      raw: 'Update the raw-material identity, unit, ordering data, and catalog tags.',
      spare: 'Update the spare-part identity, unit, ordering data, and catalog tags.',
    },
    loading: 'Loading form...',
    invalidType: 'This record is not a manufactured product.',
    identity: 'Product identity',
    name: 'Name',
    sku: 'SKU',
    internalSku: 'Internal SKU',
    barcode: 'Barcode',
    photo: 'Product photo',
    photoUrl: 'Photo URL or upload',
    choosePhoto: 'Choose photo',
    photoHelp: 'Pick a JPG, PNG, WEBP or GIF (max 5 MB). It is uploaded with the save request and the server stores the final path.',
    photoQueued: 'Preparing photo for the next save request...',
    photoQueuedSuccess: 'Photo queued. Save the product to upload it.',
    photoTypeError: 'Only JPG, PNG, WEBP, and GIF files can be uploaded here.',
    photoReadError: 'The selected file could not be read.',
    classification: 'Classification',
    type: 'Type',
    group: 'Product group',
    selectGroup: 'Select group',
    audience: 'Audience',
    season: 'Season',
    packaging: 'Packing and order rules',
    unit: 'Unit',
    selectUnit: 'Select unit of measure',
    unitsLoading: 'Loading units...',
    unitHelp: 'Units are loaded from the shared unit-of-measure repository.',
    uoms: 'Product UOMs',
    uomsHelp: 'Configure alternative packaging and entry units that convert to the product base unit.',
    addUom: 'Add UOM',
    removeUom: 'Remove',
    uomsEmpty: 'No product UOMs configured. Add rows only when alternate units are needed.',
    uomsSaveFirst: 'Save the product first to configure product UOMs.',
    uomsLoading: 'Loading product UOMs...',
    uomsSave: 'Save UOMs',
    uomsSaving: 'Saving UOMs...',
    uomsSaved: 'Product UOMs saved.',
    uomBaseSummary: 'Base unit: {unit}',
    uomCode: 'Code',
    uomLabel: 'Label',
    uomFactor: 'To base factor',
    uomBase: 'Base',
    uomFractional: 'Allow decimals',
    uomStatus: 'Active',
    uomActions: 'Actions',
    minOrder: 'Minimum order',
    socksPerPack: 'Socks per pack',
    packsPerBag: 'Packs per bag',
    tagsSection: 'Tags',
    tags: 'Tags',
    tagsPlaceholder: 'thermal, cotton, premium',
    tagsHelp: 'Use comma-separated values to preserve catalog searchability.',
    permissionHint: 'Requires the products.create or products.update permission.',
    validation: {
      name: 'Name is required.',
      sku: 'SKU is required.',
      type: 'Product type is required.',
      unit: 'Unit is required.',
      uomFields: 'Each product UOM row requires a code, label, and positive conversion factor.',
      uomCodeRequired: 'Each product UOM row requires a code.',
      uomLabelRequired: 'Each product UOM row requires a label.',
      uomUnique: 'Product UOM codes must be unique.',
      uomFactor: 'Each product UOM conversion factor must be greater than zero.',
      uomBase: 'Exactly one base UOM is required when product UOMs are provided.',
      minOrder: 'Minimum order must be a positive whole number.',
      socksPerPack: 'Socks per pack must be a positive whole number.',
      packsPerBag: 'Packs per bag must be a positive whole number.',
    },
  },
})

Object.assign(engineeringMessages.uz.engineering, {
  productForm: {
    backList: 'Ro‘yxatga qaytish',
    backItem: 'Elementga qaytish',
    saving: 'Saqlanmoqda...',
    save: 'O‘zgarishlarni saqlash',
    editAction: { manufactured: 'Mahsulotni tahrirlash', 'raw-materials': 'Xomashyoni tahrirlash', 'spare-parts': 'Ehtiyot qismni tahrirlash' },
    createAction: { manufactured: 'Mahsulot yaratish', 'raw-materials': 'Xomashyo yaratish', 'spare-parts': 'Ehtiyot qism yaratish' },
    create: 'Mahsulot yaratish',
    eyebrow: 'Muhandislik ma’lumotnomasi',
    createTitle: { manufactured: 'Ishlab chiqariladigan mahsulot yaratish', raw: 'Xomashyo yaratish', spare: 'Ehtiyot qism yaratish' },
    editTitle: { manufactured: 'Ishlab chiqariladigan mahsulotni tahrirlash', raw: 'Xomashyoni tahrirlash', spare: 'Ehtiyot qismni tahrirlash' },
    createDescription: {
      manufactured: 'BOM, texnik pasport va ishlab chiqarish buyurtmasini tayyorlashdan oldin yangi mahsulotni ro‘yxatdan o‘tkazing.',
      raw: 'Muhandislik, BOM sarfi va ishlab chiqarishni rejalashtirish uchun yangi xomashyoni ro‘yxatdan o‘tkazing.',
      spare: 'Uskunalarni qo‘llab-quvvatlash va texnik xizmat ehtiyojlari uchun yangi ehtiyot qismni ro‘yxatdan o‘tkazing.',
    },
    editDescription: {
      manufactured: 'Mahsulot identifikatori, segmentatsiyasi, qadoqlash sozlamalari va belgilarlarini yangilang.',
      raw: 'Xomashyo identifikatori, birligi, buyurtma ma’lumotlari va katalog belgilarini yangilang.',
      spare: 'Ehtiyot qism identifikatori, birligi, buyurtma ma’lumotlari va katalog belgilarini yangilang.',
    },
    loading: 'Forma yuklanmoqda...',
    invalidType: 'Bu yozuv ishlab chiqariladigan mahsulot emas.',
    identity: 'Mahsulot identifikatori',
    name: 'Nomi',
    sku: 'SKU',
    internalSku: 'Ichki SKU',
    barcode: 'Shtrix-kod',
    photo: 'Mahsulot rasmi',
    photoUrl: 'Rasm URL yoki yuklash',
    choosePhoto: 'Rasm tanlash',
    photoHelp: 'JPG, PNG, WEBP yoki GIF tanlang (5 MB gacha). Rasm saqlash so‘rovi bilan yuboriladi va server yakuniy yo‘lni saqlaydi.',
    photoQueued: 'Rasm keyingi saqlash so‘rovi uchun tayyorlanmoqda...',
    photoQueuedSuccess: 'Rasm navbatga qo‘yildi. Yuklash uchun mahsulotni saqlang.',
    photoTypeError: 'Bu yerga faqat JPG, PNG, WEBP va GIF fayllarini yuklash mumkin.',
    photoReadError: 'Tanlangan faylni o‘qib bo‘lmadi.',
    classification: 'Tasnif',
    type: 'Turi',
    group: 'Mahsulot guruhi',
    selectGroup: 'Guruhni tanlang',
    audience: 'Auditoriya',
    season: 'Mavsum',
    packaging: 'Qadoqlash va buyurtma qoidalari',
    unit: 'Birlik',
    selectUnit: 'O‘lchov birligini tanlang',
    unitsLoading: 'Birliklar yuklanmoqda...',
    unitHelp: 'Birliklar umumiy o‘lchov birligi ma’lumotnomasidan yuklanadi.',
    uoms: 'Mahsulot O‘B lari',
    uomsHelp: 'Mahsulotning bazaviy birligiga aylantiriladigan muqobil qadoqlash va kiritish birliklarini sozlang.',
    addUom: 'O‘B qo‘shish',
    removeUom: 'Olib tashlash',
    uomsEmpty: 'Mahsulot O‘B sozlanmagan. Muqobil birlik kerak bo‘lsa, qator qo‘shing.',
    uomsSaveFirst: 'Mahsulot O‘B larini sozlash uchun avval mahsulotni saqlang.',
    uomsLoading: 'Mahsulot O‘B lari yuklanmoqda...',
    uomsSave: 'O‘B larni saqlash',
    uomsSaving: 'O‘B lar saqlanmoqda...',
    uomsSaved: 'Mahsulot O‘B lari saqlandi.',
    uomBaseSummary: 'Bazaviy birlik: {unit}',
    uomCode: 'Kod',
    uomLabel: 'Nomi',
    uomFactor: 'Bazaga koeffitsiyent',
    uomBase: 'Bazaviy',
    uomFractional: 'Kasrga ruxsat',
    uomStatus: 'Faol',
    uomActions: 'Amallar',
    minOrder: 'Minimal buyurtma',
    socksPerPack: 'Bir pachkadagi paypoq',
    packsPerBag: 'Bir qopdagi pachka',
    tagsSection: 'Belgilar',
    tags: 'Belgilar',
    tagsPlaceholder: 'thermal, cotton, premium',
    tagsHelp: 'Katalog qidiruvini saqlash uchun vergul bilan ajrating.',
    permissionHint: 'Buning uchun products.create yoki products.update ruxsati kerak.',
    validation: {
      name: 'Nomi majburiy.',
      sku: 'SKU majburiy.',
      type: 'Mahsulot turi majburiy.',
      unit: 'Birlik majburiy.',
      uomFields: 'Har bir mahsulot O‘B qatorida kod, nom va musbat konversiya koeffitsiyenti bo‘lishi kerak.',
      uomCodeRequired: 'Har bir mahsulot O‘B qatorida kod bo‘lishi kerak.',
      uomLabelRequired: 'Har bir mahsulot O‘B qatorida nom bo‘lishi kerak.',
      uomUnique: 'Mahsulot O‘B kodlari takrorlanmasligi kerak.',
      uomFactor: 'Har bir mahsulot O‘B konversiya koeffitsiyenti noldan katta bo‘lishi kerak.',
      uomBase: 'Mahsulot O‘B berilganda aynan bitta bazaviy O‘B bo‘lishi kerak.',
      minOrder: 'Minimal buyurtma musbat butun son bo‘lishi kerak.',
      socksPerPack: 'Bir pachkadagi paypoq soni musbat butun son bo‘lishi kerak.',
      packsPerBag: 'Bir qopdagi pachka soni musbat butun son bo‘lishi kerak.',
    },
  },
})

Object.assign(engineeringMessages.ru.engineering, {
  productForm: {
    backList: 'Назад к списку',
    backItem: 'Назад к записи',
    saving: 'Сохранение...',
    save: 'Сохранить изменения',
    editAction: { manufactured: 'Редактировать продукт', 'raw-materials': 'Редактировать сырьё', 'spare-parts': 'Редактировать запчасть' },
    createAction: { manufactured: 'Создать продукт', 'raw-materials': 'Создать сырьё', 'spare-parts': 'Создать запчасть' },
    create: 'Создать продукт',
    eyebrow: 'Инженерный справочник',
    createTitle: { manufactured: 'Создание производимого продукта', raw: 'Создание сырья', spare: 'Создание запчасти' },
    editTitle: { manufactured: 'Редактирование производимого продукта', raw: 'Редактирование сырья', spare: 'Редактирование запчасти' },
    createDescription: {
      manufactured: 'Зарегистрируйте новый продукт до подготовки BOM, технического паспорта и производственного заказа.',
      raw: 'Зарегистрируйте новое сырьё для инженерного учёта, расхода в BOM и производственного планирования.',
      spare: 'Зарегистрируйте новую запчасть для поддержки оборудования и задач обслуживания.',
    },
    editDescription: {
      manufactured: 'Обновите идентификацию продукта, сегментацию, параметры упаковки и теги.',
      raw: 'Обновите идентификацию сырья, единицу, данные заказа и теги каталога.',
      spare: 'Обновите идентификацию запчасти, единицу, данные заказа и теги каталога.',
    },
    loading: 'Загрузка формы...',
    invalidType: 'Эта запись не относится к производимой продукции.',
    identity: 'Идентификация продукта',
    name: 'Наименование',
    sku: 'SKU',
    internalSku: 'Внутренний SKU',
    barcode: 'Штрихкод',
    photo: 'Фото продукта',
    photoUrl: 'URL фото или загрузка',
    choosePhoto: 'Выбрать фото',
    photoHelp: 'Выберите JPG, PNG, WEBP или GIF (до 5 МБ). Файл отправляется вместе с запросом сохранения, сервер сохраняет итоговый путь.',
    photoQueued: 'Фото подготавливается для запроса сохранения...',
    photoQueuedSuccess: 'Фото в очереди. Сохраните продукт, чтобы загрузить его.',
    photoTypeError: 'Здесь можно загрузить только JPG, PNG, WEBP и GIF.',
    photoReadError: 'Не удалось прочитать выбранный файл.',
    classification: 'Классификация',
    type: 'Тип',
    group: 'Группа продукции',
    selectGroup: 'Выберите группу',
    audience: 'Аудитория',
    season: 'Сезон',
    packaging: 'Упаковка и правила заказа',
    unit: 'Единица',
    selectUnit: 'Выберите единицу измерения',
    unitsLoading: 'Загрузка единиц...',
    unitHelp: 'Единицы загружаются из общего справочника единиц измерения.',
    uoms: 'Единицы продукта',
    uomsHelp: 'Настройте альтернативные упаковочные и вводимые единицы, которые переводятся в базовую единицу продукта.',
    addUom: 'Добавить единицу',
    removeUom: 'Удалить',
    uomsEmpty: 'Единицы продукта не настроены. Добавляйте строки только если нужны альтернативные единицы.',
    uomsSaveFirst: 'Сначала сохраните продукт, чтобы настроить единицы продукта.',
    uomsLoading: 'Загрузка единиц продукта...',
    uomsSave: 'Сохранить единицы',
    uomsSaving: 'Сохранение единиц...',
    uomsSaved: 'Единицы продукта сохранены.',
    uomBaseSummary: 'Базовая единица: {unit}',
    uomCode: 'Код',
    uomLabel: 'Наименование',
    uomFactor: 'Коэффициент к базе',
    uomBase: 'Базовая',
    uomFractional: 'Разрешить дробные',
    uomStatus: 'Активна',
    uomActions: 'Действия',
    minOrder: 'Минимальный заказ',
    socksPerPack: 'Носков в упаковке',
    packsPerBag: 'Упаковок в мешке',
    tagsSection: 'Теги',
    tags: 'Теги',
    tagsPlaceholder: 'thermal, cotton, premium',
    tagsHelp: 'Используйте значения через запятую, чтобы сохранить качество поиска по каталогу.',
    permissionHint: 'Требуется разрешение products.create или products.update.',
    validation: {
      name: 'Наименование обязательно.',
      sku: 'SKU обязателен.',
      type: 'Тип продукта обязателен.',
      unit: 'Единица обязательна.',
      uomFields: 'В каждой строке единицы продукта нужны код, наименование и положительный коэффициент перевода.',
      uomCodeRequired: 'В каждой строке единицы продукта нужен код.',
      uomLabelRequired: 'В каждой строке единицы продукта нужно наименование.',
      uomUnique: 'Коды единиц продукта должны быть уникальными.',
      uomFactor: 'Коэффициент перевода каждой единицы продукта должен быть больше нуля.',
      uomBase: 'Если единицы продукта указаны, должна быть ровно одна базовая единица.',
      minOrder: 'Минимальный заказ должен быть положительным целым числом.',
      socksPerPack: 'Количество носков в упаковке должно быть положительным целым числом.',
      packsPerBag: 'Количество упаковок в мешке должно быть положительным целым числом.',
    },
  },
})

Object.assign(engineeringMessages.en.engineering, {
  operations: {
    eyebrow: 'Engineering reference data',
    title: 'Operations',
    description: 'Manage the standard operation catalog used by BOM routing and production engineering.',
    count: 'registered operations',
    createEyebrow: 'New operation',
    createTitle: 'Create operation',
    editEyebrow: 'Update operation',
    editTitle: 'Edit operation',
    code: 'Code',
    name: 'Name',
    sortOrder: 'Sort order',
    dayRate: 'Default day piece rate',
    nightRate: 'Default night piece rate',
    rateCurrency: 'Rate currency',
    rateHelp: 'Used as the fallback piece rate when no product or employee-specific rate exists.',
    notSet: 'Not set',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    codePlaceholder: 'Auto-generated from name',
    codeHint: 'A short code is generated automatically from the name when left empty.',
    sortBy: 'Sort operations',
    toggleStatus: 'Toggle status',
    create: 'Create operation',
    saveChanges: 'Save changes',
    saving: 'Saving...',
    search: 'Search',
    searchPlaceholder: 'Code or operation name',
    loading: 'Loading operations...',
    empty: 'No operations found.',
    edit: 'Edit',
    remove: 'Remove',
    confirmDelete: 'Remove operation "{name}"?',
    selectedOperations: '{count} selected',
    selectOperation: 'Select {name}',
    selectPageOperations: 'Select operations on this page',
    deleteSelected: 'Delete selected',
    deletingSelected: 'Deleting...',
    deletedSelected: '{count} operations deleted.',
    confirmDeleteSelected: 'Delete {count} selected operations?',
    permissionHint: 'Requires the products.update or products.delete permission.',
  },
  equipment: {
    eyebrow: 'Engineering reference data', title: 'Machines & equipment', description: 'Manage machines and equipment available to BOM routes and technical passports.', count: 'registered items', createTitle: 'Add equipment', editTitle: 'Edit equipment', code: 'Code', name: 'Name', sortOrder: 'Sort order', status: 'Status', active: 'Active', inactive: 'Inactive', create: 'Add equipment', saveChanges: 'Save changes', saving: 'Saving...', search: 'Search', searchPlaceholder: 'Code or equipment name', loading: 'Loading equipment...', empty: 'No equipment found.', edit: 'Edit', duplicate: 'Duplicate', remove: 'Remove', confirmDelete: 'Remove equipment "{name}"?', permissionHint: 'Requires the products.update or products.delete permission.', machineTypeUnset: 'Not set', machineTypeOptions: { single_cylinder: 'Single cylinder', double_cylinder: 'Double cylinder', computerized_single_cylinder: 'Computerized single cylinder', computerized_double_cylinder: 'Computerized double cylinder', linked_toe: 'Linked-toe', seamless: 'Seamless (true seamless)', flat_knitting: 'Flat knitting', hosiery: 'Hosiery / pantyhose', other: 'Other' },
  },
  productionOrder: {
    unassigned: 'Unassigned',
    backToPassport: 'Back to technical passport',
    backToOrders: 'Back to production orders',
    backToOrder: 'Back to order',
    backToLotQueue: 'Back to lot queue',
    createPlannedOrder: 'Create planned order',
    creating: 'Creating...',
    loadingPlan: 'Loading production plan...',
    loadingOrders: 'Loading orders...',
    loadingOrder: 'Loading order...',
    loadingLots: 'Loading lots...',
    loadingLot: 'Loading lot...',
    loadingDailyOutput: 'Loading daily output...',
    retry: 'Retry',
    createdEyebrow: 'Production order created',
    plannedQuantityPairs: 'Planned quantity: {count} pairs',
    returnToPassport: 'Return to passport',
    guidedEyebrow: 'Guided production preparation',
    guidedDescription: 'Approved passport #{id} supplies the machine, thread, packaging, and material plan.',
    sectionDefinition: 'Order definition',
    moCode: 'MO code',
    department: 'Department',
    selectDepartment: 'Select department',
    browseDepartments: 'Browse departments',
    plannedPairs: 'Planned pairs',
    departmentId: 'Department ID',
    salesOrder: 'Sales order',
    selectSalesOrder: 'Select sales order',
    browseSalesOrders: 'Browse sales orders',
    salesOrderId: 'Sales order ID',
    sourceWarehouse: 'Source warehouse',
    selectSourceWarehouse: 'Select source warehouse',
    destinationWarehouse: 'Destination warehouse',
    selectDestinationWarehouse: 'Select destination warehouse',
    browseWarehouses: 'Browse warehouses',
    sourceWarehouseId: 'Source warehouse ID',
    destinationWarehouseId: 'Destination warehouse ID',
    plannedStart: 'Planned start',
    notes: 'Notes',
    sectionPassportSetup: 'Passport production setup',
    machine: 'Machine',
    notNamed: 'Not named',
    referenceNumber: 'Reference #{id}',
    needles: 'Needles',
    sockType: 'Sock type',
    packagingPerPair: 'Packaging / pair',
    sectionColorPlan: 'Knitting color plan',
    noThreadRows: 'The passport has no thread rows.',
    color: 'Color',
    yarnCode: 'Yarn code',
    desyatkaQuantity: 'Desyatka quantity',
    sectionMaterials: 'Material requirement preview',
    materialsDescription: 'Waste-adjusted quantities for {count} pairs.',
    refreshPreview: 'Refresh preview',
    refreshPreviewHint: 'Refresh the preview after changing planned quantity.',
    noMaterials: 'No required materials returned.',
    stage: 'Stage',
    material: 'Material',
    perPair: 'Per pair',
    waste: 'Waste',
    required: 'Required',
    browseWarehousesTitle: 'Warehouses',
    browseDepartmentsTitle: 'Departments',
    searchDepartments: 'Search departments',
    searchDepartmentPlaceholder: 'Department name',
    noDepartments: 'No matching departments found.',
    searchWarehouses: 'Search warehouses',
    searchWarehousePlaceholder: 'Warehouse name',
    noWarehouses: 'No matching warehouses found.',
    productionEyebrow: 'Production',
    ordersTitle: 'Production orders',
    ordersDescription: 'Planned and active manufacturing orders.',
    dailyReportLink: 'Daily output report',
    preparingExport: 'Preparing export...',
    exportQueue: 'Export shift queue',
    scheduleRiskSummary: 'Current page schedule risk summary',
    pageOrders: 'Page orders',
    startOverdue: 'Start overdue',
    incomplete: 'Incomplete',
    onTrack: 'On track',
    stageDeltas: 'Stage deltas',
    dailyOutputTitle: 'Daily production output',
    reportDate: 'Report date',
    noDailyOutputForDate: 'No production output recorded for this date.',
    noDailyOutputForFilters: 'No daily output matches the selected filters.',
    allStages: 'All stages',
    employee: 'Employee',
    allEmployees: 'All employees',
    exportDailyCsv: 'Export daily CSV',
    completed: 'Completed',
    rejected: 'Rejected',
    rework: 'Rework',
    sequence: 'Sequence',
    searchMoOrProduct: 'Search MO code or product',
    allStatuses: 'All statuses',
    sortPriority: 'Priority: risk, then earliest start',
    noOrders: 'No production orders found.',
    noOrdersForRisk: 'No orders match this schedule-risk filter.',
    product: 'Product',
    status: 'Status',
    scheduleRisk: 'Schedule risk',
    progress: 'Progress',
    start: 'Start',
    productNumber: 'Product #{id}',
    queueExportEmpty: 'No matching active orders to export.',
    queueExportSuccess: '{count} production orders exported.',
    dailyExportEmpty: 'No matching daily output to export.',
    dailyExportSuccess: '{count} daily output rows exported.',
    filterStageEmployee: 'stage: {stage}; employee: {employee}',
    filterSearchRisk: '{status}{search}{risk}',
    filterSearchPart: '; search: {value}',
    filterRiskPart: '; risk: {value}',
    defaultExportStatuses: 'planned and in progress',
    orderEyebrow: 'Production order',
    exportCsv: 'Export CSV',
    shareHandover: 'Share handover',
    printHandover: 'Print handover',
    openStageBoard: 'Open stage board',
    openLotQueue: 'Open lot queue',
    stagesTitle: 'Stage board',
    stagesDescription: 'Track progress, throughput, and exceptions for each production stage in this order.',
    scheduleAttention: 'Schedule attention:',
    plan: 'Plan',
    actual: 'Actual',
    remaining: 'Remaining',
    completion: 'Completion',
    activeLots: 'Active lots',
    shiftHandover: 'Shift handover',
    supervisorAttention: 'Supervisor attention',
    snapshot: 'Snapshot',
    noExceptions: 'No open production exceptions. Order plan is complete.',
    activeLotsCount: '{count} active lots',
    completedLotsCount: '{count} completed lots',
    totalLotsCount: '{count} total lots',
    stagePlanVsActual: 'Plan versus actual by stage',
    plannedVariantsEyebrow: 'Order plan',
    plannedVariantsTitle: 'Planned colour / size quantities',
    plannedVariantsTotal: 'Total {count}',
    plannedVariantsTotalLabel: 'Total planned',
    plannedVariantsEmpty: 'This order has no colour / size breakdown.',
    deactivateOrder: 'Deactivate order',
    deactivatingOrder: 'Deactivating...',
    confirmDeactivate: 'Deactivate order {code}? It is removed from the active order list and its material reservations are released.',
    bottleneck: 'Bottleneck: {name}',
    noBomStages: 'No BOM operation stages are available.',
    variance: 'Variance',
    yield: 'Yield',
    stageRejectedRework: 'Rejected {rejected} · Rework {rework}',
    stageRejectedReworkEmployee: 'Rejected {rejected} · Rework {rework} · {employee}',
    stageVariantTitle: 'Produced by colour / size',
    stageVariantColour: 'Colour',
    stageVariantSize: 'Size',
    stageVariantQuantity: 'Produced',
    stageVariantEmpty: 'No colour / size output recorded for this stage yet.',
    stageVariantCount: '{count} variants',
    handoverUnitsRemaining: '{count} units remain against the order plan.',
    handoverBottleneckSignal: '{stage} is the lowest-progress open stage at {percent}%.',
    handoverRejectedSignal: '{count} rejected units require supervisor review.',
    handoverReworkSignal: '{count} units remain in rework flow.',
    handoverActiveLotsSignal: '{count} production lots remain active.',
    handoverExported: 'CSV handover exported.',
    handoverShared: 'Handover shared.',
    handoverDownloaded: 'Sharing is unavailable, so the handover text was downloaded.',
    lotsTitle: 'Production lot queue',
    lotsDescription: 'Track operational batches through dynamic BOM stages.',
    totalLots: 'Total lots',
    needsReview: 'Needs review',
    scanOrEnterLot: 'Scan or enter lot number',
    lotPlaceholder: 'LOT-55-20260512-0001',
    openLot: 'Open lot',
    search: 'Search',
    lotNumber: 'Lot number',
    currentStage: 'Current stage',
    needsReviewOnly: 'Needs review only',
    noLotsForFilters: 'No production lots match these filters.',
    lot: 'Lot',
    initial: 'Initial',
    current: 'Current',
    exceptions: 'Exceptions',
    created: 'Created',
    reviewCount: '{count} review',
    clear: 'Clear',
    lotPagination: '{count} lots · Page {page} of {total}',
    previous: 'Previous',
    next: 'Next',
    createLot: 'Create lot',
    createProductionLot: 'Create production lot',
    startingStage: 'Starting stage',
    quantity: 'Quantity',
    cancel: 'Cancel',
    scanRequired: 'Scan or enter a lot number.',
    lotNotFound: 'Lot not found in this production order.',
    lotCreateValidation: 'Select a stage and enter a quantity greater than zero.',
    lotEyebrow: 'Production lot',
    currentStageLabel: 'Current stage:',
    exceptionReviewRequired: 'Production exception requires review',
    exceptionReviewSummary: '{rejected} rejected and {rework} marked for rework across recorded lot activity.',
    qcHandover: 'Quality-control handoff',
    readyToClose: 'Ready to close',
    checksRequired: 'Checks required',
    qualityCheck: 'Quality check',
    confirmAcceptance: 'Confirm acceptance criteria',
    qcRequired: 'Required',
    recordProgress: 'Record stage progress',
    saveProgress: 'Save progress',
    saving: 'Saving...',
    closeCurrentStage: 'Close current stage',
    history: 'History',
    noActivity: 'No activity recorded.',
    employeeFallback: 'Employee',
    selectProductionEmployee: 'Select production employee',
    printLotCard: 'Print lot card',
    qcNoApprovedPassport: 'No approved technical passport is available for this product.',
    qcNoStageChecks: 'No quality checks are defined for the current stage.',
    qcLoadFailed: 'Quality checklist could not be loaded. Stage progress remains available.',
    lotProgressValidation: 'Employee and at least one positive quantity are required.',
    lotEmployeeRequired: 'Select the responsible employee first.',
    lotQcRequired: 'Complete all required quality checks before closing this stage.',
    confirmProgress: 'Confirm {completed} completed, {rejected} rejected, and {rework} rework for {lot}?',
    confirmClose: 'Close stage {stage} for {lot}?',
    stageChecks: '{stage} checks',
    historyDeltas: '+{completed} completed · {rejected} rejected · {rework} rework',
    productionTeam: 'Production team',
    selectEmployee: 'Select employee',
    close: 'Close',
    employeeSearchPlaceholder: 'Name or employee code',
    loadingEmployees: 'Loading employees...',
    noProductionEmployees: 'No active production employees found.',
    productionEmployee: 'Production employee',
    employeePagination: '{count} employees · {page} / {total}',
    reportEyebrow: 'Production report',
    reportDescription: 'Shareable stage and employee output snapshot for a selected production date.',
    printReport: 'Print report',
    rows: 'Rows',
    printEngineering: 'Yuksalish ERP · Engineering',
    date: 'Date',
    moCodeRequired: 'Manufacturing order code is required.',
    departmentRequired: 'Enter a valid production department ID.',
    plannedPairsRequired: 'Planned quantity must be greater than zero.',
    warehousesMustDiffer: 'Source and destination warehouses must differ.',
    colorAndYarnRequired: 'Color and yarn code are required.',
    desyatkaRequired: 'Desyatka quantity must be a whole number greater than zero.',
    passportRequired: 'A valid technical passport is required.',
    passportMustBeApproved: 'Only approved technical passports can prepare production orders.',
    csvFilters: 'Filters',
    csvStageCode: 'Stage code',
    csvEmployeeId: 'Employee ID',
    csvCompletionPercent: 'Completion percent',
    queueShiftSummary: 'Production queue shift summary',
    queueOrders: 'Orders',
    queueTotalPlanned: 'Total planned',
    queueTotalProduced: 'Total produced',
    queueTotalRemaining: 'Total remaining',
    queueOverallCompletionPercent: 'Overall completion percent',
    queueStartDate: 'Start date',
    queueEquipment: 'Equipment',
    queueSubmittedBy: 'Submitted by',
    riskStartOverdueOne: 'Start overdue by {days} day',
    riskStartOverdueMany: 'Start overdue by {days} days',
    riskIncompleteAfterStartOne: 'Incomplete {days} day after start',
    riskIncompleteAfterStartMany: 'Incomplete {days} days after start',
  },
})

Object.assign(engineeringMessages.en.engineering, {
  audiences: {
    eyebrow: 'Engineering reference data',
    title: 'Product audiences',
    description: 'Manage the audience catalog used to classify manufactured products, raw materials, and spare parts.',
    count: 'registered audiences',
    createEyebrow: 'New audience',
    createTitle: 'Create audience',
    editEyebrow: 'Update audience',
    editTitle: 'Edit audience',
    code: 'Code',
    name: 'Name',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    create: 'Create audience',
    saveChanges: 'Save changes',
    saving: 'Saving...',
    search: 'Search',
    searchPlaceholder: 'Audience code or name',
    loading: 'Loading audiences...',
    empty: 'Audiences not found.',
    edit: 'Edit',
    remove: 'Remove',
    confirmDelete: 'Remove audience "{name}"?',
    permissionHint: 'Requires the products.update or products.delete permission.',
  },
  seasons: {
    eyebrow: 'Engineering reference data',
    title: 'Product seasons',
    description: 'Manage the season catalog used to classify manufactured products, raw materials, and spare parts.',
    count: 'registered seasons',
    createEyebrow: 'New season',
    createTitle: 'Create season',
    editEyebrow: 'Update season',
    editTitle: 'Edit season',
    code: 'Code',
    name: 'Name',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    create: 'Create season',
    saveChanges: 'Save changes',
    saving: 'Saving...',
    search: 'Search',
    searchPlaceholder: 'Season code or name',
    loading: 'Loading seasons...',
    empty: 'Seasons not found.',
    edit: 'Edit',
    remove: 'Remove',
    confirmDelete: 'Remove season "{name}"?',
    permissionHint: 'Requires the products.update or products.delete permission.',
  },
  productGroups: {
    eyebrow: 'Product catalog structure',
    title: 'Product groups',
    description: 'Organise products into a hierarchy of groups used by catalog filters and reporting.',
    count: 'registered groups',
    createEyebrow: 'New group',
    createTitle: 'Create group',
    editEyebrow: 'Update group',
    editTitle: 'Edit group',
    name: 'Name',
    code: 'Code',
    parent: 'Parent group',
    parentNone: 'Top level',
    parentMissing: 'Unknown parent',
    parentHint: 'Leave the parent empty to create a top-level group.',
    parentClearUnsupported: 'The API cannot move a group back to the top level. Choose another parent group instead.',
    create: 'Create group',
    saveChanges: 'Save changes',
    saving: 'Saving...',
    search: 'Search',
    searchPlaceholder: 'Group name or code',
    loading: 'Loading groups...',
    empty: 'Product groups not found.',
    edit: 'Edit',
    remove: 'Remove',
    confirmDelete: 'Remove product group "{name}"?',
    deleteHasChildren: '"{name}" still has subgroups. Move or remove them first.',
    permissionHint: 'Requires the products.create, products.update, or products.delete permission.',
  },
  colors: {
    eyebrow: 'Engineering reference data',
    title: 'Colors',
    description: 'Manage the colour catalog used by production order variants, technical passports, and output reports.',
    count: 'registered colors',
    createEyebrow: 'New color',
    createTitle: 'Create color',
    editEyebrow: 'Update color',
    editTitle: 'Edit color',
    code: 'Code',
    name: 'Name',
    sortOrder: 'Sort order',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    create: 'Create color',
    saveChanges: 'Save changes',
    saving: 'Saving...',
    search: 'Search',
    searchPlaceholder: 'Color code or name',
    loading: 'Loading colors...',
    empty: 'Colors not found.',
    edit: 'Edit',
    remove: 'Remove',
    confirmDelete: 'Remove color "{name}"?',
    permissionHint: 'Requires the products.update or products.delete permission.',
  },
  units: {
    eyebrow: 'Engineering reference data',
    title: 'Units of measurement',
    description: 'Manage the standard unit catalog used by products, technical passports, and material consumption.',
    count: 'registered units',
    createEyebrow: 'New unit',
    createTitle: 'Create unit',
    editEyebrow: 'Update unit',
    editTitle: 'Edit unit',
    code: 'Code',
    name: 'Name',
    sortOrder: 'Sort order',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    create: 'Create unit',
    saveChanges: 'Save changes',
    saving: 'Saving...',
    search: 'Search',
    searchPlaceholder: 'Unit code or name',
    loading: 'Loading units...',
    empty: 'Units not found.',
    edit: 'Edit',
    remove: 'Remove',
    confirmDelete: 'Remove unit "{name}"?',
    permissionHint: 'Requires the products.update or products.delete permission.',
  },
})

Object.assign(engineeringMessages.uz.engineering, {
  audiences: {
    eyebrow: 'Muhandislik ma’lumotnomasi',
    title: 'Mahsulot auditoriyalari',
    description: 'Ishlab chiqariladigan mahsulotlar, xomashyolar va ehtiyot qismlarni tasniflashda ishlatiladigan auditoriya katalogini boshqaring.',
    count: 'ro‘yxatdan o‘tgan auditoriyalar',
    createEyebrow: 'Yangi auditoriya',
    createTitle: 'Auditoriya yaratish',
    editEyebrow: 'Auditoriyani yangilash',
    editTitle: 'Auditoriyani tahrirlash',
    code: 'Kod',
    name: 'Nomi',
    status: 'Holat',
    active: 'Faol',
    inactive: 'Nofaol',
    create: 'Auditoriya yaratish',
    saveChanges: 'O‘zgarishlarni saqlash',
    saving: 'Saqlanmoqda...',
    search: 'Qidirish',
    searchPlaceholder: 'Auditoriya kodi yoki nomi',
    loading: 'Auditoriyalar yuklanmoqda...',
    empty: 'Auditoriyalar topilmadi.',
    edit: 'Tahrirlash',
    remove: 'Olib tashlash',
    confirmDelete: '"{name}" auditoriyasi olib tashlansinmi?',
    permissionHint: 'Buning uchun products.update yoki products.delete ruxsati kerak.',
  },
  seasons: {
    eyebrow: 'Muhandislik ma’lumotnomasi',
    title: 'Mahsulot mavsumlari',
    description: 'Ishlab chiqariladigan mahsulotlar, xomashyolar va ehtiyot qismlarni tasniflashda ishlatiladigan mavsum katalogini boshqaring.',
    count: 'ro‘yxatdan o‘tgan mavsumlar',
    createEyebrow: 'Yangi mavsum',
    createTitle: 'Mavsum yaratish',
    editEyebrow: 'Mavsumni yangilash',
    editTitle: 'Mavsumni tahrirlash',
    code: 'Kod',
    name: 'Nomi',
    status: 'Holat',
    active: 'Faol',
    inactive: 'Nofaol',
    create: 'Mavsum yaratish',
    saveChanges: 'O‘zgarishlarni saqlash',
    saving: 'Saqlanmoqda...',
    search: 'Qidirish',
    searchPlaceholder: 'Mavsum kodi yoki nomi',
    loading: 'Mavsumlar yuklanmoqda...',
    empty: 'Mavsumlar topilmadi.',
    edit: 'Tahrirlash',
    remove: 'Olib tashlash',
    confirmDelete: '"{name}" mavsumi olib tashlansinmi?',
    permissionHint: 'Buning uchun products.update yoki products.delete ruxsati kerak.',
  },
  productGroups: {
    eyebrow: 'Mahsulot katalogi tuzilishi',
    title: 'Mahsulot guruhlari',
    description: 'Mahsulotlarni katalog filtrlari va hisobotlarida ishlatiladigan guruhlar ierarxiyasiga ajratib turing.',
    count: 'ro‘yxatdan o‘tgan guruhlar',
    createEyebrow: 'Yangi guruh',
    createTitle: 'Guruh yaratish',
    editEyebrow: 'Guruhni yangilash',
    editTitle: 'Guruhni tahrirlash',
    name: 'Nomi',
    code: 'Kod',
    parent: 'Yuqori guruh',
    parentNone: 'Yuqori daraja',
    parentMissing: 'Noma’lum yuqori guruh',
    parentHint: 'Yuqori darajadagi guruh yaratish uchun ota guruhni bo‘sh qoldiring.',
    parentClearUnsupported: 'API guruhni qayta yuqori darajaga ko‘chira olmaydi. Boshqa ota guruhni tanlang.',
    create: 'Guruh yaratish',
    saveChanges: 'O‘zgarishlarni saqlash',
    saving: 'Saqlanmoqda...',
    search: 'Qidirish',
    searchPlaceholder: 'Guruh nomi yoki kodi',
    loading: 'Guruhlar yuklanmoqda...',
    empty: 'Mahsulot guruhlari topilmadi.',
    edit: 'Tahrirlash',
    remove: 'Olib tashlash',
    confirmDelete: '"{name}" mahsulot guruhi olib tashlansinmi?',
    deleteHasChildren: '"{name}" guruhida kichik guruhlar bor. Avval ularni ko‘chiring yoki olib tashlang.',
    permissionHint: 'Buning uchun products.create, products.update yoki products.delete ruxsati kerak.',
  },
  colors: {
    eyebrow: 'Muhandislik ma’lumotnomasi',
    title: 'Ranglar',
    description: 'Ishlab chiqarish buyurtma variantlari, texnik pasportlar va chiqim hisobotlarida ishlatiladigan rang katalogini boshqaring.',
    count: 'ro‘yxatdan o‘tgan ranglar',
    createEyebrow: 'Yangi rang',
    createTitle: 'Rang yaratish',
    editEyebrow: 'Rangni yangilash',
    editTitle: 'Rangni tahrirlash',
    code: 'Kod',
    name: 'Nomi',
    sortOrder: 'Saralash tartibi',
    dayRate: 'Kunduzgi standart ishbay narx',
    nightRate: 'Tungi standart ishbay narx',
    rateCurrency: 'Narx valyutasi',
    rateHelp: 'Mahsulot yoki xodim uchun alohida narx bo‘lmaganda standart ishbay narx sifatida ishlatiladi.',
    notSet: 'Belgilanmagan',
    status: 'Holat',
    active: 'Faol',
    inactive: 'Nofaol',
    create: 'Rang yaratish',
    saveChanges: 'O‘zgarishlarni saqlash',
    saving: 'Saqlanmoqda...',
    search: 'Qidirish',
    searchPlaceholder: 'Rang kodi yoki nomi',
    loading: 'Ranglar yuklanmoqda...',
    empty: 'Ranglar topilmadi.',
    edit: 'Tahrirlash',
    remove: 'Olib tashlash',
    confirmDelete: '"{name}" rangi olib tashlansinmi?',
    permissionHint: 'Buning uchun products.update yoki products.delete ruxsati kerak.',
  },
  equipment: {
    eyebrow: 'Muhandislik ma’lumotnomasi', title: 'Mashina va uskunalar', description: 'BOM yo‘nalishlari va texnik pasportlarda ishlatiladigan mashina hamda uskunalarni boshqaring.', count: 'ro‘yxatdagi uskunalar', createTitle: 'Uskuna qo‘shish', editTitle: 'Uskunani tahrirlash', code: 'Kod', name: 'Nomi', sortOrder: 'Saralash tartibi', status: 'Holat', active: 'Faol', inactive: 'Nofaol', create: 'Uskuna qo‘shish', saveChanges: 'O‘zgarishlarni saqlash', saving: 'Saqlanmoqda...', search: 'Qidirish', searchPlaceholder: 'Kod yoki uskuna nomi', loading: 'Uskunalar yuklanmoqda...', empty: 'Uskunalar topilmadi.', edit: 'Tahrirlash', duplicate: 'Nusxalash', remove: 'Olib tashlash', confirmDelete: '"{name}" uskunasi olib tashlansinmi?', permissionHint: 'Buning uchun products.update yoki products.delete ruxsati kerak.', machineTypeUnset: 'Belgilanmagan', machineTypeOptions: { single_cylinder: 'Bir silindrli', double_cylinder: 'Ikki silindrli', computerized_single_cylinder: 'Kompyuterlashtirilgan bir silindrli', computerized_double_cylinder: 'Kompyuterlashtirilgan ikki silindrli', linked_toe: 'Bog‘langan barmoq (linked-toe)', seamless: 'Chokssiz (true seamless)', flat_knitting: 'Yassi to‘qish', hosiery: 'Paypoq / kolgotka', other: 'Boshqa' },
  },
  units: {
    eyebrow: 'Muhandislik ma’lumotnomasi',
    title: 'O‘lchov birliklari',
    description: 'Mahsulotlar, texnik pasportlar va material sarfida ishlatiladigan standart o‘lchov birliklari katalogini boshqaring.',
    count: 'ro‘yxatdan o‘tgan birliklar',
    createEyebrow: 'Yangi birlik',
    createTitle: 'Birlik yaratish',
    editEyebrow: 'Birlikni yangilash',
    editTitle: 'Birlikni tahrirlash',
    code: 'Kod',
    name: 'Nomi',
    sortOrder: 'Saralash tartibi',
    status: 'Holat',
    active: 'Faol',
    inactive: 'Nofaol',
    create: 'Birlik yaratish',
    saveChanges: 'O‘zgarishlarni saqlash',
    saving: 'Saqlanmoqda...',
    search: 'Qidirish',
    searchPlaceholder: 'Kod yoki birlik nomi',
    loading: 'O‘lchov birliklari yuklanmoqda...',
    empty: 'O‘lchov birliklari topilmadi.',
    edit: 'Tahrirlash',
    remove: 'Olib tashlash',
    confirmDelete: '"{name}" birligi olib tashlansinmi?',
    permissionHint: 'Buning uchun products.update yoki products.delete ruxsati kerak.',
  },
  operations: {
    eyebrow: 'Muhandislik ma’lumotnomasi',
    title: 'Operatsiyalar',
    description: 'BOM yo‘nalishlari va ishlab chiqarish muhandisligi uchun ishlatiladigan standart operatsiyalar katalogini boshqaring.',
    count: 'ro‘yxatdan o‘tgan operatsiyalar',
    createEyebrow: 'Yangi operatsiya',
    createTitle: 'Operatsiya yaratish',
    editEyebrow: 'Operatsiyani yangilash',
    editTitle: 'Operatsiyani tahrirlash',
    code: 'Kod',
    name: 'Nomi',
    sortOrder: 'Saralash tartibi',
    status: 'Holat',
    active: 'Faol',
    inactive: 'Nofaol',
    codePlaceholder: 'Nomi asosida avtomatik',
    codeHint: 'Bo‘sh qoldirilsa, qisqa kod nomi asosida avtomatik yaratiladi.',
    sortBy: 'Operatsiyalarni saralash',
    toggleStatus: 'Holatni almashtirish',
    create: 'Operatsiya yaratish',
    saveChanges: 'O‘zgarishlarni saqlash',
    saving: 'Saqlanmoqda...',
    search: 'Qidirish',
    searchPlaceholder: 'Kod yoki operatsiya nomi',
    loading: 'Operatsiyalar yuklanmoqda...',
    empty: 'Operatsiyalar topilmadi.',
    edit: 'Tahrirlash',
    remove: 'Olib tashlash',
    confirmDelete: '"{name}" operatsiyasi olib tashlansinmi?',
    selectedOperations: '{count} ta tanlangan',
    selectOperation: '{name} ni tanlash',
    selectPageOperations: 'Bu sahifadagi operatsiyalarni tanlash',
    deleteSelected: 'Tanlanganlarni o‘chirish',
    deletingSelected: 'O‘chirilmoqda...',
    deletedSelected: '{count} ta operatsiya o‘chirildi.',
    confirmDeleteSelected: '{count} ta tanlangan operatsiya o‘chirilsinmi?',
    permissionHint: 'Buning uchun products.update yoki products.delete ruxsati kerak.',
  },
  productionOrder: {
    unassigned: 'Biriktirilmagan',
    backToPassport: 'Texnik pasportga qaytish',
    backToOrders: 'Ishlab chiqarish buyurtmalariga qaytish',
    createPlannedOrder: 'Rejalashtirilgan buyurtma yaratish',
    creating: 'Yaratilmoqda...',
    loadingPlan: 'Ishlab chiqarish rejasi yuklanmoqda...',
    loadingOrders: 'Ishlab chiqarish buyurtmalari yuklanmoqda...',
    loadingOrder: 'Buyurtma yuklanmoqda...',
    loadingDailyOutput: 'Kunlik ishlab chiqarish natijalari yuklanmoqda...',
    retry: 'Qayta urinish',
    createdEyebrow: 'Ishlab chiqarish buyurtmasi yaratildi',
    plannedQuantityPairs: 'Rejalashtirilgan miqdor: {count} juft',
    status: 'Holat',
    returnToPassport: 'Pasportga qaytish',
    guidedEyebrow: 'Yo‘naltirilgan ishlab chiqarish tayyorgarligi',
    guidedDescription: 'Tasdiqlangan pasport #{id} uskuna, ip, qadoqlash va material rejasini beradi.',
    sectionDefinition: 'Buyurtma ta’rifi',
    moCode: 'MO kodi',
    department: 'Bo‘lim',
    selectDepartment: 'Bo‘limni tanlang',
    browseDepartments: 'Bo‘limlarni ko‘rish',
    plannedPairs: 'Rejalashtirilgan juftlar',
    departmentId: 'Bo‘lim ID',
    salesOrder: 'Savdo buyurtmasi',
    selectSalesOrder: 'Savdo buyurtmasini tanlang',
    browseSalesOrders: 'Savdo buyurtmalarini ko‘rish',
    salesOrderId: 'Savdo buyurtmasi ID',
    sourceWarehouse: 'Manba ombor',
    selectSourceWarehouse: 'Manba omborni tanlang',
    destinationWarehouse: 'Qabul ombori',
    selectDestinationWarehouse: 'Qabul omborini tanlang',
    browseWarehouses: 'Omborlarni ko‘rish',
    sourceWarehouseId: 'Manba ombor ID',
    destinationWarehouseId: 'Qabul ombori ID',
    plannedStart: 'Rejalashtirilgan boshlanish',
    notes: 'Izohlar',
    sectionPassportSetup: 'Pasport ishlab chiqarish sozlamasi',
    machine: 'Uskuna',
    notNamed: 'Nomlanmagan',
    referenceNumber: 'Reference #{id}',
    needles: 'Ignalar',
    sockType: 'Paypoq turi',
    packagingPerPair: 'Qadoq / juft',
    sectionColorPlan: 'To‘qish rang rejasi',
    noThreadRows: 'Pasportda ip qatorlari yo‘q.',
    color: 'Rang',
    yarnCode: 'Ip kodi',
    desyatkaQuantity: 'O‘nlik miqdori',
    sectionMaterials: 'Material talabi ko‘rinishi',
    materialsDescription: '{count} juft uchun chiqindi hisoblangan miqdorlar.',
    refreshPreview: 'Ko‘rinishni yangilash',
    refreshPreviewHint: 'Rejalashtirilgan miqdor o‘zgargandan keyin ko‘rinishni yangilang.',
    noMaterials: 'Kerakli materiallar qaytmadi.',
    stage: 'Bosqich',
    material: 'Material',
    perPair: 'Har juftga',
    waste: 'Chiqindi',
    required: 'Talab qilinadi',
    browseWarehousesTitle: 'Omborlar',
    browseDepartmentsTitle: 'Bo‘limlar',
    searchDepartments: 'Bo‘limlarni qidirish',
    searchDepartmentPlaceholder: 'Bo‘lim nomi',
    noDepartments: 'Mos bo‘limlar topilmadi.',
    searchWarehouses: 'Omborlarni qidirish',
    searchWarehousePlaceholder: 'Ombor nomi',
    noWarehouses: 'Mos omborlar topilmadi.',
    productionEyebrow: 'Ishlab chiqarish',
    ordersTitle: 'Ishlab chiqarish buyurtmalari',
    ordersDescription: 'Rejalashtirilgan va faol ishlab chiqarish buyurtmalari.',
    dailyReportLink: 'Kunlik ishlab chiqarish hisoboti',
    preparingExport: 'Eksport tayyorlanmoqda...',
    exportQueue: 'Smena navbatini eksport qilish',
    scheduleRiskSummary: 'Joriy sahifadagi jadval xavfi xulosasi',
    pageOrders: 'Sahifadagi buyurtmalar',
    startOverdue: 'Boshlanish kechikkan',
    incomplete: 'Tugallanmagan',
    onTrack: 'Rejada',
    stageDeltas: 'Bosqich kesimidagi natijalar',
    dailyOutputTitle: 'Kunlik ishlab chiqarish natijalari',
    reportDate: 'Hisobot sanasi',
    noDailyOutputForDate: 'Bu sana uchun ishlab chiqarish natijalari qayd etilmagan.',
    noDailyOutputForFilters: 'Tanlangan filtrlarga mos kunlik natijalar topilmadi.',
    allStages: 'Barcha bosqichlar',
    employee: 'Xodim',
    allEmployees: 'Barcha xodimlar',
    exportDailyCsv: 'Kunlik CSV eksporti',
    completed: 'Bajarilgan',
    rejected: 'Rad etilgan',
    rework: 'Qayta ishlash',
    sequence: 'Tartib',
    searchMoOrProduct: 'MO kodi yoki mahsulotni qidiring',
    allStatuses: 'Barcha holatlar',
    sortPriority: 'Ustuvorlik: xavf, so‘ng eng erta boshlanish',
    noOrders: 'Ishlab chiqarish buyurtmalari topilmadi.',
    noOrdersForRisk: 'Bu jadval-xavf filtri uchun buyurtmalar topilmadi.',
    product: 'Mahsulot',
    scheduleRisk: 'Jadval xavfi',
    progress: 'Jarayon',
    start: 'Boshlanish',
    productNumber: 'Mahsulot #{id}',
    queueExportEmpty: 'Eksport uchun mos faol buyurtmalar topilmadi.',
    queueExportSuccess: '{count} ta ishlab chiqarish buyurtmasi eksport qilindi.',
    dailyExportEmpty: 'Eksport uchun mos kunlik natijalar yo‘q.',
    dailyExportSuccess: '{count} ta kunlik natija qatori eksport qilindi.',
    filterStageEmployee: 'bosqich: {stage}; xodim: {employee}',
    filterSearchRisk: '{status}{search}{risk}',
    filterSearchPart: '; qidiruv: {value}',
    filterRiskPart: '; xavf: {value}',
    defaultExportStatuses: 'rejalashtirilgan va jarayonda',
    orderEyebrow: 'Ishlab chiqarish buyurtmasi',
    exportCsv: 'CSV eksport qilish',
    shareHandover: 'Smena topshirig‘ini ulashish',
    printHandover: 'Smena topshirig‘ini chop etish',
    openStageBoard: 'Bosqichlar doskasini ochish',
    openLotQueue: 'Lot navbatini ochish',
    stagesTitle: 'Bosqichlar doskasi',
    stagesDescription: 'Ushbu buyurtma bo‘yicha har bir ishlab chiqarish bosqichining jarayoni, unumdorligi va istisnolarini kuzating.',
    scheduleAttention: 'Jadval bo‘yicha e’tibor:',
    plan: 'Reja',
    actual: 'Amalda',
    remaining: 'Qolgan',
    completion: 'Bajarilish',
    activeLots: 'Faol lotlar',
    shiftHandover: 'Smena topshirig‘i',
    supervisorAttention: 'Rahbar e’tibori',
    snapshot: 'Holat nusxasi',
    noExceptions: 'Ochiq ishlab chiqarish muammolari yo‘q. Buyurtma rejasi to‘liq.',
    activeLotsCount: '{count} ta faol lot',
    completedLotsCount: '{count} ta yakunlangan lot',
    totalLotsCount: '{count} ta jami lot',
    stagePlanVsActual: 'Bosqichlar bo‘yicha reja va amaldagi natija',
    plannedVariantsEyebrow: 'Buyurtma rejasi',
    plannedVariantsTitle: 'Reja: rang / o‘lcham miqdorlari',
    plannedVariantsTotal: 'Jami {count}',
    plannedVariantsTotalLabel: 'Jami reja',
    plannedVariantsEmpty: 'Bu buyurtmada rang / o‘lcham taqsimoti yo‘q.',
    deactivateOrder: 'Buyurtmani bekor qilish',
    deactivatingOrder: 'Bekor qilinmoqda...',
    confirmDeactivate: '{code} buyurtmasi faolsizlantirilsinmi? U faol buyurtmalar ro‘yxatidan olib tashlanadi va material zahiralari bo‘shatiladi.',
    bottleneck: 'Tor joy: {name}',
    noBomStages: 'BOM operatsiya bosqichlari mavjud emas.',
    variance: 'Farq',
    yield: 'Samaradorlik',
    stageRejectedRework: 'Rad etilgan {rejected} · Qayta ishlash {rework}',
    stageRejectedReworkEmployee: 'Rad etilgan {rejected} · Qayta ishlash {rework} · {employee}',
    stageVariantTitle: 'Rang / o‘lcham bo‘yicha ishlab chiqarilgan',
    stageVariantColour: 'Rang',
    stageVariantSize: 'O‘lcham',
    stageVariantQuantity: 'Ishlab chiqarilgan',
    stageVariantEmpty: 'Bu bosqich uchun rang / o‘lcham bo‘yicha chiqim hali qayd etilmagan.',
    stageVariantCount: '{count} ta variant',
    handoverUnitsRemaining: 'Buyurtma rejasiga nisbatan {count} dona qoldi.',
    handoverBottleneckSignal: '{stage} eng past ochiq bosqich bo‘lib, {percent}% da turibdi.',
    handoverRejectedSignal: '{count} ta rad etilgan birlik rahbar ko‘rib chiqishini talab qiladi.',
    handoverReworkSignal: '{count} ta birlik qayta ishlash jarayonida qolmoqda.',
    handoverActiveLotsSignal: '{count} ta ishlab chiqarish loti hali ham faol.',
    handoverExported: 'CSV topshiriq eksport qilindi.',
    handoverShared: 'Topshiriq ulashildi.',
    handoverDownloaded: 'Ulashish mavjud emas, shu sababli topshiriq matni yuklab olindi.',
    backToOrder: 'Buyurtmaga qaytish',
    backToLotQueue: 'Lotlar navbatiga qaytish',
    loadingLots: 'Lotlar yuklanmoqda...',
    loadingLot: 'Lot yuklanmoqda...',
    lotsTitle: 'Ishlab chiqarish lotlari navbati',
    lotsDescription: 'Operatsion partiyalarni dinamik BOM bosqichlari bo‘yicha kuzating.',
    totalLots: 'Jami lotlar',
    needsReview: 'Ko‘rib chiqish kerak',
    scanOrEnterLot: 'Lot raqamini skanerlang yoki kiriting',
    lotPlaceholder: 'LOT-55-20260512-0001',
    openLot: 'Lotni ochish',
    search: 'Qidirish',
    lotNumber: 'Lot raqami',
    currentStage: 'Joriy bosqich',
    needsReviewOnly: 'Faqat ko‘rib chiqilishi kerak bo‘lganlar',
    noLotsForFilters: 'Tanlangan filtrlarga mos ishlab chiqarish lotlari topilmadi.',
    lot: 'Lot',
    initial: 'Boshlang‘ich',
    current: 'Joriy',
    exceptions: 'Istisnolar',
    created: 'Yaratilgan',
    reviewCount: '{count} ta ko‘rib chiqish',
    clear: 'Muammosiz',
    lotPagination: '{count} ta lot · {page}/{total}-sahifa',
    previous: 'Oldingi',
    next: 'Keyingi',
    createLot: 'Lot yaratish',
    createProductionLot: 'Ishlab chiqarish lotini yaratish',
    startingStage: 'Boshlang‘ich bosqich',
    quantity: 'Miqdor',
    cancel: 'Bekor qilish',
    scanRequired: 'Lot raqamini skanerlang yoki kiriting.',
    lotNotFound: 'Bu ishlab chiqarish buyurtmasida lot topilmadi.',
    lotCreateValidation: 'Bosqichni tanlang va noldan katta miqdor kiriting.',
    lotEyebrow: 'Ishlab chiqarish loti',
    currentStageLabel: 'Joriy bosqich:',
    exceptionReviewRequired: 'Ishlab chiqarish istisnosini ko‘rib chiqish kerak',
    exceptionReviewSummary: 'Qayd etilgan lot faoliyatida {rejected} ta rad etilgan va {rework} ta qayta ishlashga belgilangan.',
    qcHandover: 'Sifat nazoratiga topshirish',
    stageChecks: '{stage} bosqichi tekshiruvlari',
    readyToClose: 'Yopishga tayyor',
    checksRequired: 'Tekshiruvlar talab qilinadi',
    qualityCheck: 'Sifat tekshiruvi',
    confirmAcceptance: 'Qabul mezonlariga mosligini tasdiqlang',
    qcRequired: 'Majburiy',
    recordProgress: 'Bosqich natijasini qayd etish',
    saveProgress: 'Natijani saqlash',
    closeCurrentStage: 'Joriy bosqichni yopish',
    history: 'Tarix',
    noActivity: 'Faoliyat qayd etilmagan.',
    employeeFallback: 'Xodim',
    selectProductionEmployee: 'Ishlab chiqarish xodimini tanlang',
    printLotCard: 'Lot kartasini chop etish',
    qcNoApprovedPassport: 'Bu mahsulot uchun tasdiqlangan texnik pasport mavjud emas.',
    qcNoStageChecks: 'Joriy bosqich uchun sifat tekshiruvlari belgilanmagan.',
    qcLoadFailed: 'Sifat tekshiruvlari ro‘yxatini yuklab bo‘lmadi. Bosqich natijasini kiritish mumkin.',
    lotProgressValidation: 'Xodim va kamida bitta musbat miqdor talab qilinadi.',
    lotEmployeeRequired: 'Avval mas’ul xodimni tanlang.',
    lotQcRequired: 'Bosqichni yopishdan oldin barcha majburiy sifat tekshiruvlarini bajaring.',
    confirmProgress: '{lot} uchun {completed} ta bajarilgan, {rejected} ta rad etilgan va {rework} ta qayta ishlash natijasi tasdiqlansinmi?',
    confirmClose: '{lot} uchun {stage} bosqichi yopilsinmi?',
    historyDeltas: '+{completed} bajarilgan · {rejected} rad etilgan · {rework} qayta ishlash',
    productionTeam: 'Ishlab chiqarish jamoasi',
    selectEmployee: 'Xodimni tanlash',
    close: 'Yopish',
    employeeSearchPlaceholder: 'Ism yoki xodim kodi',
    loadingEmployees: 'Xodimlar yuklanmoqda...',
    noProductionEmployees: 'Faol ishlab chiqarish xodimlari topilmadi.',
    productionEmployee: 'Ishlab chiqarish xodimi',
    employeePagination: '{count} ta xodim · {page} / {total}',
    reportEyebrow: 'Ishlab chiqarish hisoboti',
    reportDescription: 'Tanlangan sana bo‘yicha bosqichlar va xodimlar natijalarining ulashiladigan ko‘rinishi.',
    printReport: 'Hisobotni chop etish',
    rows: 'Qatorlar',
    printEngineering: 'Yuksalish ERP · Muhandislik',
    date: 'Sana',
    csvFilters: 'Filtrlar',
    csvStageCode: 'Bosqich kodi',
    csvEmployeeId: 'Xodim ID',
    csvCompletionPercent: 'Bajarilish foizi',
    queueShiftSummary: 'Ishlab chiqarish navbati smena xulosasi',
    queueOrders: 'Buyurtmalar',
    queueTotalPlanned: 'Jami rejalashtirilgan',
    queueTotalProduced: 'Jami ishlab chiqarilgan',
    queueTotalRemaining: 'Jami qolgan',
    queueOverallCompletionPercent: 'Umumiy bajarilish foizi',
    queueStartDate: 'Boshlanish sanasi',
    queueEquipment: 'Uskuna',
    queueSubmittedBy: 'Yuborgan',
    riskStartOverdueOne: 'Boshlanish {days} kunga kechikkan',
    riskStartOverdueMany: 'Boshlanish {days} kunga kechikkan',
    riskIncompleteAfterStartOne: 'Boshlanganidan {days} kun o‘tib ham tugallanmagan',
    riskIncompleteAfterStartMany: 'Boshlanganidan {days} kun o‘tib ham tugallanmagan',
    moCodeRequired: 'Ishlab chiqarish buyurtmasi kodi majburiy.',
    departmentRequired: 'To‘g‘ri ishlab chiqarish bo‘limini tanlang.',
    plannedPairsRequired: 'Rejalashtirilgan miqdor noldan katta bo‘lishi kerak.',
    warehousesMustDiffer: 'Manba va qabul omborlari turlicha bo‘lishi kerak.',
    colorAndYarnRequired: 'Rang va ip kodi majburiy.',
    desyatkaRequired: 'O‘nlik miqdori noldan katta butun son bo‘lishi kerak.',
    passportRequired: 'To‘g‘ri texnik pasport kerak.',
    passportMustBeApproved: 'Ishlab chiqarish buyurtmasini faqat tasdiqlangan texnik pasport tayyorlay oladi.',
  },
})

Object.assign(engineeringMessages.ru.engineering, {
  audiences: {
    eyebrow: 'Инженерный справочник',
    title: 'Аудитории продукции',
    description: 'Управляйте каталогом аудиторий, который используется для классификации выпускаемой продукции, сырья и запчастей.',
    count: 'зарегистрированных аудиторий',
    createEyebrow: 'Новая аудитория',
    createTitle: 'Создать аудиторию',
    editEyebrow: 'Обновление аудитории',
    editTitle: 'Редактировать аудиторию',
    code: 'Код',
    name: 'Наименование',
    status: 'Статус',
    active: 'Активна',
    inactive: 'Неактивна',
    create: 'Создать аудиторию',
    saveChanges: 'Сохранить изменения',
    saving: 'Сохранение...',
    search: 'Поиск',
    searchPlaceholder: 'Код или название аудитории',
    loading: 'Загрузка аудиторий...',
    empty: 'Аудитории не найдены.',
    edit: 'Редактировать',
    remove: 'Удалить',
    confirmDelete: 'Удалить аудиторию "{name}"?',
    permissionHint: 'Требуется разрешение products.update или products.delete.',
  },
  seasons: {
    eyebrow: 'Инженерный справочник',
    title: 'Сезоны продукции',
    description: 'Управляйте каталогом сезонов, который используется для классификации выпускаемой продукции, сырья и запчастей.',
    count: 'зарегистрированных сезонов',
    createEyebrow: 'Новый сезон',
    createTitle: 'Создать сезон',
    editEyebrow: 'Обновление сезона',
    editTitle: 'Редактировать сезон',
    code: 'Код',
    name: 'Наименование',
    status: 'Статус',
    active: 'Активен',
    inactive: 'Неактивен',
    create: 'Создать сезон',
    saveChanges: 'Сохранить изменения',
    saving: 'Сохранение...',
    search: 'Поиск',
    searchPlaceholder: 'Код или название сезона',
    loading: 'Загрузка сезонов...',
    empty: 'Сезоны не найдены.',
    edit: 'Редактировать',
    remove: 'Удалить',
    confirmDelete: 'Удалить сезон "{name}"?',
    permissionHint: 'Требуется разрешение products.update или products.delete.',
  },
  productGroups: {
    eyebrow: 'Структура каталога продукции',
    title: 'Группы продуктов',
    description: 'Организуйте продукцию в иерархию групп, используемую фильтрами каталога и отчётами.',
    count: 'зарегистрированных групп',
    createEyebrow: 'Новая группа',
    createTitle: 'Создать группу',
    editEyebrow: 'Обновление группы',
    editTitle: 'Редактировать группу',
    name: 'Наименование',
    code: 'Код',
    parent: 'Родительская группа',
    parentNone: 'Верхний уровень',
    parentMissing: 'Неизвестная родительская группа',
    parentHint: 'Оставьте родителя пустым, чтобы создать группу верхнего уровня.',
    parentClearUnsupported: 'API не позволяет вернуть группу на верхний уровень. Выберите другую родительскую группу.',
    create: 'Создать группу',
    saveChanges: 'Сохранить изменения',
    saving: 'Сохранение...',
    search: 'Поиск',
    searchPlaceholder: 'Название или код группы',
    loading: 'Загрузка групп...',
    empty: 'Группы продуктов не найдены.',
    edit: 'Редактировать',
    remove: 'Удалить',
    confirmDelete: 'Удалить группу продуктов "{name}"?',
    deleteHasChildren: 'В группе "{name}" есть подгруппы. Сначала переместите или удалите их.',
    permissionHint: 'Требуется разрешение products.create, products.update или products.delete.',
  },
  colors: {
    eyebrow: 'Инженерный справочник',
    title: 'Цвета',
    description: 'Управляйте каталогом цветов, который используется в вариантах производственных заказов, технических паспортах и отчётах о выработке.',
    count: 'зарегистрированных цветов',
    createEyebrow: 'Новый цвет',
    createTitle: 'Создать цвет',
    editEyebrow: 'Обновление цвета',
    editTitle: 'Редактировать цвет',
    code: 'Код',
    name: 'Наименование',
    sortOrder: 'Порядок сортировки',
    dayRate: 'Дневная ставка по умолчанию',
    nightRate: 'Ночная ставка по умолчанию',
    rateCurrency: 'Валюта ставки',
    rateHelp: 'Используется как базовая сдельная ставка, если нет отдельной ставки для продукции или сотрудника.',
    notSet: 'Не задано',
    status: 'Статус',
    active: 'Активен',
    inactive: 'Неактивен',
    create: 'Создать цвет',
    saveChanges: 'Сохранить изменения',
    saving: 'Сохранение...',
    search: 'Поиск',
    searchPlaceholder: 'Код или название цвета',
    loading: 'Загрузка цветов...',
    empty: 'Цвета не найдены.',
    edit: 'Редактировать',
    remove: 'Удалить',
    confirmDelete: 'Удалить цвет "{name}"?',
    permissionHint: 'Требуется разрешение products.update или products.delete.',
  },
  equipment: {
    eyebrow: 'Инженерный справочник', title: 'Машины и оборудование', description: 'Управляйте машинами и оборудованием для маршрутов BOM и технических паспортов.', count: 'зарегистрированных позиций', createTitle: 'Добавить оборудование', editTitle: 'Редактировать оборудование', code: 'Код', name: 'Наименование', sortOrder: 'Порядок сортировки', status: 'Статус', active: 'Активно', inactive: 'Неактивно', create: 'Добавить оборудование', saveChanges: 'Сохранить изменения', saving: 'Сохранение...', search: 'Поиск', searchPlaceholder: 'Код или название оборудования', loading: 'Загрузка оборудования...', empty: 'Оборудование не найдено.', edit: 'Редактировать', duplicate: 'Дублировать', remove: 'Удалить', confirmDelete: 'Удалить оборудование "{name}"?', permissionHint: 'Требуется разрешение products.update или products.delete.', machineTypeUnset: 'Не задано', machineTypeOptions: { single_cylinder: 'Одноцилиндровая', double_cylinder: 'Двухцилиндровая', computerized_single_cylinder: 'Компьютеризированная одноцилиндровая', computerized_double_cylinder: 'Компьютеризированная двухцилиндровая', linked_toe: 'Со связанным мыском (linked-toe)', seamless: 'Бесшовная (true seamless)', flat_knitting: 'Плосковязальная', hosiery: 'Чулочно-носочная', other: 'Другое' },
  },
  units: {
    eyebrow: 'Инженерный справочник',
    title: 'Единицы измерения',
    description: 'Управляйте стандартным каталогом единиц измерения, который используется в продукции, технических паспортах и расходе материалов.',
    count: 'зарегистрированных единиц',
    createEyebrow: 'Новая единица',
    createTitle: 'Создать единицу',
    editEyebrow: 'Обновление единицы',
    editTitle: 'Редактировать единицу',
    code: 'Код',
    name: 'Наименование',
    sortOrder: 'Порядок сортировки',
    status: 'Статус',
    active: 'Активна',
    inactive: 'Неактивна',
    create: 'Создать единицу',
    saveChanges: 'Сохранить изменения',
    saving: 'Сохранение...',
    search: 'Поиск',
    searchPlaceholder: 'Код или название единицы',
    loading: 'Загрузка единиц измерения...',
    empty: 'Единицы измерения не найдены.',
    edit: 'Редактировать',
    remove: 'Удалить',
    confirmDelete: 'Удалить единицу "{name}"?',
    permissionHint: 'Требуется разрешение products.update или products.delete.',
  },
  operations: {
    eyebrow: 'Инженерный справочник',
    title: 'Операции',
    description: 'Управляйте стандартным каталогом операций, который используется в маршрутах BOM и производственной инженерии.',
    count: 'зарегистрированных операций',
    createEyebrow: 'Новая операция',
    createTitle: 'Создать операцию',
    editEyebrow: 'Обновление операции',
    editTitle: 'Редактировать операцию',
    code: 'Код',
    name: 'Наименование',
    sortOrder: 'Порядок сортировки',
    status: 'Статус',
    active: 'Активна',
    inactive: 'Неактивна',
    codePlaceholder: 'Автоматически из названия',
    codeHint: 'Короткий код создаётся автоматически из названия, если поле пустое.',
    sortBy: 'Сортировать операции',
    toggleStatus: 'Переключить статус',
    create: 'Создать операцию',
    saveChanges: 'Сохранить изменения',
    saving: 'Сохранение...',
    search: 'Поиск',
    searchPlaceholder: 'Код или название операции',
    loading: 'Загрузка операций...',
    empty: 'Операции не найдены.',
    edit: 'Редактировать',
    remove: 'Удалить',
    confirmDelete: 'Удалить операцию "{name}"?',
    selectedOperations: 'Выбрано: {count}',
    selectOperation: 'Выбрать {name}',
    selectPageOperations: 'Выбрать операции на этой странице',
    deleteSelected: 'Удалить выбранные',
    deletingSelected: 'Удаление...',
    deletedSelected: 'Удалено операций: {count}.',
    confirmDeleteSelected: 'Удалить {count} выбранных операций?',
    permissionHint: 'Требуется разрешение products.update или products.delete.',
  },
  productionOrder: {
    unassigned: 'Не назначен',
    backToPassport: 'Назад к техническому паспорту',
    backToOrders: 'Назад к производственным заказам',
    createPlannedOrder: 'Создать плановый заказ',
    creating: 'Создание...',
    loadingPlan: 'Загрузка производственного плана...',
    loadingOrders: 'Загрузка производственных заказов...',
    loadingOrder: 'Загрузка заказа...',
    loadingDailyOutput: 'Загрузка суточной выработки...',
    retry: 'Повторить',
    createdEyebrow: 'Производственный заказ создан',
    plannedQuantityPairs: 'Плановое количество: {count} пар',
    status: 'Статус',
    returnToPassport: 'Вернуться к паспорту',
    guidedEyebrow: 'Пошаговая подготовка производства',
    guidedDescription: 'Утвержденный паспорт #{id} задает оборудование, нити, упаковку и план материалов.',
    sectionDefinition: 'Параметры заказа',
    moCode: 'Код MO',
    department: 'Подразделение',
    selectDepartment: 'Выберите подразделение',
    browseDepartments: 'Открыть подразделения',
    plannedPairs: 'Плановые пары',
    departmentId: 'ID подразделения',
    salesOrder: 'Заказ на продажу',
    selectSalesOrder: 'Выберите заказ на продажу',
    browseSalesOrders: 'Открыть заказы на продажу',
    salesOrderId: 'ID заказа на продажу',
    sourceWarehouse: 'Склад-источник',
    selectSourceWarehouse: 'Выберите склад-источник',
    destinationWarehouse: 'Склад-получатель',
    selectDestinationWarehouse: 'Выберите склад-получатель',
    browseWarehouses: 'Открыть склады',
    sourceWarehouseId: 'ID склада-источника',
    destinationWarehouseId: 'ID склада-получателя',
    plannedStart: 'Плановый старт',
    notes: 'Примечания',
    sectionPassportSetup: 'Производственная настройка паспорта',
    machine: 'Машина',
    notNamed: 'Без названия',
    referenceNumber: 'Reference #{id}',
    needles: 'Иглы',
    sockType: 'Тип носка',
    packagingPerPair: 'Упаковка / пара',
    sectionColorPlan: 'План цветов вязания',
    noThreadRows: 'В паспорте нет строк нитей.',
    color: 'Цвет',
    yarnCode: 'Код нити',
    desyatkaQuantity: 'Количество десятков',
    sectionMaterials: 'Предпросмотр потребности в материалах',
    materialsDescription: 'Количество с учетом отходов для {count} пар.',
    refreshPreview: 'Обновить предпросмотр',
    refreshPreviewHint: 'Обновите предпросмотр после изменения планового количества.',
    noMaterials: 'Не возвращены требуемые материалы.',
    stage: 'Этап',
    material: 'Материал',
    perPair: 'На пару',
    waste: 'Отходы',
    required: 'Требуется',
    browseWarehousesTitle: 'Склады',
    browseDepartmentsTitle: 'Подразделения',
    searchDepartments: 'Поиск подразделений',
    searchDepartmentPlaceholder: 'Название подразделения',
    noDepartments: 'Подходящие подразделения не найдены.',
    searchWarehouses: 'Поиск складов',
    searchWarehousePlaceholder: 'Название склада',
    noWarehouses: 'Подходящие склады не найдены.',
    productionEyebrow: 'Производство',
    ordersTitle: 'Производственные заказы',
    ordersDescription: 'Плановые и активные производственные заказы.',
    dailyReportLink: 'Отчёт по суточной выработке',
    preparingExport: 'Подготовка экспорта...',
    exportQueue: 'Экспорт очереди смены',
    scheduleRiskSummary: 'Сводка рисков графика по текущей странице',
    pageOrders: 'Заказы на странице',
    startOverdue: 'Просрочен старт',
    incomplete: 'Не завершено',
    onTrack: 'По графику',
    stageDeltas: 'Результат по этапам',
    dailyOutputTitle: 'Суточная выработка',
    reportDate: 'Дата отчёта',
    noDailyOutputForDate: 'Для этой даты выработка не зафиксирована.',
    noDailyOutputForFilters: 'Нет строк выработки для выбранных фильтров.',
    allStages: 'Все этапы',
    employee: 'Сотрудник',
    allEmployees: 'Все сотрудники',
    exportDailyCsv: 'Экспорт суточного CSV',
    completed: 'Выполнено',
    rejected: 'Брак',
    rework: 'Доработка',
    sequence: 'Последовательность',
    searchMoOrProduct: 'Поиск по коду MO или продукту',
    allStatuses: 'Все статусы',
    sortPriority: 'Приоритет: риск, затем самый ранний старт',
    noOrders: 'Производственные заказы не найдены.',
    noOrdersForRisk: 'Нет заказов для этого фильтра риска графика.',
    product: 'Продукт',
    scheduleRisk: 'Риск графика',
    progress: 'Прогресс',
    start: 'Старт',
    productNumber: 'Продукт #{id}',
    queueExportEmpty: 'Нет подходящих активных заказов для экспорта.',
    queueExportSuccess: 'Экспортировано производственных заказов: {count}.',
    dailyExportEmpty: 'Нет подходящей суточной выработки для экспорта.',
    dailyExportSuccess: 'Экспортировано строк суточной выработки: {count}.',
    filterStageEmployee: 'этап: {stage}; сотрудник: {employee}',
    filterSearchRisk: '{status}{search}{risk}',
    filterSearchPart: '; поиск: {value}',
    filterRiskPart: '; риск: {value}',
    defaultExportStatuses: 'плановые и в работе',
    orderEyebrow: 'Производственный заказ',
    exportCsv: 'Экспорт CSV',
    shareHandover: 'Поделиться передачей смены',
    printHandover: 'Печать передачи смены',
    openStageBoard: 'Открыть доску этапов',
    openLotQueue: 'Открыть очередь лотов',
    stagesTitle: 'Доска этапов',
    stagesDescription: 'Отслеживайте ход выполнения, пропускную способность и отклонения по каждому этапу этого заказа.',
    scheduleAttention: 'Внимание к графику:',
    plan: 'План',
    actual: 'Факт',
    remaining: 'Остаток',
    completion: 'Выполнение',
    activeLots: 'Активные лоты',
    shiftHandover: 'Передача смены',
    supervisorAttention: 'Внимание руководителя',
    snapshot: 'Снимок',
    noExceptions: 'Открытых производственных отклонений нет. План заказа выполнен.',
    activeLotsCount: 'Активных лотов: {count}',
    completedLotsCount: 'Завершённых лотов: {count}',
    totalLotsCount: 'Всего лотов: {count}',
    stagePlanVsActual: 'План и факт по этапам',
    plannedVariantsEyebrow: 'План заказа',
    plannedVariantsTitle: 'План по цвету / размеру',
    plannedVariantsTotal: 'Итого {count}',
    plannedVariantsTotalLabel: 'Итого по плану',
    plannedVariantsEmpty: 'В заказе нет разбивки по цвету / размеру.',
    deactivateOrder: 'Деактивировать заказ',
    deactivatingOrder: 'Деактивация...',
    confirmDeactivate: 'Деактивировать заказ {code}? Он будет удалён из списка активных заказов, а резервы материалов — освобождены.',
    bottleneck: 'Узкое место: {name}',
    noBomStages: 'Этапы операций BOM недоступны.',
    variance: 'Отклонение',
    yield: 'Выход',
    stageRejectedRework: 'Брак {rejected} · Доработка {rework}',
    stageRejectedReworkEmployee: 'Брак {rejected} · Доработка {rework} · {employee}',
    stageVariantTitle: 'Выпущено по цвету / размеру',
    stageVariantColour: 'Цвет',
    stageVariantSize: 'Размер',
    stageVariantQuantity: 'Выпущено',
    stageVariantEmpty: 'По этому этапу ещё нет выпуска по цвету / размеру.',
    stageVariantCount: 'Вариантов: {count}',
    handoverUnitsRemaining: 'По плану заказа осталось {count} ед.',
    handoverBottleneckSignal: '{stage} остаётся самым медленным открытым этапом на уровне {percent}%.',
    handoverRejectedSignal: '{count} бракованных единиц требуют проверки руководителя.',
    handoverReworkSignal: '{count} единиц остаются в доработке.',
    handoverActiveLotsSignal: '{count} производственных лотов всё ещё активны.',
    handoverExported: 'CSV передачи смены экспортирован.',
    handoverShared: 'Передача смены отправлена.',
    handoverDownloaded: 'Поделиться нельзя, поэтому текст передачи смены был скачан.',
    backToOrder: 'Назад к заказу',
    backToLotQueue: 'Назад к очереди лотов',
    loadingLots: 'Загрузка лотов...',
    loadingLot: 'Загрузка лота...',
    lotsTitle: 'Очередь производственных лотов',
    lotsDescription: 'Отслеживайте операционные партии по динамическим этапам BOM.',
    totalLots: 'Всего лотов',
    needsReview: 'Требуют проверки',
    scanOrEnterLot: 'Сканируйте или введите номер лота',
    lotPlaceholder: 'LOT-55-20260512-0001',
    openLot: 'Открыть лот',
    search: 'Поиск',
    lotNumber: 'Номер лота',
    currentStage: 'Текущий этап',
    needsReviewOnly: 'Только требующие проверки',
    noLotsForFilters: 'Нет производственных лотов, соответствующих фильтрам.',
    lot: 'Лот',
    initial: 'Начальное',
    current: 'Текущее',
    exceptions: 'Отклонения',
    created: 'Создан',
    reviewCount: 'Проверок: {count}',
    clear: 'Без отклонений',
    lotPagination: 'Лотов: {count} · Страница {page} из {total}',
    previous: 'Назад',
    next: 'Далее',
    createLot: 'Создать лот',
    createProductionLot: 'Создать производственный лот',
    startingStage: 'Начальный этап',
    quantity: 'Количество',
    cancel: 'Отмена',
    scanRequired: 'Сканируйте или введите номер лота.',
    lotNotFound: 'Лот не найден в этом производственном заказе.',
    lotCreateValidation: 'Выберите этап и введите количество больше нуля.',
    lotEyebrow: 'Производственный лот',
    currentStageLabel: 'Текущий этап:',
    exceptionReviewRequired: 'Производственное отклонение требует проверки',
    exceptionReviewSummary: 'В операциях лота зафиксировано брака: {rejected}, на доработку: {rework}.',
    qcHandover: 'Передача на контроль качества',
    stageChecks: 'Проверки этапа {stage}',
    readyToClose: 'Готов к закрытию',
    checksRequired: 'Требуются проверки',
    qualityCheck: 'Проверка качества',
    confirmAcceptance: 'Подтвердите соответствие критериям приёмки',
    qcRequired: 'Обязательно',
    recordProgress: 'Зафиксировать результат этапа',
    saveProgress: 'Сохранить результат',
    closeCurrentStage: 'Закрыть текущий этап',
    history: 'История',
    noActivity: 'Операции не зафиксированы.',
    employeeFallback: 'Сотрудник',
    selectProductionEmployee: 'Выберите производственного сотрудника',
    printLotCard: 'Печать карточки лота',
    qcNoApprovedPassport: 'Для этого продукта нет утверждённого технического паспорта.',
    qcNoStageChecks: 'Для текущего этапа проверки качества не заданы.',
    qcLoadFailed: 'Не удалось загрузить список проверок качества. Ввод результата этапа остаётся доступным.',
    lotProgressValidation: 'Необходимо выбрать сотрудника и указать хотя бы одно положительное количество.',
    lotEmployeeRequired: 'Сначала выберите ответственного сотрудника.',
    lotQcRequired: 'Перед закрытием этапа выполните все обязательные проверки качества.',
    confirmProgress: 'Подтвердить для лота {lot}: выполнено {completed}, брак {rejected}, доработка {rework}?',
    confirmClose: 'Закрыть этап {stage} для лота {lot}?',
    historyDeltas: '+{completed} выполнено · {rejected} брак · {rework} доработка',
    productionTeam: 'Производственная команда',
    selectEmployee: 'Выберите сотрудника',
    close: 'Закрыть',
    employeeSearchPlaceholder: 'Имя или код сотрудника',
    loadingEmployees: 'Загрузка сотрудников...',
    noProductionEmployees: 'Активные производственные сотрудники не найдены.',
    productionEmployee: 'Производственный сотрудник',
    employeePagination: 'Сотрудников: {count} · {page} / {total}',
    reportEyebrow: 'Производственный отчёт',
    reportDescription: 'Снимок выработки по этапам и сотрудникам за выбранную дату, пригодный для обмена.',
    printReport: 'Печать отчёта',
    rows: 'Строки',
    printEngineering: 'Yuksalish ERP · Инженерный модуль',
    date: 'Дата',
    csvFilters: 'Фильтры',
    csvStageCode: 'Код этапа',
    csvEmployeeId: 'ID сотрудника',
    csvCompletionPercent: 'Процент выполнения',
    queueShiftSummary: 'Сводка очереди производства за смену',
    queueOrders: 'Заказы',
    queueTotalPlanned: 'Всего по плану',
    queueTotalProduced: 'Всего произведено',
    queueTotalRemaining: 'Всего осталось',
    queueOverallCompletionPercent: 'Общий процент выполнения',
    queueStartDate: 'Дата старта',
    queueEquipment: 'Оборудование',
    queueSubmittedBy: 'Отправил',
    riskStartOverdueOne: 'Старт просрочен на {days} день',
    riskStartOverdueMany: 'Старт просрочен на {days} дня',
    riskIncompleteAfterStartOne: 'Не завершено через {days} день после старта',
    riskIncompleteAfterStartMany: 'Не завершено через {days} дня после старта',
    moCodeRequired: 'Код производственного заказа обязателен.',
    departmentRequired: 'Выберите корректное производственное подразделение.',
    plannedPairsRequired: 'Плановое количество должно быть больше нуля.',
    warehousesMustDiffer: 'Склад-источник и склад-получатель должны различаться.',
    colorAndYarnRequired: 'Цвет и код нити обязательны.',
    desyatkaRequired: 'Количество десятков должно быть положительным целым числом.',
    passportRequired: 'Требуется корректный технический паспорт.',
    passportMustBeApproved: 'Подготовить производственный заказ могут только утвержденные технические паспорта.',
  },
})

Object.assign(engineeringMessages.en.engineering.equipment, { machineType: 'Machine type', brandName: 'Brand name', modelName: 'Model name', needleCount: 'Needle count', location: 'Location', duplicateCode: 'Machine Code must be unique.', invalidNeedleCount: 'Needle count must be a non-negative whole number.' })
Object.assign(engineeringMessages.uz.engineering.equipment, { machineType: 'Mashina turi', brandName: 'Brend nomi', modelName: 'Model nomi', needleCount: 'Ignalar soni', location: 'Joylashuvi', duplicateCode: 'Mashina kodi yagona bo‘lishi kerak.', invalidNeedleCount: 'Ignalar soni manfiy bo‘lmagan butun son bo‘lishi kerak.' })
Object.assign(engineeringMessages.ru.engineering.equipment, { machineType: 'Тип машины', brandName: 'Бренд', modelName: 'Модель', needleCount: 'Количество игл', location: 'Расположение', duplicateCode: 'Код машины должен быть уникальным.', invalidNeedleCount: 'Количество игл должно быть целым неотрицательным числом.' })

Object.assign(engineeringMessages.en.engineering, { shopfloorMap: { eyebrow: 'Engineering reference data', title: 'Shopfloor map', description: 'Current machine layout shared with Shopfloor.', layout: 'Layout', loading: 'Loading shopfloor map...', empty: 'No shopfloor layouts found.' } })
Object.assign(engineeringMessages.uz.engineering, { shopfloorMap: { eyebrow: 'Muhandislik ma’lumotnomasi', title: 'Sex xaritasi', description: 'Shopfloor bilan umumiy joriy mashinalar joylashuvi.', layout: 'Joylashuv', loading: 'Sex xaritasi yuklanmoqda...', empty: 'Sex xaritalari topilmadi.' } })
Object.assign(engineeringMessages.ru.engineering, { shopfloorMap: { eyebrow: 'Инженерный справочник', title: 'Карта цеха', description: 'Текущее расположение машин, общее с Shopfloor.', layout: 'Схема', loading: 'Загрузка карты цеха...', empty: 'Схемы цеха не найдены.' } })

Object.assign(engineeringMessages.uz.engineering.operations, { dayRate: 'Kunduzgi standart ishbay narx', nightRate: 'Tungi standart ishbay narx', rateCurrency: 'Narx valyutasi', rateHelp: 'Mahsulot yoki xodim uchun alohida narx bo‘lmasa, standart ishbay narx sifatida ishlatiladi.', notSet: 'Belgilanmagan' })
Object.assign(engineeringMessages.ru.engineering.operations, { dayRate: 'Дневная ставка по умолчанию', nightRate: 'Ночная ставка по умолчанию', rateCurrency: 'Валюта ставки', rateHelp: 'Используется как стандартная сдельная ставка, если для продукта или сотрудника не задана отдельная ставка.', notSet: 'Не задано' })

Object.assign(engineeringMessages.en.engineering.passportDetail, { downloadPdf: 'Download Uzbek PDF', pdfGenerating: 'Generating PDF...' })
Object.assign(engineeringMessages.uz.engineering.passportDetail, { downloadPdf: 'PDF pasportni yuklab olish', pdfGenerating: 'PDF tayyorlanmoqda...' })
Object.assign(
  (engineeringMessages.ru.engineering as unknown as { passportDetail: Record<string, string> }).passportDetail,
  { downloadPdf: 'Скачать PDF на узбекском', pdfGenerating: 'Формирование PDF...' },
)
